import { portalES } from '../portal/es';
import { privacyPolicyBodyEs } from '@/shared/i18n/locales/privacy-policy-bodies/es';
import { restaurantOrdersEs } from '@/shared/i18n/locales/restaurant-orders/es';
import type { TranslationSchema } from '@/shared/i18n/types';

const paymentPolicyTemplate = `POLÍTICA DE PAGO, TARJETA Y CANCELACIÓN

1. Identidad del establecimiento
La reserva y su pago los gestiona [RAZÓN SOCIAL], con sede en [DIRECCIÓN], contactable en [EMAIL/TELÉFONO]. Stripe actúa únicamente como proveedor del servicio de pago.

2. Verificación de la tarjeta
Cuando solo se requiere la verificación, no se cobra ni se bloquea ningún importe. La tarjeta se verifica a través de Stripe para confirmar su validez.

3. Pago anticipado
Cuando se requiere el pago inmediato, el total mostrado antes de la confirmación se cobra en el momento de la reserva y se registra como [ANTICIPO/PAGO COMPLETO].

4. Preautorización
Cuando se prevé una preautorización, el importe mostrado se bloquea temporalmente. El establecimiento puede cobrarlo en los casos descritos en esta política. Si no se cobra, los plazos de desbloqueo dependen del banco del cliente.

5. Cancelación y modificación
Cancelación gratuita hasta [PLAZO]. Después de ese plazo, el establecimiento puede retener o cobrar [IMPORTE/PORCENTAJE]. Las modificaciones están sujetas a disponibilidad.

6. No-show y retraso
En caso de no presentarse o de un retraso superior a [MINUTOS], el establecimiento puede retener o cobrar [IMPORTE/PORCENTAJE].

7. Reembolsos
Los reembolsos se emiten al mismo método de pago en un plazo de [DÍAS] días; los plazos finales de abono dependen de la red de la tarjeta y del banco.

8. Conservación y uso futuro de la tarjeta
Stripe puede conservar la tarjeta de forma tokenizada y reutilizarla solo si el cliente da su consentimiento específico para los cargos descritos. Ni PRO20 ni el establecimiento almacenan el número completo de la tarjeta.

9. Reclamaciones y asistencia
Para solicitudes, cancelaciones o reclamaciones, contacta con [EMAIL/TELÉFONO] indicando el código de reserva.

Versión y validez
La versión aceptada, el importe, la modalidad de pago y la fecha de aceptación se registran junto con la reserva.`;

export const es: TranslationSchema = {
  collaboratorRoles: {
    inherited:
      'This role is not configured yet and retains its current access. Save to define explicit permissions.',
    title: 'App permissions',
    description:
      'Choose which app operations each collaborator role can perform. Portal access is unchanged.',
    role: 'Collaborator role',
    enabled: 'enabled',
    wholeCategory: 'Entire category',
    search: 'Search operations',
    saved: 'Permissions saved',
    saveError:
      'Could not save. Reload the role to check for changes by another manager.',
    versionError: 'The permission catalog has changed. Reload before saving.',
    unsaved: 'Unsaved changes',
    appOnly: 'These permissions apply to the app only.',
    reset: 'Discard changes',
    save: 'Save permissions',
    discard: 'Discard unsaved changes and load the selected role?',
    selectBranch: 'Select a branch to configure permissions.',
    loading: 'Loading permissions…',
    unavailable:
      'Role permissions could not be loaded. Check your access and that the service supports this configuration. No permission changes have been applied.',
    retry: 'Retry',
  },

  portal: portalES,
  restaurantOrders: restaurantOrdersEs,
  giftCards: {
    openRecap: 'Abrir resumen',
    recapError:
      'No se puede obtener el enlace personal de la tarjeta. El servicio Wallet podría no estar disponible.',

    select: 'Seleccionar',
    card: 'Tarjeta',
    invalidDates: 'Selecciona un intervalo de fechas válido.',

    title: 'Tarjetas regalo',
    description: 'Tarjetas emitidas, QR y saldos',
    create: 'Crear tarjeta regalo',
    search: 'Buscar cliente o código…',
    empty: 'No hay tarjetas regalo',
    loadError: 'No se pueden cargar las tarjetas.',
    noBranch: 'Selecciona un establecimiento.',
    customer: 'Cliente',
    amount: 'Importe (€)',
    chooseCustomer: 'Elige un cliente de la lista',
    selected: 'Cliente seleccionado',
    createSuccess: 'Tarjeta creada.',
    createError: 'No se puede crear la tarjeta.',
    amountError:
      'Introduce un importe mayor que cero con un máximo de dos decimales.',
    details: 'Detalles de la tarjeta',
    initial: 'Importe inicial',
    balance: 'Saldo',
    status: 'Estado',
    created: 'Creación',
    expires: 'Vencimiento',
    operations: 'Movimientos',
    operation: 'Movimiento',
    note: 'Nota',
    noOperations: 'No hay movimientos.',
    email: 'Enviar email',
    emailSuccess: 'Envío de email solicitado.',
    emailError: 'No se puede solicitar el envío.',
    active: 'Activa',
    used: 'Utilizada',
    expired: 'Caducada',
    cancelled: 'Cancelada',
    allStates: 'Todos los estados',
    from: 'Desde',
    to: 'Hasta',
    dateRange: 'Rango de fechas',
    clearDateRange: 'Borrar rango de fechas',
    requestPending:
      'Mantén el cliente y el importe para reintentar sin emitir una segunda tarjeta.',
  },
  common: {
    retry: 'Reintentar',
    loading: 'Cargando',
    save: 'Guardar',
    delete: 'Sí, eliminar',
    deleting: 'Eliminando...',
    createLoading: 'Creando...',
    cancel: 'Cancelar',
    yes: 'Sí',
    no: 'No',
    back: 'Atrás',
    search: 'Buscar',
    noData: 'No hay datos disponibles',
    noDataDesc: 'No hay datos que coincidan con los filtros seleccionados.',
    previous: 'Página anterior',
    next: 'Página siguiente',
    actions: 'Acciones',
    serialNumber: 'Sr No.',
    itemsPerPage: 'Elementos por página',
    range: '{{start}} - {{end}} de {{total}}',
    footer: 'Pro20 © {{year}}',
    toast: {
      copyUrlSuccess: 'URL copiada al portapapeles.',
    },
  },
  nav: {
    collaboratorRoles: 'Collaborator roles',
    deliverySection: 'Delivery',
    drivers: 'Repartidores',
    orders: 'Pedidos',
    delivery: 'Delivery',
    deliveryTakeaway: 'Entrega / Para llevar',
    orderingConfiguration: 'Configuración',
    takeaway: 'Para llevar',
    operations: 'Pedidos',
    suppliers: 'Suppliers and catalogs',
    supplierOrderHistory: 'Order history',
    supplierOrders: 'Supplier orders',
    ingredients: 'Ingredientes',
    menuList: 'Tus menús',
    home: 'INICIO',
    menu: 'Menú',
    settingsGroup: 'CONFIGURACIÓN',
    dashboard: 'Panel',
    calendar: 'Calendario',
    calendarDesc: 'Reservas y formularios',
    marketing: 'Marketing',
    marketingDesc: 'Mensajes y promociones',
    menuItem: 'Menú',
    staff: 'Personal',
    settings: 'Configuraciones',
    payments: 'Pagos',
    settingsDesc: 'Configuración general',
    users: 'Usuarios',
    reports: 'Informes',
    notifications: 'Notificaciones',
    bookingHistory: 'Reservas',
    forms: 'Formularios de reserva',
    formHistory: 'Booking',
    formMenu: 'Menú',
    others: 'Otros',
    extraServiceAndProduct: 'Servicios extra',
    events: 'Evento',
    bulkMessage: 'Mensaje masivo',
    surveys: 'Questionnaires',
    automationConfiguration: 'Configuración',
    automationDashboard: 'Panel de rendimiento',
    automation: 'Automation',
    dataCollectionForm: 'Recogida de datos',
    promotion: 'Promociones',
    fidelity: 'Fidelización',
    discountCards: 'Discount cards',
    cards: 'Cards',
    experienceCards: 'Experience',
    prizes: 'Premio',
    loyalty: 'Fidelización',
    landingPage: 'Páginas de Destino',
    tag: 'Etiqueta',
    publicRelation: 'Relaciones públicas',
    collaborator: 'Colaboradores',
    collaboratorReport: 'Informe de colaboradores',
    myCustomers: 'Mis clientes',
    localConfiguration: 'Local',
    stripeConfiguration: 'Configuración de Stripe',
    stripeTransactions: 'Transacciones de Stripe',
    configureWhatsApp: 'WhatsApp no oficial',
    messages: 'Mensajes',
    officialWhatsAppChannel: 'Canal oficial de WhatsApp',
    templates: 'Plantillas',
    whatsAppOfficial: 'WhatsApp Official',
    whatsAppOfficialConfigure: 'Configurar',
    whatsAppOfficialTemplates: 'Plantillas',
    whatsAppOfficialInbox: 'Bandeja de entrada',
  },
  header: {
    fullscreen: 'Pantalla completa',
    language: 'Idioma',
    theme: 'Tema',
    lightMode: 'Claro',
    darkMode: 'Oscuro',
    profile: 'Perfil',
    signOut: 'Cerrar sesión',
    userCode: 'Código de usuario',
    branchList: 'Lista de sucursales',
    searchBranches: 'Buscar sucursales...',
    noBranchesFound: 'No se encontraron sucursales',
    switchingBranch: 'Cambiando sucursal…',
  },
  dashboard: {
    aiCredits: 'Créditos IA',
    smsAvailable: 'SMS disponibles',
    smsLoading: 'Cargando saldo SMS…',
    smsError: 'Error de saldo SMS',
    smsErrorHint: 'Saldo SMS no disponible. Inténtalo de nuevo.',
    smsRefresh: 'Actualizar saldo SMS',
    smsEmpty: 'SMS agotados',
    title: 'Panel',
    subtitle: 'Esto es lo que está pasando en tu negocio.',
    headerMeta: '{{dateRange}} · ventana de rendimiento de {{days}} días',
    customRange: 'Rango de fechas',
    customRangePlaceholder: 'Seleccionar rango de fechas',
    refresh: 'Actualizar',
    bookingStatistics: 'Booking statistics',
    lastWeek: 'Last week',
    thisWeek: 'This week',
    processedBookings: 'Processed bookings vs. Arrived customers',
    overview: 'Overview',
    bookings: 'Bookings',
    customersManaged: 'Customers managed',
    arrived: 'Llegados',
    refused: 'REFUSED',
    noShow: 'NO SHOW',
    cancelled: 'CANCELLED',
    summary: 'Summary of workspace',
    customerContacts: 'Customer Contacts',
    acceptedBookings: 'Accepted Bookings',
    managedGuests: 'Managed Guests',
    callCenterInteractions: 'Call Center Interactions',
    remainingSms: 'Remaining SMS',
    remainingEmails: 'Remaining Emails',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    revenue: 'Revenue',
    pendingTasks: 'Pending Tasks',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    fromLastMonth: 'from last month',
    noData: '—',
    noBranchTitle: 'No branch selected',
    noBranchMessage: 'Select a branch to view dashboard analytics.',
    loadError: 'Could not load dashboard data. Please try again.',
    sectionLoadError: 'No se pudo cargar esta sección.',
    bookingsFlow: 'Bookings flow',
    forms: 'Formularios',
    statusDistribution: 'Status distribution',
    totalBookingsShort: 'Bookings',
    waitingAction: 'Esperando acción',
    waitingActionSubtitle: 'Reservas en IN_ATTESA listas para procesar.',
    waitingBadge: 'Esperando',
    people: 'personas',
    openBooking: 'Abrir reserva',
    copyContact: 'Copiar contacto',
    copySuccess: 'Copiado al portapapeles.',
    emptyWaiting: 'No hay reservas esperando acción.',
    emptyChart: 'No hay datos para este periodo.',
    emptyCustomers: 'No hay clientes que mostrar.',
    emptyForms: 'No hay formularios que mostrar.',
    guests: 'comensales',
    children: 'niños',
    accept: 'Aceptar',
    reject: 'Rechazar',
    acceptSuccess: 'Reserva aceptada.',
    rejectSuccess: 'Reserva rechazada.',
    actionError: 'No se pudo actualizar el estado de la reserva.',
    busiestSlots: 'Franjas más ocupadas',
    busiestSlotsSubtitle: 'Donde el servicio se concentra.',
    customerMix: 'Base de clientes',
    customerRisk: 'Riesgo de clientes',
    customerRiskSubtitle: 'Concentración de no-show y rechazos.',
    bestCustomers: 'Mejores clientes',
    bestCustomersSubtitle: 'Presencia recurrente de alto valor.',
    lastBooking: 'Última reserva',
    messageDelivery: 'Entrega de mensajes',
    messageDeliverySubtitle: 'Salud de la comunicación automática.',
    dailyOperations: 'Operaciones diarias',
    dailyOperationsSubtitle: 'Reservas y carga de comensales por día.',
    bookingsFlowSubtitle: 'Reservas diarias en el periodo seleccionado.',
    formsSubtitle: 'De dónde vienen las reservas.',
    statusDistributionSubtitle: 'Cómo terminaron las reservas.',
    customerMixSubtitle: 'Tu base de clientes en este periodo.',
    pulse: {
      today: 'Hoy',
      upcoming: 'Próximas',
      pending: 'Pendientes',
      waitList: 'Lista de espera',
      refusalRate: 'Tasa de rechazo',
      google: 'Google Reservations',
      googleOn: 'Activo',
      googleOff: 'No activo',
      googleMissing: '{{count}} requisitos pendientes',
    },
    waitingTabs: {
      all: 'Todos',
      today: 'Hoy',
      future: 'Futuras',
      large: 'Grandes',
    },
    flow: {
      volume: 'Volumen',
      people: 'Personas',
      outcome: 'Resultado',
    },
    kpis: {
      reservations: 'Reservas',
      reservationsHint: '{{count}} personas',
      arrivalRate: 'Tasa de llegada',
      customers: 'Clientes',
      arrivedOfTotal: '{{arrived}} de {{total}} llegaron',
      customersHint: '{{active}} activos · {{new}} nuevos',
      messageErrors: '{{count}} errores de envío',
      noShowRate: 'Tasa de no-show',
      arrivedVsNoShow: '{{arrived}} llegados · {{noShow}} no-show',
      peakDay: 'Día pico',
      peakSlot: 'Franja pico',
      dailyAverage: 'Media diaria',
      dailyAverageHint: 'Reservas por día',
      messagesWaiting: 'Mensajes en espera',
      scheduledTotal: '{{count}} programados en total',
      bookingsGuests: '{{bookings}} reservas, {{guests}} comensales',
      slotStats: '{{bookings}} reservas · {{arrived}} llegados ({{rate}}%)',
      totalBookings: 'Reservas totales',
      activeUsers: 'Usuarios activos',
      newUsers: 'Nuevos usuarios',
      peakDate: 'Fecha pico',
      managedBookings: 'Reservas gestionadas',
    },
    series: {
      bookings: 'Reservas',
      confirmed: 'Confirmadas',
      pending: 'Pendientes',
      refused: 'Rechazadas',
      noShow: 'No show',
      risk: 'Riesgo',
    },
    mix: {
      active: 'Activos',
      new: 'Nuevos',
      marketingOptIn: 'Opt-in de marketing',
      blacklisted: 'En lista negra',
      ofBase: '{{percent}}% de la base',
      total: 'en total',
    },
    comm: {
      bookingNotifications: 'Notificaciones de reserva',
      scheduledMessages: 'Mensajes programados',
      successRate: 'Tasa de éxito',
      total: 'Total',
      processed: 'procesados',
      waiting: 'en espera',
      errors: 'errores',
      centralino: 'Estado del centralino',
      centralinoActive: 'Centralino activo',
      wao: 'WAO',
      whatsapp: 'WhatsApp',
      sms: 'SMS',
      email: 'Email',
      enabled: 'On',
      disabled: 'Off',
    },
    ops: {
      byDay: 'Por día',
      byWeekday: 'Por día de la semana',
      avgPeople: 'Personas prom.',
      avgBookings: 'Reservas prom.',
      avgPartySize: 'Tamaño medio del grupo',
      busiestWeekday: 'Día más concurrido',
      totalPeople: 'Personas en total',
      daysCount: '{{count}} días',
    },
    days: {
      mon: 'Lun',
      tue: 'Mar',
      wed: 'Mié',
      thu: 'Jue',
      fri: 'Vie',
      sat: 'Sáb',
      sun: 'Dom',
    },
  },
  auth: {
    welcomeBack: '¡Bienvenido de nuevo!',
    signIn: 'Iniciar sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    enterPassword: 'Introduce tu contraseña',
    userCode: 'Código de usuario',
    enterUserCode: 'Introduce tu código de usuario',
    branchCode: 'Código de sucursal',
    enterBranchCode: 'Introduce el código de sucursal',
    employeeId: 'ID de empleado',
    enterEmployeeId: 'Introduce el ID de empleado',
    rememberMe: 'Recordarme',
    rememberCredentials: 'Recordar credenciales',
    forgotPassword: '¿Olvidaste tu contraseña?',
    signInToContinue:
      'Inicia sesión para seguir gestionando tu restaurante con PRO20.',
    signInToAccount: 'Inicia sesión en tu cuenta',
    chooseSignInMethod: 'Elige tu método de inicio de sesión preferido',
    or: 'o',
    loginWithEmployeeId: 'Iniciar sesión con ID de empleado',
    loginWithUserCode: 'Iniciar sesión con código de usuario',
    loginWithAdminCode: 'Iniciar sesión con código de administrador',
    invalidCredentials: 'Credenciales no válidas. Inténtalo de nuevo.',
    enterpriseTagline: 'Admin empresarial, simplificado.',
    enterpriseDescription:
      'Gestiona usuarios, monitoriza analíticas y configura tu espacio de trabajo desde un solo panel.',
    trustedBy: 'De la confianza de restaurantes de todo el mundo',
    trustSecurityTitle: 'Seguridad empresarial',
    trustSecurityDesc: 'Protección de datos y cumplimiento de nivel bancario.',
    trustUptimeTitle: '99,9 % de disponibilidad',
    trustUptimeDesc: 'Rendimiento fiable cuando lo necesitas.',
    trustSupportTitle: 'Soporte 24/7',
    trustSupportDesc: 'Estamos aquí para ayudarte en cualquier momento.',
    secureSignIn: 'Inicio de sesión seguro | Tu conexión está cifrada.',
    copyright: '© {{year}} PRO20. Todos los derechos reservados.',
    dashboardPreviewAlt: 'Vista previa del panel PRO20',
    demoCredentials: 'Credenciales demo: admin@proventi.com / password123',
    version: 'v. {{version}}(#1)',
  },
  subscription: {
    expired: {
      badge: 'Suscripción caducada',
      title: 'Tu suscripción ha caducado',
      description: 'Si ya has renovado, contacta con el soporte.',
      infoTitle: '¿Necesitas renovar o restaurar el acceso?',
      infoDescription:
        'Contacta con nuestro equipo de soporte y te ayudaremos a volver a estar en línea.',
      footerHelp: '¿Todavía necesitas ayuda?',
      footerHelpDesc:
        'Nuestro equipo de soporte está disponible 24/7 para asistirte.',
    },
  },
  automation: {
    disabled: {
      badge: 'Sección no habilitada',
      title: 'Esta sección no está habilitada',
      description:
        'Esta función está actualmente deshabilitada para tu cuenta. Contacta con soporte para saber más.',
      infoTitle: '¿Necesitas acceso a esta sección?',
      infoDescription:
        'Contacta a nuestro equipo de soporte y te ayudaremos a comenzar.',
      footerHelp: '¿Aún necesitas ayuda?',
      footerHelpDesc:
        'Nuestro equipo de soporte está disponible 24/7 para asistirte.',
    },
  },
  orders: {
    disabled: {
      badge: 'Sección no habilitada',
      title: 'Esta sección no está habilitada',
      description:
        'Esta función está actualmente deshabilitada para tu cuenta. Contacta con soporte para saber más.',
      infoTitle: '¿Necesitas acceso a esta sección?',
      infoDescription:
        'Contacta a nuestro equipo de soporte y te ayudaremos a comenzar.',
      footerHelp: '¿Aún necesitas ayuda?',
      footerHelpDesc:
        'Nuestro equipo de soporte está disponible 24/7 para asistirte.',
    },
  },
  delivery: {
    disabled: {
      badge: 'Sección no habilitada',
      title: 'Esta sección no está habilitada',
      description:
        'Esta función está actualmente deshabilitada para tu cuenta. Contacta con soporte para saber más.',
      infoTitle: '¿Necesitas acceso a esta sección?',
      infoDescription:
        'Contacta a nuestro equipo de soporte y te ayudaremos a comenzar.',
      footerHelp: '¿Aún necesitas ayuda?',
      footerHelpDesc:
        'Nuestro equipo de soporte está disponible 24/7 para asistirte.',
    },
  },
  users: {
    title: 'Usuarios',
    subtitle: 'Gestiona miembros del equipo y permisos',
    searchPlaceholder: 'Buscar usuarios...',
    role: 'Rol',
    status: 'Estado',
    joined: 'Registrado',
    accountDetails: 'Detalles de la cuenta',
    userId: 'ID de usuario',
    created: 'Creado',
    lastUpdated: 'Última actualización',
    notFound: 'Usuario no encontrado',
  },
  settings: {
    title: 'Configuración',
    subtitle: 'Gestiona las preferencias del espacio de trabajo',
    comingSoon: 'Esta sección estará disponible pronto.',
    general: 'General',
    theme: 'Tema',
    language: 'Idioma',
    timezone: 'Zona horaria',
    notifications: 'Notificaciones',
    emailNotifications: 'Notificaciones por email',
    pushNotifications: 'Notificaciones push',
    enabled: 'Activado',
    disabled: 'Desactivado',
  },
  userSettings: {
    title: 'Configuración de usuario',
    subtitle: 'Gestiona la contraseña y las preferencias de tu cuenta',
    password: {
      sectionTitle: 'Gestión de contraseña',
      sectionSubtitle: 'Cambiar contraseña',
      passwordLabel: 'Contraseña',
      confirmLabel: 'Confirmar contraseña',
      submit: 'Actualizar contraseña',
      errors: {
        required: 'La contraseña es obligatoria',
        minLength: 'La contraseña debe tener al menos 4 caracteres',
        confirmRequired: 'Confirma la contraseña',
        mismatch: 'Las contraseñas no coinciden',
      },
      toast: {
        success: 'Contraseña actualizada correctamente',
        failed: 'No se pudo actualizar la contraseña. Inténtalo de nuevo.',
        missingUser: 'No se encontró tu cuenta. Vuelve a iniciar sesión.',
      },
    },
  },
  localConfiguration: {
    subtitle:
      'Configura el logo, el horario, la ubicación y el seguimiento del restaurante.',
    saveConfiguration: 'Guardar configuración',
    saveTooltip: 'Guardar la configuración del restaurante',
    noBranch: 'Selecciona una sede para configurar los ajustes locales.',
    loadError: 'No se pudo cargar la configuración local. Inténtalo de nuevo.',
    logoTitle: 'Logo del restaurante',
    logoHint: 'Sube una imagen',
    logoDescription:
      'Sube un logo PNG con fondo transparente. Haz doble clic en la imagen para abrir la vista previa.',
    uploadImage: 'Sube una imagen',
    removeLogo: 'Eliminar logo',
    logoUploading: 'Subiendo…',
    logoRemoving: 'Eliminando…',
    timezoneTitle: 'Zona horaria de la sala',
    timezoneHint:
      'Determina el día actual mostrado en el formulario y evita desajustes de fecha para clientes del extranjero.',
    timezoneLabel: 'Área geográfica',
    timezoneHelper:
      'El cambio entre la hora solar y el horario de verano se gestiona automáticamente.',
    generalTitle: 'Detalles generales',
    generalHint:
      'Configura la capacidad del local, los tramos horarios y los días de cierre.',
    scheduleTitle: 'Horario',
    scheduleHint: 'Elige cuándo pueden reservar los clientes.',
    dayOverviewTitle: 'Cuándo pueden reservar los clientes',
    dayOverviewHint: 'Ábrelo para ver el día completo.',
    dayOverviewShow: 'Mostrar la vista del día',
    dayOverviewHide: 'Ocultar la vista del día',
    guestsCanBook: 'Los clientes pueden reservar en este horario.',
    guestsCannotBook: 'Los clientes no pueden reservar en este horario.',
    emptyServiceTimes:
      'Aún no hay horarios. Añade desayuno, almuerzo o cena para que los clientes sepan cuándo reservar.',
    timeStepHint:
      'Elige horas en pasos de 15 minutos. Las reservas terminan a la hora de fin.',
    switchOn: 'Sí',
    switchOff: 'No',
    editAction: 'Editar',
    deleteAction: 'Eliminar',
    howItWorks: 'Cómo funciona',
    previewOnly: 'Solo vista previa — edita las franjas abajo',
    stepBadge: 'Paso: 15 min — 04:00 / 03:59',
    localDay: 'Día local: {{time}}',
    tips: {
      turnOnTitle: 'Activar',
      turnOn: 'Deja activas solo las franjas que utilizas.',
      moveTitle: 'Mover',
      move: 'Mueve inicio y fin con los deslizadores.',
      saveTitle: 'Guardar',
      save: 'El sistema evita las superposiciones.',
    },
    shiftsTitle: 'Horarios de servicio',
    shiftsHint:
      'Añade los horarios en los que aceptas reservas, como el almuerzo o la cena. Puedes apagar uno sin eliminarlo.',
    createMealBand: 'Añadir un horario',
    createMealBandAction: 'Añadir',
    createMealBandHint:
      'Ponle un nombre y elige cuándo empieza y cuándo termina.',
    editMealBand: 'Editar horario',
    editMealBandNamed: 'Editar {{name}}',
    updateMealBandAction: 'Guardar',
    mealBandName: 'Nombre',
    mealBandNamePlaceholder: 'Almuerzo',
    selectStartTime: 'Seleccionar hora de inicio',
    selectEndTime: 'Seleccionar hora de fin',
    mealBandLimitReached: 'Puedes añadir hasta 20 horarios.',
    customMealBand: 'Horario de servicio',
    deleteMealBand: 'Eliminar {{name}}',
    deleteMealBandTitle: '¿Eliminar este horario?',
    deleteMealBandDescription:
      '¿Seguro que deseas eliminar {{name}}? Esta acción no se puede deshacer.',
    active: 'Activa',
    inactive: 'Inactiva',
    shiftLegendOff: '{{name}}: no acepta reservas',
    shiftLegendRange: '{{name}}: {{from}} - {{to}}',
    booking: 'Reserva',
    activeBand: 'Aceptar reservas',
    activeBandHint:
      'Apágalo si los clientes no pueden reservar en este horario.',
    deactivate: 'Desactivar',
    deactivated: 'Desactivada',
    activate: 'Activar',
    valueSaved: 'Valor guardado',
    valueSavedHint:
      'Los clientes podrán reservar dentro de esta franja horaria.',
    selectBandTitle: 'Selecciona la franja con una sola barra',
    selectBandHint: 'La parte seleccionada respeta las demás franjas activas.',
    fine: 'Fin',
    spaceAvailable: 'Espacio disponible para {{name}}: {{from}} - {{to}}',
    minStartValue: 'Inicio mín',
    maxEndValue: 'Fin máx',
    bandTurnedOff:
      'Esta franja está desactivada. Actívala con el interruptor superior para editar los horarios.',
    activityHours: 'Horarios de actividad',
    start: 'Inicio',
    end: 'Fin',
    closuresTitle: 'Días en que el restaurante está cerrado',
    closuresHint: 'Los clientes no pueden reservar en estas fechas.',
    addClosure: 'Añadir cierre',
    closuresModalHint:
      'Selecciona una fecha de inicio y una de fin para el periodo de cierre.',
    closingPeriod: 'Periodo de cierre',
    chooseDateRange: 'Elige un intervalo de fechas',
    fromDate: 'Desde',
    toDate: 'Hasta',
    actions: 'Acciones',
    noClosures: 'Aún no hay días de cierre.',
    deleteClosure: 'Eliminar cierre',
    locationTitle: 'Ubicación',
    trackingTitle: 'Seguimiento y analítica',
    trackingHint:
      'Configura el Administrador de etiquetas de Google, el Píxel de Facebook y las herramientas de seguimiento',
    googleTagManager: 'Administrador de etiquetas de Google',
    facebookPixel: 'Píxel de Facebook',
    tabs: {
      tabListLabel: 'Secciones de configuración local',
      general: 'General',
      bookingTimeSlots: 'Horario',
      locationTracking: 'Ubicación y seguimiento',
    },
    shifts: {
      breakfast: 'Desayuno',
      breakfastSubtitle: 'Franja de desayuno',
      lunch: 'Almuerzo',
      lunchSubtitle: 'Franja de almuerzo',
      aperitivo: 'Hora feliz',
      aperitivoSubtitle: 'Franja de aperitivo',
      dinner: 'Cena',
      dinnerSubtitle: 'Franja de cena',
    },
    fields: {
      localName: 'Nombre del local',
      localCapacity: 'Capacidad del local',
      address: 'Dirección',
      city: 'Ciudad',
      zip: 'Código postal',
      province: 'Provincia',
      nation: 'País',
      state: 'Estado',
      latitude: 'Latitud',
      longitude: 'Longitud',
      gtmId: 'ID del contenedor',
      pixelId: 'ID del píxel',
    },
    validation: {
      nameRequired: 'El nombre del local es obligatorio',
      mealBandNameRequired: 'Escribe un nombre',
      mealBandNameDuplicate: 'Ya existe un horario con este nombre',
      openingTimeRequired: 'La hora de inicio es obligatoria',
      closingTimeRequired: 'La hora de fin es obligatoria',
    },
    toast: {
      saved: 'Configuración guardada',
      saveError: 'No se pudo guardar la configuración',
      logoUploadSuccess: 'Logo subido correctamente',
      logoUploadError: 'No se pudo subir el logo',
      logoRemoveSuccess: 'Logo eliminado correctamente',
      logoRemoveError: 'No se pudo eliminar el logo',
      selectDateRange: 'Selecciona una fecha de inicio y una de fin',
      closureAdded: 'Periodo de cierre añadido',
      closureAddError: 'No se pudo añadir el periodo de cierre',
      closureOverlap:
        'Este periodo se solapa con un periodo de cierre existente',
      closureRemoved: 'Periodo de cierre eliminado',
      closureDeleteError: 'No se pudo eliminar el periodo de cierre',
      mealBandCreated: 'Horario añadido',
      mealBandCreateError: 'No se pudo añadir este horario',
      mealBandUpdated: 'Horario actualizado',
      mealBandUpdateError: 'No se pudo actualizar este horario',
      mealBandStatusUpdated: 'Estado de reservas actualizado',
      mealBandStatusError: 'No se pudo actualizar el estado de las reservas',
      mealBandDeleted: 'Horario eliminado',
      mealBandDeleteError: 'No se pudo eliminar este horario',
      mealBandInvalidCode:
        'No se pudo guardar este horario. Inténtalo de nuevo.',
    },
  },
  stripe: {
    common: {
      refresh: 'Actualizar desde Stripe',
      noBranch: 'Selecciona un establecimiento para continuar.',
      processing: 'Procesando…',
    },
    configuration: {
      title: 'Configuración de Stripe',
      subtitle: 'Datos en vivo de la cuenta de Stripe conectada.',
      subtitleWithBranch: 'Datos en vivo de la cuenta conectada a {{branch}}.',
      loadError: 'No se pudo obtener el estado directamente desde Stripe.',
      notConnectedTitle: 'Cuenta de Stripe no configurada',
      notConnectedDescription:
        'El establecimiento seleccionado aún no tiene una cuenta de Stripe conectada. Completa el onboarding desde el backoffice.',
      accountSection: 'Información de la cuenta',
      accountHeading: 'Cuenta de Stripe',
      email: 'Email',
      country: 'País',
      currency: 'Moneda',
      accountType: 'Tipo de cuenta',
      disabledReason: 'Motivo del bloqueo',
      disabledReasonNone: 'Ninguno',
      detailsSubmitted: 'Datos enviados',
      chargesEnabled: 'Pagos',
      payoutsEnabled: 'Abonos',
      requirements: {
        title: 'Requisitos de Stripe',
        currentlyDue: 'Por completar',
        pastDue: 'Vencidos',
        pendingVerification: 'En verificación',
        none: 'Ninguno',
      },
      policy: {
        title: 'Política de pago',
        description:
          'Texto mostrado al cliente cuando la reserva requiere verificación de tarjeta, preautorización o pago.',
        versionMeta: 'Versión {{version}} · actualizada {{date}}',
        useTemplate: 'Sugiéreme una estructura de política',
        templateWarning:
          'La plantilla es un punto de partida: sustituye todos los campos entre corchetes y haz revisar el texto por un asesor legal.',
        placeholder: 'Introduce la política de pago del establecimiento…',
        charCount: '{{count}}/{{max}} caracteres',
        save: 'Guardar y crear nueva versión',
        template: paymentPolicyTemplate,
      },
      toast: {
        policySaved: 'Política de pago guardada.',
        policyError: 'No se pudo guardar la política de pago.',
      },
      validation: {
        policyRequired: 'Introduce el texto de la política de pago.',
        policyMax: 'La política no puede superar los 20000 caracteres.',
      },
    },
    transactions: {
      title: 'Transacciones de Stripe',
      empty: 'Ninguna operación',
      emptyDescription:
        'Todavía no hay pagos ni depósitos registrados para este establecimiento.',
      notConnectedTitle: 'Cuenta de Stripe no conectada.',
      notConnectedDescription:
        'Aquí siguen disponibles todas las transacciones históricas registradas para el establecimiento.',
      filters: {
        all: 'Todas',
        succeeded: 'Correctas',
        uncaptured: 'Sin cobrar',
        failed: 'Fallidas',
      },
      columns: {
        date: 'Fecha',
        booking: 'Reserva',
        operation: 'Operación',
        requested: 'Solicitado',
        captured: 'Cobrado',
        status: 'Estado',
        details: 'Detalles',
      },
      mode: {
        payNow: 'Pago',
        authorizeOnly: 'Depósito / preautorización',
      },
      status: {
        linkCreated: 'Esperando el pago',
        processing: 'Registrado, esperando la confirmación de Stripe',
        pending: 'Pendiente',
        authorized: 'Sin cobrar',
        partiallyPaid: 'Cobrado parcialmente',
        paid: 'Correcto',
        failed: 'Fallido',
        canceled: 'Cancelado',
        refundPending: 'Reembolso en curso',
        partiallyRefunded: 'Reembolsado parcialmente',
        refunded: 'Reembolsado',
        refundFailed: 'Reembolso fallido',
      },
      actions: {
        capture: 'Cobrar depósito',
        release: 'Rechazar depósito',
      },
      confirm: {
        captureTitle: '¿Cobrar el depósito?',
        captureDescription:
          'Se cobrarán {{amount}} por la reserva {{booking}}.',
        releaseTitle: '¿Rechazar el depósito?',
        releaseDescription:
          'La autorización de {{amount}} se anulará y el importe se liberará.',
      },
      toast: {
        captureSuccess: 'Stripe confirmó el cobro.',
        captureError:
          'El cobro no se completó. Comprueba el estado en Stripe e inténtalo de nuevo.',
        releaseSuccess: 'Stripe anuló la autorización.',
        releaseError:
          'La liberación no se completó. Comprueba el estado en Stripe e inténtalo de nuevo.',
      },
      technical: {
        title: 'Detalles técnicos',
        description:
          'Identificadores usados por PRO20 y Stripe para esta operación.',
        open: 'Mostrar detalles técnicos',
        close: 'Cerrar',
        bookingCode: 'Código de reserva',
        depositCode: 'Código de transacción PRO20',
        paymentIntentId: 'PaymentIntent de Stripe',
        paymentLinkId: 'Payment Link de Stripe',
        connectedAccountId: 'Cuenta de Stripe',
      },
    },
  },
  messages: {
    orderingEvents: {
      CREATED: 'Pedido recibido',
      ACCEPTED: 'Pedido aceptado',
      PREPARING: 'Pedido en preparación',
      READY: 'Pedido listo',
      DELIVERED: 'Pedido entregado',
      COLLECTED: 'Pedido recogido',
      CANCELLED: 'Pedido cancelado',
      FAILED: 'Entrega no realizada',
      PAYMENT_REQUIRED: 'Enlace de pago',
      PAYMENT_CONFIRMED: 'Pago recibido',
      PAYMENT_FAILED: 'Pago no realizado',
      REFUNDED: 'Reembolso confirmado',
      SCHEDULE_REMINDER: 'Recordatorio del pedido',
    },
    orderingFlows: {
      CREATED:
        'Después de crear el pedido. Se informa al cliente de que el restaurante aún debe aceptarlo.',
      ACCEPTED: 'Cuando aceptas el pedido y eliges avisar al cliente.',
      PREPARING: 'Cuando empieza la preparación y eliges avisar al cliente.',
      READY:
        'Cuando el pedido está listo y eliges avisar al cliente. En delivery no significa que el repartidor haya salido.',
      DELIVERED:
        'Cuando procesas individualmente el pedido como entregado y eliges avisar al cliente. Las acciones masivas y del repartidor no envían nada.',
      COLLECTED:
        'Cuando procesas individualmente el pedido como recogido y eliges avisar al cliente. Las acciones masivas no envían nada.',
      CANCELLED:
        'Cuando cancelas el pedido y eliges avisar al cliente. No confirma un reembolso.',
      FAILED:
        'Cuando marcas la entrega como no realizada y eliges avisar al cliente.',
      PAYMENT_REQUIRED:
        'Solo después de crear realmente un enlace de pago válido.',
      PAYMENT_CONFIRMED:
        'Después de la confirmación real del pago, automática o manual. Las acciones masivas no envían nada.',
      PAYMENT_FAILED:
        'Si el intento de pago falla y el pedido sigue sin pagar.',
      REFUNDED: 'Solo después de completar realmente el reembolso.',
      SCHEDULE_REMINDER:
        'Antes de la hora prevista, solo para pedidos aceptados o en preparación. Se excluyen los pagos con tarjeta no completados.',
    },
    ordering: {
      configurationHint:
        'Activa este mensaje para los pedidos de esta sede. Guardar no envía ningún mensaje.',
      initialize: 'Crear mensajes de Delivery y para llevar',
      initialized: 'Mensajes creados. No se ha enviado nada.',
      loadError:
        'No se pudieron cargar los mensajes de Delivery y para llevar. Inténtalo de nuevo.',
      missing:
        'Crea los mensajes que faltan. Se conserva el contenido personalizado. Los mensajes nuevos empiezan desactivados.',
      minutes: 'Minutos antes de la hora del pedido',
      scheduleError: 'Introduce un número entero de 1 a 10080 minutos.',
      exclusive: 'Elige WhatsApp no oficial o una plantilla oficial, no ambos.',
      emailPreset: 'Contenido de email predeterminado',
      paymentHint:
        'Déjalo desactivado si ya envías el enlace de pago manualmente por WhatsApp.',
    },

    searchPlaceholder: 'Buscar por nombre, código, categoría o canal…',
    allCategories: 'Todas las categorías',
    browseCategories: 'Categorías de mensajes',
    searchEmpty:
      'Sin resultados. Prueba otro término o selecciona todas las categorías.',

    giftCards: {
      scheduleTitle: 'Recordatorio antes del vencimiento',
      daysBeforeExpiry: 'Días antes del vencimiento',
      scheduleHint:
        'Envía el recordatorio estos días antes del vencimiento, a la hora seleccionada en la zona horaria del local. Excluye tarjetas utilizadas, canceladas o sin saldo.',
      scheduleError:
        'Introduce un número entero positivo de días y una hora entre 00:00 y 23:00.',

      creation: 'Creación de tarjeta regalo',
      expired: 'Tarjeta regalo caducada',
      walletHint:
        'El backend genera wallet_link al enviar. El botón Wallet debe usar el sufijo de la tarjeta, nunca un enlace fijo de cliente.',

      initialize: 'Crear mensajes que faltan',
      initializeSuccess: 'Configuraciones creadas.',
      initializeError: 'El backend no pudo crear las configuraciones.',
      missing:
        'Faltan estas configuraciones. Créelas para asociar plantillas oficiales de WhatsApp y email.',
      notCreated: 'Pendiente en el backend',
      loadError:
        'No se pueden cargar las configuraciones de Marketing. Vuelva a intentar.',
    },
    title: 'Centro de mensajes',
    subtitle:
      'Gestiona todos los mensajes automáticos de WhatsApp, SMS y Email del local.',
    configuredLabel: 'Mensajes configurados',
    refresh: 'Actualizar',
    emptyCategory: 'Aún no hay mensajes en esta categoría.',
    emptyTitle: 'No se encontraron mensajes',
    emptyDescription:
      'Todavía no hay mensajes automáticos configurados para este local.',
    noBranchTitle: 'Ningún local seleccionado',
    noBranchDescription:
      'Selecciona un local para ver y gestionar los mensajes automáticos.',
    loadErrorTitle: 'No se pudieron cargar los mensajes',
    loadErrorDescription:
      'Error al cargar la lista de mensajes. Inténtalo de nuevo.',
    categories: {
      restaurantOrdering: 'Delivery y para llevar',
      supplierOrders: 'Pedidos a proveedores',
      giftCards: 'Mensajes de tarjetas regalo',
      standard: 'Mensajes estándar',
      cta: 'CTA',
      marketing: 'Mensajes de marketing',
      preBooking: 'Comunicaciones pre-reserva',
      postBooking: 'Comunicaciones post-reserva',
      switchboard: 'Mensajes de centralita',
    },
    categoryDescriptions: {
      marketing:
        'Mensajes promocionales, de fidelización y acciones automáticas sobre las tarjetas.',
    },
    items: {
      ORDER_SENT: 'Pedido enviado',
      ORDER_UPDATED: 'Pedido actualizado',
      ORDER_DELIVERY: 'Entrega del pedido',
      ORDER_GENERIC: 'Pedido al proveedor',

      ATT: 'Mensaje en espera de confirmación',
      ARR: 'Confirmación de llegada',
      CONF: 'Confirmación de reserva',
      REF: 'Reserva rechazada',
      DEL: 'Reserva eliminada',
      FQ1: 'Gestión de cola 1',
      FQ2: 'Gestión de cola 2',
      EDTU: 'Actualización de reserva (usuario)',
      EDTC: 'Actualización de reserva (cliente)',
      EDTR: 'Actualización de reserva (restaurante)',
      ANS1: 'Alerta de no-show 1',
      ANS2: 'Alerta de no-show 2',
      ANS3: 'Alerta de no-show 3',
      RPR: 'Recordatorio de promo',
      DOB: 'Promo de cumpleaños',
      AAD: 'Solicitud de reseña',
      RRP: 'Promo te echamos de menos',
      CBP: 'Invitación a volver',
      CENTRALINO1: 'Función de centralita 1',
      CENTRALINO2: 'Función de centralita 2',
      CENTRALINO3: 'Función de centralita 3',
    },
    variables: {
      nomelocale: '@nomelocale@',
      ospiti: '@ospiti@',
      bambini: '@bambini@',
      nome: '@nome@',
      cognome: '@cognome@',
      oraprenotazione: '@oraprenotazione@',
      dataprenotazione: '@dataprenotazione@',
      servizioextra: '@servizioextra@',
      linkmodificaprenotazione: '@linkmodificaprenotazione@',
      linkrichiestacartadicredito: '@linkrichiestacartadicredito@',
      avviso_uscita: '@avviso_uscita@',
    },
    detail: {
      close: 'Cerrar',
      pageSubtitle:
        'Configura los canales de mensajes automáticos para esta plantilla.',
      marketingTitle: 'Mensaje de marketing',
      enableConfiguration: 'Activar esta configuración',
      enableConfigurationHint:
        'Los cambios se aplican a todos los programas que usan {{type}}.',
      enableWhatsApp: 'Activar WhatsApp',
      enableTemplates: 'Activar plantillas',
      enableEmail: 'Activar Email',
      emailObject: 'Asunto',
      configuration: 'Configuración',
      configurationHint: 'Elige cómo quieres enviar los mensajes',
      configurationHintPreBooking: 'Configura métodos de envío y temporización',
      configurationHintPostBooking:
        'Configura el envío y la programación de mensajes post-reserva',
      sendingMethods: 'Métodos de envío',
      sendingMode: 'Modo de envío',
      sendingTiming: 'Temporización de envío',
      whenToSend: 'Cuándo enviar',
      sendingNow: 'Hora de envío',
      timingImmediate: 'Enviar el mensaje justo después de la reserva 🕒',
      timingOneHourBefore: 'Enviar el mensaje 1 hora antes 🕒',
      timingHoursAdvance: 'Enviar el mensaje {{count}} horas antes 🕒',
      timingOneDayAdvance: 'Enviar el mensaje 1 día antes 🕒',
      timingDaysAdvance: 'Enviar el mensaje {{count}} días antes 🕒',
      whenToSendOneDayLater: 'Enviar el mensaje 1 día después',
      whenToSendDaysLater: 'Enviar el mensaje {{count}} días después',
      sendingWhatsAppOnly: 'Enviar solo WhatsApp',
      sendingWhatsAppAndEmail: 'Enviar WhatsApp y Email',
      whatsappMessage: 'Mensaje de WhatsApp',
      whatsappMessageDesc:
        'Configura el mensaje de WhatsApp enviado al cliente',
      whatsappForeign: 'WhatsApp clientes extranjeros',
      whatsappForeignDesc:
        'Versión en inglés. Mensaje automático en inglés para clientes con prefijo internacional.',
      whatsappBusinessTemplate: 'Plantilla WhatsApp Business',
      whatsappBusinessTemplateDesc:
        'Selecciona entre plantillas aprobadas por Meta para enviar vía WhatsApp Business API.',
      smsMessage: 'Mensaje SMS',
      smsMessageDesc: 'Configura el mensaje SMS enviado al cliente',
      email: 'Email',
      emailDesc: 'Configura el mensaje de email enviado al cliente',
      emailSubject: 'Asunto del email',
      emailSubjectPlaceholder: 'Ej: Comunicación de @hotel-name',
      bodyPlaceholder: 'Escribe tu mensaje aquí...',
      inspireMe: 'Inspírame',
      testSending: 'Envío de prueba',
      testSendPrompt: 'Introduce el número al que quieres enviar el mensaje',
      testSendAction: 'Enviar',
      selectTemplate: 'Seleccionar plantilla',
      selectTemplateTooltip: 'Selecciona una plantilla aprobada por Meta',
      selectTemplateHint:
        'Elige una plantilla aprobada por Meta para este mensaje.',
      selectedTemplate: 'Plantilla seleccionada',
      noTemplateSelected: 'Ninguna plantilla seleccionada',
      noTemplateHint:
        'Ninguna plantilla seleccionada. Selecciona una plantilla aprobada por Meta para configurar el envío vía WhatsApp Business API.',
      templateCategory: 'Categoría',
      templateType: 'Tipo',
      templateLanguages: 'Idiomas',
      templateLanguage: 'Idioma',
      templateVariableMap: 'Mapa de variables',
      templateBody: 'Cuerpo',
      templateBodyEmpty: 'No hay vista previa del cuerpo para este idioma.',
      ctaButton: 'Llamada a la acción',
      ctaDynamicPath: 'Ruta dinámica',
      ctaDynamicPathPlaceholder:
        'Ruta dinámica, p. ej. /menu/xxxx0111 o /booking/0000101111',
      ctaDynamicPathHint:
        'Se añade a la URL de la plantilla al enviar el mensaje, en lugar de {{1}}, para que puedas crear enlaces según el contexto del mensaje.',
      ctaDynamicPathRequired: 'La ruta dinámica no puede estar vacía.',
      ctaUrlValid: '{{url}} es válida',
      ctaUrlInvalid: 'URL no válida. Comprueba el formato.',
      ctaLinksTooltip: '¿Qué opciones tengo?',
      ctaLinksTitle: 'Enlaces disponibles para los servicios creados en Pro20',
      ctaLinksDesc: 'Selecciona un enlace para usarlo como ruta dinámica.',
      ctaLinksEmpty:
        'Todavía no has creado ningún servicio en Pro20, así que no hay nada que sugerir.',
      ctaLinkUnnamed: 'Enlace sin nombre',
    },
    toast: {
      refreshed: 'Lista de mensajes actualizada.',
      saveSuccess: 'Configuración del mensaje guardada.',
      saveError: 'No se pudo guardar la configuración del mensaje.',
      testSent: 'Mensaje de prueba enviado correctamente.',
      sendError: 'No se pudo enviar el mensaje.',
      templateSelected: 'Plantilla seleccionada.',
      resetSuccess: 'Configuración restaurada a los valores predeterminados.',
      resetError:
        'No se pudo restablecer la configuración. Inténtalo de nuevo.',
      applyDefaultsSuccess:
        'Textos predeterminados aplicados y canal habilitado.',
      applyDefaultsError:
        'No se pudieron aplicar los valores predeterminados. Inténtalo de nuevo o cancela.',
      detailUnavailable:
        'El detalle del mensaje aún no está disponible para esta categoría.',
    },
    validation: {
      whatsappBodyRequired:
        'Introduce un mensaje de WhatsApp cuando WhatsApp esté activado.',
      templateRequired:
        'Selecciona una plantilla de WhatsApp Business cuando las plantillas estén activadas.',
      emailSubjectRequired:
        'Introduce el asunto del email cuando Email esté activado.',
      emailBodyRequired:
        'Introduce el cuerpo del email cuando Email esté activado.',
    },
    reset: {
      tooltip: 'Restaurar configuración predeterminada',
      title: '¿Restablecer la configuración?',
      description:
        'Las configuraciones existentes se reemplazarán por la configuración predeterminada.',
      confirm: 'Sí, continuar',
      cancel: 'Cancelar',
      resetting: 'Restableciendo…',
    },
    applyDefaults: {
      whatsappTooltip:
        'Aplicar textos predeterminados y habilitar WhatsApp para este grupo',
      emailTooltip:
        'Aplicar asunto y texto predeterminados y habilitar email para este grupo',
      whatsappAria: 'Aplicar configuración predeterminada de WhatsApp',
      emailAria: 'Aplicar configuración predeterminada de email',
      whatsappTitle: '¿Aplicar valores predeterminados de WhatsApp?',
      emailTitle: '¿Aplicar valores predeterminados de email?',
      description:
        'Los textos del canal elegido se reemplazarán y el canal se habilitará para todo el grupo. La activación de mensajes, otros canales y las reglas de envío no cambian.',
      marketingWhatsappNote:
        'WhatsApp usará el mensaje libre; se conservarán los datos de la plantilla oficial.',
      confirm: 'Aplicar y habilitar',
      cancel: 'Cancelar',
      applying: 'Aplicando…',
    },
  },
  configureWhatsApp: {
    title: 'Configurar WhatsApp',
    subtitle: 'Conecta un número de WhatsApp Business para esta sucursal',
    disabled: {
      badge: 'Configuración deshabilitada',
      title: 'Configuración de WhatsApp no disponible',
      description: 'Continúa desde la app móvil para habilitar esta función.',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business',
      title: 'Conecta el número local de WhatsApp',
      description:
        'Crea una nueva instancia, escanea el código QR y habilita el envío de mensajes automáticos desde la plataforma PRO20.',
      create: 'Crear configuración',
      guided: 'Operación guiada y reversible',
      featureSecureTitle: 'Seguro y fiable',
      featureSecureDesc: 'Seguridad de nivel empresarial',
      featureQuickTitle: 'Configuración rápida',
      featureQuickDesc: 'Tarda menos de 2 minutos',
      featureReversibleTitle: 'Reversible',
      featureReversibleDesc: 'Cambia o desconecta en cualquier momento',
      step1Title: 'Crear instancia',
      step1Desc:
        'Genera la configuración técnica conectada a la sucursal seleccionada.',
      step1Time: '~ 30 sec',
      step2Title: 'Escanear QR',
      step2Desc:
        'Abre WhatsApp en tu teléfono y conecta tu dispositivo mediante código QR.',
      step2Time: '~ 60 sec',
      step3Title: 'Probar el envío',
      step3Desc: 'Envía un mensaje de prueba y comprueba que todo funciona.',
      step3Time: '~ 30 sec',
    },
    creating: {
      title: 'Configuración en curso…',
      description: 'Espera mientras creamos tu instancia de WhatsApp.',
      starting:
        'WhatsApp se está iniciando. La primera conexión puede tardar un par de minutos.',
    },
    success: {
      title: 'Completado',
      description: '¡Instancia creada correctamente!',
      banner:
        'Ahora puedes vincular el dispositivo con un código QR o un código de emparejamiento.',
      continue: 'Continuar',
    },
    configured: {
      heading: 'Instancia de WhatsApp',
      subtitle:
        'Gestiona la vinculación, el estado y los mensajes de prueba de esta sucursal.',
      statusInstanceCreated: 'Instancia creada',
      statusQr: 'Esperando escaneo QR',
      statusReady: 'Conectado',
      statusCreating: 'Creando…',
      statusFailed: 'Conexión fallida',
      restart: 'Reiniciar conexión',
      stillStarting:
        'WhatsApp sigue iniciándose. Esta página seguirá comprobando. Usa Escanear QR cuando el código esté listo, o reinicia la conexión.',
      failureHint:
        'El motor de WhatsApp no se inició. Reinicia la conexión para intentarlo de nuevo.',
      scanQr: 'Escanear QR',
      delete: 'Eliminar',
      deleting: 'Eliminando...',
      statusDeleting: 'Eliminando...',
      sessionName: 'Nombre de sesión',
      instanceId: 'ID de instancia',
      connectedNumber: 'Número conectado',
      operatingStatus: 'Estado operativo',
      nextStepTitle: 'Siguiente paso',
      nextStepQr:
        'Escanea el código QR con WhatsApp en tu teléfono para vincular el dispositivo.',
      notLinked: 'Aún no vinculado',
    },
    link: {
      title: 'Vincular dispositivo',
      description:
        'Usa un código QR o un código de emparejamiento para conectar WhatsApp.',
      tabQr: 'Código QR',
      tabPhone: 'Código móvil',
      qrInstructions:
        'Abre WhatsApp en tu teléfono → Dispositivos vinculados → Vincular un dispositivo y escanea este código.',
      waiting: 'Esperando el escaneo…',
      phoneInstructions:
        'Introduce el número móvil que se vinculará y solicita un código de emparejamiento.',
      phoneLabel: 'Número móvil',
      phonePlaceholder: '333 123 4567',
      getCode: 'Obtener código',
      pairingCode: 'Tu código de emparejamiento',
      pairingHint:
        'Introduce este código en WhatsApp → Dispositivos vinculados → Vincular con número de teléfono.',
    },
    delete: {
      title: '¿Eliminar la configuración de WhatsApp?',
      description:
        'Se eliminará la instancia de esta sucursal. Podrás crear una nueva más adelante.',
      confirm: 'Sí, eliminar',
    },
    test: {
      title: 'Enviar mensaje de prueba',
      phone: 'Número del destinatario',
      phonePlaceholder: '333 123 4567',
      message: 'Mensaje',
      defaultMessage: '¡Hola! Este es un mensaje de prueba de Proventi.',
      send: 'Enviar prueba',
      success: 'Mensaje de prueba enviado correctamente.',
      disabledHint:
        'Escanea el código QR y vincula WhatsApp antes de enviar un mensaje de prueba.',
      notConnected:
        'La sesión de WhatsApp no está conectada. Escanea el código QR para vincular primero tu dispositivo.',
    },
    toast: {
      linked: 'WhatsApp vinculado correctamente.',
      stillStarting:
        'WhatsApp sigue iniciándose. Esta página seguirá comprobando.',
      createFailed: 'No se pudo crear la sesión de WhatsApp.',
      qrFailed: 'No se pudo preparar el QR de WhatsApp.',
    },
  },
  configureWhatsAppOfficial: {
    title: 'Configurar WhatsApp Oficial',
    subtitle:
      'Conecta tu número oficial de WhatsApp Business (Meta) para esta sucursal.',
    loading: 'Cargando estado de incorporación…',
    loadError: 'No se pudo cargar el estado de incorporación.',
    retry: 'Reintentar',
    toast: {
      onboardSuccess: 'Incorporación de WhatsApp completada correctamente.',
      onboardError: 'No se pudo completar la incorporación de WhatsApp.',
      registerSuccess: 'Número de teléfono registrado correctamente.',
      registerError: 'No se pudo registrar el número de teléfono.',
      businessDetailsSuccess: 'Detalles del negocio cargados correctamente.',
      businessDetailsError: 'No se pudieron cargar los detalles del negocio.',
      missingPhoneNumberId: 'Falta el ID del número de teléfono.',
      metaError: 'Error de registro de WhatsApp: {{message}}',
      missingBranch: 'Falta el código de sucursal.',
      sdkNotReady: 'El SDK de Facebook aún no está listo.',
    },
    onboarding: {
      eyebrow: 'API de WhatsApp Business',
      title: 'Conectar con WhatsApp Business API',
      description:
        'En esta sección puedes crear y conectar un número al canal oficial de WhatsApp Business. La configuración te permite asociar tu número de empresa con la plataforma para empezar a enviar y recibir mensajes de forma profesional, automatizada y conforme a las políticas.',
      connect: 'Crear configuración',
      connecting: 'Conectando…',
      howToConnect: 'Cómo conectar',
      featuresTitle: 'Con Meta puedes',
      featureExisting: 'Conectar un número de WhatsApp Business existente',
      featureNew: 'Crear una nueva cuenta con Meta',
      step1Title: 'Registra tu número',
      step1Desc:
        'Añade y verifica el número de teléfono de tu empresa con Meta para activar el canal.',
      step1Time: '~ 2 min',
      step2Title: 'Crear plantilla y obtener aprobación',
      step2Desc:
        'Crea una plantilla de mensaje y envíala a Meta para su revisión y aprobación.',
      step2Time: '~ 1 día',
      step3Title: 'Enviar plantilla de texto',
      step3Desc:
        'Envía una plantilla aprobada para iniciar una nueva conversación.',
      step3Time: '~ 30 seg',
      step4Title: 'Enviar a un número de teléfono',
      step4Desc:
        'Entrega mensajes a cualquier número de teléfono de cliente y comienza a chatear.',
      step4Time: '~ 30 seg',
    },
    configured: {
      heading: 'Información de incorporación de WhatsApp',
      subtitle: 'Tu cuenta oficial de WhatsApp Business está conectada.',
      connectedBadge: 'Conectado',
      phoneNumberId: 'ID del número de teléfono',
      wabaId: 'ID de la cuenta de WhatsApp Business',
      businessId: 'ID de la empresa',
      onboardingStatus: 'Estado de incorporación',
      phoneRegistrationStatus: 'Estado de registro del número',
      statusCompleted: 'Completado',
      statusSuccess: 'Correcto',
      statusPending: 'Pendiente',
      statusRegistered: 'Registrado',
      statusNotRegistered: 'No registrado',
      registerPhone: 'Registrar tu número',
      registeringPhone: 'Registrando…',
      businessDetailsHeading: 'Detalles del negocio',
      businessDetailsEmpty:
        'Los detalles del negocio aún no están disponibles. Obtenlos de Meta con el ID de tu número de teléfono.',
      fetchBusinessDetails: 'Obtener detalles del negocio',
      fetchingBusinessDetails: 'Obteniendo…',
      verifiedName: 'Nombre verificado',
      displayPhoneNumber: 'Número de teléfono mostrado',
      qualityRating: 'Calificación de calidad',
      codeVerificationStatus: 'Verificación del código',
      platformType: 'Tipo de plataforma',
      throughputLevel: 'Rendimiento',
      phoneStatus: 'Estado del teléfono',
      accountMode: 'Modo de cuenta',
      nameStatus: 'Estado del nombre',
      messagingLimitTier: 'Límite de mensajes',
      officialBusinessAccount: 'Cuenta de empresa oficial',
      businessProfileHeading: 'Perfil de empresa',
      email: 'Correo empresarial',
      vertical: 'Categoría del negocio',
      description: 'Descripción del negocio',
      about: 'Acerca de',
      address: 'Dirección',
      websites: 'Sitios web',
      metaPhoneNumberId: 'ID de número Meta',
      webhookApplication: 'URL del webhook',
      reset: 'Restablecer configuración',
      resetting: 'Restableciendo…',
    },
    reset: {
      title: '¿Restablecer la configuración?',
      description:
        'Esto solo restablece la vista local para que puedas ejecutar la configuración de nuevo. No elimina la incorporación en el backend.',
      confirm: 'Sí, restablecer',
    },
    guide: {
      title: 'Guía de incorporación de la API de WhatsApp Business',
      intro:
        '¡Bienvenido! Sigue los pasos a continuación para conectar tu cuenta de WhatsApp Business a nuestra plataforma.',
      prerequisitesTitle: 'Requisitos previos',
      prerequisitesIntro: 'Antes de comenzar, asegúrate de tener lo siguiente:',
      prereq1: 'Una cuenta de Facebook con acceso de Administrador.',
      prereq2:
        'Una cuenta de Meta Business Manager (o estar listo para crear una).',
      prereq3:
        'Un número de móvil válido que pueda recibir verificación por SMS o llamada de voz.',
      prereq4:
        'Un número de teléfono que no esté registrado actualmente en WhatsApp Messenger, la app de WhatsApp Business u otro proveedor de la API de WhatsApp Business.',
      prereq5:
        'La información de tu empresa lista: nombre, sitio web (opcional), correo electrónico y categoría.',
      prereq6:
        'Una tarjeta de crédito/débito válida para añadir como método de pago en Meta.',
      prereq7: 'Una conexión a internet estable.',
      prereqNote:
        'Importante: Si tu número de teléfono se usa actualmente en la app de WhatsApp Business, elimínalo de la app antes de conectarlo a la WhatsApp Business Platform.',
      stepsTitle: 'Pasos de incorporación',
      step1Title: 'Paso 1 – Inicia sesión en nuestra plataforma',
      step1Body:
        'Inicia sesión en tu cuenta, ve a Integración de WhatsApp y haz clic en Conectar WhatsApp.',
      step2Title: 'Paso 2 – Inicia sesión con Facebook',
      step2Body:
        'Se abrirá una ventana segura de inicio de sesión de Meta (Facebook). Inicia sesión con tu cuenta de Facebook y haz clic en Continuar.',
      step3Title: 'Paso 3 – Selecciona o crea un Business Manager',
      step3Body:
        'Selecciona tu Meta Business Manager existente o haz clic en Crear cuenta de empresa e introduce el nombre, correo, sitio web y país de tu empresa, luego haz clic en Siguiente.',
      step4Title: 'Paso 4 – Crea o selecciona una cuenta de WhatsApp Business',
      step4Body:
        'Usa una cuenta de WhatsApp Business existente o crea una nueva proporcionando el nombre de la cuenta, el nombre para mostrar y la categoría de la empresa, luego haz clic en Continuar.',
      step5Title: 'Paso 5 – Añade tu número de teléfono',
      step5Body:
        'Selecciona tu país, introduce tu número de teléfono, elige la verificación por SMS o llamada de voz y luego introduce el código de verificación de 6 dígitos.',
      step6Title: 'Paso 6 – Concede permisos',
      step6Body:
        'Meta solicitará los permisos necesarios. Haz clic en Continuar y Finalizar; nuestra plataforma completa la configuración automáticamente.',
      step7Title: 'Paso 7 – Conexión completada',
      step7Body:
        'Una vez conectado, verás tu número de teléfono conectado, la cuenta de WhatsApp Business, el estado de la conexión y estarás listo para enviar y recibir mensajes.',
      nextStepsTitle: 'Próximos pasos recomendados',
      nextStep1: 'Crea tus plantillas de mensajes de WhatsApp.',
      nextStep2: 'Añade un método de pago válido en Meta Business Manager.',
      nextStep3: 'Completa la verificación de empresa de Meta (recomendado).',
      nextStep4: 'Envía un mensaje de plantilla de prueba.',
      nextStep5: 'Configura tu chatbot o automatización (si aplica).',
      notesTitle: 'Notas importantes',
      note1:
        'Un número de teléfono solo puede conectarse a una cuenta de WhatsApp a la vez.',
      note2:
        'No elimines tu cuenta de WhatsApp Business de Meta después de la incorporación.',
      note3:
        'Mantén tu método de pago activo para evitar interrupciones en la entrega de mensajes.',
      note4:
        'La verificación de empresa se recomienda para obtener límites de mensajería más altos.',
      note5:
        'La aprobación del nombre para mostrar la gestiona Meta y puede tardar un tiempo.',
      issuesTitle: 'Problemas comunes',
      issue1Title: 'Código de verificación no recibido',
      issue1Body:
        'Verifica que el número de teléfono sea correcto, prueba la opción de llamada de voz y espera unos minutos antes de solicitar otro código.',
      issue2Title: 'Número de teléfono ya registrado',
      issue2Body:
        'El número está conectado a WhatsApp Messenger, la app de WhatsApp Business u otro proveedor de la API de WhatsApp Business. Elimínalo del servicio existente antes de la incorporación.',
      issue3Title: 'Error de permisos',
      issue3Body:
        'Asegúrate de ser Administrador tanto de la cuenta de Facebook como del Meta Business Manager.',
      issue4Title: 'Pago requerido',
      issue4Body:
        'Meta requiere un método de pago válido antes de poder iniciar ciertas conversaciones. Añade tu método de pago en Meta Business Manager.',
      issue5Title: 'Verificación de empresa pendiente',
      issue5Body:
        'Tu cuenta aún puede conectarse y usarse, pero se recomienda encarecidamente completar la verificación de empresa para desbloquear límites de mensajería más altos y mejorar la confianza de la cuenta.',
      needHelpTitle: '¿Necesitas ayuda?',
      needHelpIntro:
        'Si tienes algún problema durante la incorporación, contacta a nuestro equipo de soporte con:',
      help1: 'Captura de pantalla del error.',
      help2: 'Número de teléfono (incluido el código de país).',
      help3: 'ID de Meta Business Manager (si está disponible).',
      help4: 'ID de la cuenta de WhatsApp Business (si está disponible).',
      needHelpOutro:
        'Proporcionar esta información nos ayudará a resolver tu problema más rápidamente.',
    },
  },
  whatsAppOfficial: {
    title: 'WhatsApp Official',
    subtitle: 'Manage inbox conversations for the official WhatsApp channel.',
    systemBadge: 'System',
    disabled: {
      badge: 'Sección no habilitada',
      title: 'Esta sección no está habilitada',
      description:
        'Esta función está actualmente deshabilitada para tu cuenta. Contacta con soporte para saber más.',
      infoTitle: '¿Necesitas acceso a esta sección?',
      infoDescription:
        'Contacta a nuestro equipo de soporte y te ayudaremos a comenzar.',
      footerHelp: '¿Aún necesitas ayuda?',
      footerHelpDesc:
        'Nuestro equipo de soporte está disponible 24/7 para asistirte.',
    },
    inbox: {
      searchPlaceholder: 'Search conversations...',
      noConversations: 'No conversations found',
      noConversationsDesc:
        'New chats will appear here as soon as customers contact this branch.',
      emptySelectionTitle: 'Select a chat',
      emptySelectionDescription:
        'Choose a conversation from the list to view the thread and reply.',
    },
    thread: {
      sessionLabel: 'State 24 hours',
      sessionTooltip:
        'When the 24-hour service window is active, you can send free text replies. Otherwise, you must send an approved template first.',
      active: 'Active',
      inactive: 'Inactive',
      today: 'Today',
      yesterday: 'Yesterday',
      emptyTitle: 'No messages yet',
      emptyDescription:
        'This conversation is ready to receive the first inbound or outbound message.',
      pending: 'Enviando…',
      retry: 'Reintentar',
      retryConfirmTitle: '¿Reintentar el mensaje?',
      retryConfirmMessage: '¿Seguro que quieres enviar este mensaje de nuevo?',
      billingFree: 'Gratis',
      billingPaid: 'De pago',
    },
    delivery: {
      warningTooltip: 'No entregado — toca para ver detalles',
      detailsTitle: 'Problema de entrega',
      fallbackTitle: 'El mensaje no se entregó',
      technicalDetails: 'Detalles técnicos',
      noTechnicalDetails: 'No hay detalles técnicos disponibles.',
    },
    composer: {
      placeholder: 'Escribe un mensaje...',
      send: 'Enviar',
      submitTemplate: 'Enviar plantilla',
      templateOnlyTitle: 'Plantilla requerida',
      templateOnlyDescription:
        'La ventana de servicio de 24 horas no está activa. Envía una plantilla aprobada para reabrir la conversación.',
      attach: 'Adjuntar archivo',
      attachImage: 'Imagen',
      attachAudio: 'Audio',
      attachDocument: 'Documento',
      removeAttachment: 'Quitar adjunto',
    },
    templateSend: {
      title: 'Enviar plantilla',
      fillVariablesTitle: 'Completar variables',
      customerLabel: 'Cliente',
      phoneLabel: 'Teléfono del destinatario',
      prefix: 'Prefijo',
      number: 'Número',
      sendingTo: 'Enviando a',
      phoneInvalid: 'Introduce el prefijo y el número antes de enviar.',
      enterValuePlaceholder: 'Introduce un valor',
      fieldRequired: 'Este campo es obligatorio',
      ctaUrlSuffix: 'Sufijo URL CTA',
      ctaUrlSuffixPlaceholder: 'Sufijo URL',
      ctaUrlSuffixHint:
        'Se añade a la URL del botón cuando la plantilla tiene un enlace dinámico.',
      noVariables: 'Esta plantilla no tiene variables que completar.',
      sendMessage: 'Enviar mensaje',
      cancel: 'Cancelar',
      previewTitle: 'Vista previa',
      infoCategory: 'Categoría',
      infoLanguage: 'Idioma',
      infoType: 'Tipo',
      infoSendingTo: 'Enviando a',
    },
    toast: {
      sendSuccess: 'Mensaje enviado.',
      sendError: 'No se pudo enviar el mensaje.',
      templateSendSuccess: 'Plantilla enviada.',
      templateSendError: 'No se pudo enviar la plantilla.',
      loadConversationsError: 'No se pudieron cargar las conversaciones.',
      loadMessagesError: 'No se pudieron cargar los mensajes.',
    },
  },
  templateslist: {
    import_no_branch: 'Branch code is missing. Please try again.',
    processing: 'Processing…',
    import_global_template: 'Import global template',
    bulk_import_started: 'Bulk import started',
    import_failed: 'Could not import templates.',
    bulk_import_job_not_found: 'Bulk import job not found.',
    bulk_import_poll_timeout: 'Bulk import timed out. Please try again.',
    bulk_import_bad_accept_response:
      'Invalid response from bulk import request.',
    bulk_import_failed_with_reason: 'Bulk import failed: {{message}}',
    bulk_import_completed_summary:
      'Bulk import completed. Success: {{success}}, Failed: {{failed}}',
    import_success: 'Global templates imported.',
  },
  whatsAppTemplates: {
    browseGroups: 'Grupos de plantillas',
    allGroups: 'Todas las plantillas',
    allStatuses: 'Todos los estados',
    filterStatus: 'Filtrar por estado',
    searchEmpty:
      'No hay plantillas que coincidan con la búsqueda o los filtros.',
    groups: {
      UTILITY: 'Servicio',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Autenticación',
      OTHER: 'Otros',
    },
    title: 'Plantillas',
    subtitle:
      'Organiza plantillas para un acceso fácil y escala la comunicación con mensajes predefinidos.',
    learnMore: 'Saber más',
    importGlobal: 'Importar plantilla global',
    addTemplate: 'Añadir plantilla',
    tableTitle: 'Plantillas',
    sync: 'Sincronizar plantillas',
    searchPlaceholder: 'Buscar plantillas...',
    emptyTitle: 'No se encontraron plantillas',
    emptyDescription:
      'Crea o sincroniza plantillas para empezar a mensajear a los clientes.',
    detailsTitle: 'Detalles de la plantilla',
    deleteTitle: '¿Eliminar plantilla?',
    deleteDescription:
      '¿Seguro que quieres eliminar "{{name}}"? Esta acción no se puede deshacer.',
    columns: {
      statusDate: 'Estado / Fecha',
      nameType: 'Nombre / Tipo',
      category: 'Categoría',
      language: 'Idioma',
      actions: 'Acciones',
    },
    stats: {
      approved: 'Plantillas aprobadas',
      pending: 'Plantillas pendientes',
      draft: 'Plantillas en borrador',
      rejected: 'Plantillas rechazadas',
    },
    status: {
      APPROVED: 'APPROVED',
      PENDING: 'PENDING',
      DRAFT: 'DRAFT',
      REJECTED: 'REJECTED',
    },
    languages: {
      en: 'English',
      it: 'Italiano',
      es: 'Español',
      fr: 'Français',
      nl: 'Nederlands',
      zh: '中文',
    },
    actions: {
      menu: 'Acciones de plantilla',
      edit: 'Editar',
      delete: 'Eliminar',
    },
    toast: {
      learnMoreSoon: 'La documentación estará disponible pronto.',
      importSoon: 'La importación global estará disponible pronto.',
      createSoon: 'La creación de plantillas estará disponible pronto.',
      syncSuccess: 'Plantillas sincronizadas correctamente.',
      syncError: 'No se pudieron sincronizar las plantillas.',
      importSuccess: 'Plantillas globales importadas.',
      importError: 'No se pudieron importar las plantillas globales.',
      loadError: 'No se pudieron cargar las plantillas.',
      deleteSuccess: 'Plantilla eliminada.',
      deleteError: 'No se pudo eliminar la plantilla.',
    },
    create: {
      title: 'Nueva plantilla',
      subtitle: 'Crea fácilmente nuevas plantillas según tus necesidades.',
      backToList: 'Volver a plantillas',
      formTitle: 'Añadir nueva plantilla',
      editTitle: 'Editar plantilla',
      editSubtitle:
        'Actualiza una plantilla rechazada y envíala de nuevo a revisión.',
      editFormTitle: 'Editar plantilla',
      category: 'Categoría',
      name: 'Nombre de la plantilla',
      namePlaceholder: 'p. ej. booking_confirmation_v1',
      language: 'Idioma de la plantilla',
      typeLabel: 'Tipo de plantilla',
      types: {
        text: 'Texto',
        image: 'Imagen',
        video: 'Video',
        audio: 'Audio',
        document: 'Documento',
      },
      categories: {
        UTILITY: 'Utilidad',
        MARKETING: 'Marketing',
        AUTHENTICATION: 'Autenticación',
      },
      uploadFile: 'Subir archivo',
      browse: 'Examinar…',
      uploading: 'Subiendo…',
      clearMedia: 'Borrar',
      noFileChosen: 'Ningún archivo elegido',
      orPasteUrl: 'O pega handle / URL',
      mediaUrlPlaceholder: 'https://…',
      previewHandlePlaceholder:
        'Media de encabezado subido (vista previa no disponible)',
      mediaHintImage: 'Solo JPEG o PNG, máximo 5 MB.',
      mediaHintVideo: 'Solo MP4, máximo 16 MB, máx. 60 segundos.',
      mediaHintAudio: 'MP3, OGG o M4A, máximo 16 MB.',
      mediaHintDocument: 'PDF o documentos Office, máximo 100 MB.',
      heading: 'Encabezado (opcional)',
      headingHint: 'Se muestra encima del cuerpo del mensaje.',
      body: 'Cuerpo',
      footer: 'Pie de página',
      variablesTitle: 'Variables',
      variables: {
        requested_amount: 'Depósito solicitado',
        balance_due: 'Saldo restante',
        paid_total: 'Total recibido',
        deposit_percentage: 'Porcentaje del depósito',
        payment_link: 'Enlace de pago del depósito',

        service_name: 'Servicio',
        order_number: 'Número de pedido',
        ordering_channel: 'Para llevar / Delivery',
        scheduled_label: 'Fecha y hora del pedido',
        total_amount: 'Total del pedido',
        order_link: 'Enlace del resumen del pedido',
        delivery_address: 'Dirección de entrega',
        pickup_address: 'Dirección de recogida',
        paid_amount: 'Importe pagado',
        refund_amount: 'Importe reembolsado',
        cancellation_reason: 'Motivo de cancelación',
        failure_reason: 'Motivo de entrega fallida',
        amount_due: 'Importe a pagar',

        customerName: 'Nombre del cliente',
        branchName: 'Nombre de la sede',
        bookingId: 'ID de reserva',
        discountCode: 'Código de descuento',
        promoCode: 'Código promo',
        bookingDate: 'Fecha de reserva',
        bookingTime: 'Hora de reserva',
        bookingUpdateLink: 'Enlace de actualización',
        branchCode: 'Código de sede',
        guestCount: 'Número de invitados',
        childrenCount: 'Número de niños',
        creditCardRequestUrl: 'URL de solicitud de tarjeta de crédito',
        depositRequestLink: 'Enlace de solicitud de depósito',
        depositAmount: 'Importe del depósito',
        expectedLeaveTime: 'Hora de salida prevista',
        qrCodeImageUrl: 'URL de imagen del código QR',
      },
      buttonsTitle: 'Botones',
      buttonType: 'Tipo de botón',
      buttonText: 'Texto del botón',
      buttonUrl: 'URL del botón',
      quickReply: 'Respuesta rápida',
      ctaUrl: 'CTA URL',
      addQuickReply: 'Respuesta rápida',
      addCta: 'CTA',
      removeButton: 'Eliminar',
      buttonsHint: 'Puedes añadir hasta 3 botones por plantilla.',
      submit: 'Enviar',
      previewTitle: 'Vista previa de WhatsApp',
      previewEmpty: 'La vista previa de la plantilla aparecerá aquí.',
      validation: {
        categoryRequired: 'Selecciona una categoría.',
        nameMin: 'El nombre debe tener al menos 3 caracteres.',
        nameMax: 'El nombre puede tener como máximo 60 caracteres.',
        nameFormat: 'Usa solo minúsculas, números y guiones bajos.',
        languageRequired: 'Selecciona un idioma.',
        bodyRequired: 'El cuerpo es obligatorio.',
        bodyMax: 'El cuerpo puede tener como máximo 1024 caracteres.',
        mediaRequired:
          'El media es obligatorio para plantillas de imagen y video.',
        invalidImageType: 'La imagen debe ser JPEG o PNG.',
        invalidVideoType: 'El video debe ser MP4.',
        imageTooLarge: 'La imagen debe ser de 5 MB o menos.',
        videoTooLarge: 'El video debe ser de 16 MB o menos.',
        videoDurationMax: 'El video debe durar 60 segundos o menos.',
        videoDurationUnreadable: 'No se pudo leer la duración del video.',
        buttonLabelRequired: 'El texto del botón es obligatorio.',
        buttonUrlRequired: 'Los botones CTA requieren una URL.',
        qrPlaceholder:
          'El texto de respuesta rápida no puede contener variables.',
        qrMax: 'El texto de respuesta rápida debe tener 25 caracteres o menos.',
        urlInvalid: 'La URL CTA debe ser una dirección http(s) válida.',
      },
      toast: {
        success: 'Plantilla creada correctamente.',
        error: 'No se pudo crear la plantilla.',
        updateSuccess: 'Plantilla actualizada correctamente.',
        updateError: 'No se pudo actualizar la plantilla.',
        uploadSuccess: 'Media de encabezado subido.',
        uploadFailed: 'No se pudo subir el media de encabezado.',
      },
    },
  },
  notifications: {
    title: 'Notificaciones',
    allCaughtUp: 'Todo al día',
    unread: 'notificación sin leer',
    unreadPlural: 'notificaciones sin leer',
    markAllRead: 'Marcar todas como leídas',
    noNotifications: 'Sin notificaciones',
    noNotificationsDesc:
      'Estás al día. Las nuevas notificaciones aparecerán aquí.',
    viewAll: 'Ver todas las notificaciones',
  },
  bookings: {
    history: {
      title: 'Historial de reservas',
      subtitle: 'Reservas de {{branch}}',
      subtitleDefault: 'Ver y gestionar el historial de reservas',
      totalBookings: '{{count}} reservas',
      bookingsForDateBefore: '{{count}} reservas para el ',
      bookingsForDateAfter: '',
      previousDay: 'Día anterior',
      nextDay: 'Día siguiente',
      calendar: {
        title: 'Seleccionar fecha de reserva',
        openCalendar: 'Abrir calendario de reservas',
        previousMonth: 'Mes anterior',
        nextMonth: 'Mes siguiente',
        bookings: '{{count}} reservas',
        arrived: '{{count}} llegados',
        refused: '{{count}} rechazados',
        legendBookings: 'Reservas',
        legendArrived: 'Llegados',
        legendRefused: 'Rechazados',
      },
      calendarConfig: {
        eyebrow: 'Calendario de reservas',
        title: 'Configurar este día',
        subtitle: 'Edita solo la fecha seleccionada en el calendario.',
        branchLabel: 'Sucursal',
        singleDay: 'Solo este día',
        updateDate: 'Actualizar {{date}}',
        dateRange: 'Rango de fechas',
        dateRangeHint: 'Crear o sobrescribir varios días',
        eventLabel: 'Recurrencia o evento',
        eventPlaceholder: 'Añadir una recurrencia o nombre de evento',
        daysSelected: '{{count}} día seleccionado',
        daysSelectedPlural: '{{count}} días seleccionados',
        rangePlaceholder: 'Seleccionar rango de fechas',
        dateRangeRequired: 'Selecciona una fecha de inicio y fin.',
        rulesTitle: 'Reglas de reservas',
        manual: 'Manual',
        acceptTo: 'Aceptar hasta',
        acceptToValue: 'Número',
        acceptToRequired: 'Este campo es obligatorio',
        acceptAll: 'Aceptar todo',
        refuseAll: 'Rechazar todo',
        manualHint: 'Manual deja la decisión al flujo normal de la app/portal.',
        close: 'Cerrar',
        saveDay: 'Guardar día',
        saveRange: 'Guardar rango',
        loadError: 'No se pudo cargar la configuración del día.',
        saveSuccess: 'Configuración del día guardada.',
        saveError: 'No se pudo guardar la configuración del día.',
      },
      exportPdf: 'Descargar PDF de las reservas de hoy',
      exportPdfGeneratingTitle: 'Generación de PDF',
      exportPdfGeneratingDescription: 'Preparando el archivo de reservas...',
      exportPdfReadyTitle: 'PDF listo',
      exportPdfReadyDescription: 'El archivo se generó correctamente.',
      exportPdfOpen: 'Abrir PDF',
      exportPdfClose: 'Cerrar',
      exportPdfError: 'No se pudo generar el PDF. Inténtalo de nuevo.',
      addBooking: 'Añadir reserva',
      shifts: {
        all: 'Todas',
        filterLabel: 'Servicio',
        breakfast: 'Desayuno',
        daily: 'Almuerzo',
        happyhour: 'Happy hour',
        night: 'Cena',
      },
      searchPlaceholder: 'Buscar cliente, email, teléfono o código de reserva',
      filterStatus: 'Filtrar estados',
      callCustomer: 'Llamar a {{name}}',
      moreActions: 'Más acciones',
      snapshot: {
        bookings: 'Reservas',
        confirmed: 'Confirmado',
        arrived: 'Llegados',
        waiting: 'En espera',
        issues: 'No-show / rechazados',
        configureShifts: 'Configurar las reglas del día',
        filterShift: 'Filtrar {{shift}}',
        shiftSelected: '{{shift}} seleccionado — pulsa para quitar',
        shiftUnavailable: '{{shift}} no está configurado para este local',
      },
      itemsPerPage: 'Elementos por página',
      range: '{{start}} - {{end}} de {{total}}',
      date: 'Fecha',
      time: 'Hora',
      timeAndGuests: 'Hora e invitados',
      customer: 'Cliente',
      guests: 'Invitados',
      guestCount: '{{count}} invitados',
      childrenCount: '+ {{count}} niños',
      event: 'Evento',
      noEvent: 'no event',
      contacts: 'Contactos',
      details: 'Detalles',
      openImage: 'Abrir imagen',
      openPdf: 'Abrir PDF de la reserva',
      googleReservation: 'Reserva desde Google Reservation',
      walkIn: 'Walk in',
      associatedServicesProducts: 'Servicios/productos asociados',
      generatedBooking: 'Reserva generada {{score}}/100',
      extraServices: 'Servicios extra {{count}}',
      extraServicesOnly: 'Servicios extra ',
      tables: 'Mesas asignadas',
      noTables: 'Sin mesas asignadas',
      notes: 'Notas privadas',
      noNotes: 'Sin notas privadas',
      manageBooking: 'Gestionar reserva',
      eventDetails: {
        title: 'Detalles del evento',
        eventName: 'Nombre del evento',
        eventCode: 'Código del evento',
        startDate: 'Inicio',
        maxCapacity: 'Capacidad',
        eventDescription: 'Descripción',
        eventImage: 'Imagen del evento',
        formCodes: 'Formularios',
        noImage: 'Sin imagen',
      },
      menuDetails: {
        title: 'Detalles del menú',
        menuName: 'Nombre del menú',
        peopleCount: 'Número de personas',
      },
      viewBookingImage: 'Ver imagen de la reserva',
      bookingImage: 'Imagen de la reserva',
      users: 'users',
      status: 'Estado',
      processedBy: 'Gestionado por',
      source: 'Origen',
      noBranch: 'Seleccione una sucursal para ver el historial',
      empty: 'No se encontraron reservas',
      emptyDesc: 'No hay reservas para el día {{date}}.',
      updateStatus: 'Actualizar el estado de la reserva',
      confirmOperationTitle: '¿Confirmar operación?',
      confirmStatusChange:
        '¿Actualizar el estado de la reserva a "{{status}}"?',
      notifyCustomerTitle: '¿Notificar al cliente?',
      notifyCustomerMessage: '¿Desea notificar al cliente de este cambio?',
      statusUpdateSuccess: 'Estado de la reserva actualizado correctamente.',
      statusUpdateError:
        'No se pudo actualizar el estado de la reserva. Inténtelo de nuevo.',
      updatingStatus: 'Actualizando estado...',
      associatedServices: {
        title: 'Servicios/productos asociados',
        reference: 'Reserva {{code}}',
        quantity: '×{{count}}',
        empty: 'No hay servicios asociados a esta reserva.',
        paid: 'Pagado',
        unpaid: 'No pagado',
      },
      messageHistory: {
        open: 'Historial de mensajes',
        communicationError: 'Error en el envío del mensaje',
        title: 'Historial de mensajes',
        reference: 'Referencia {{code}}',
        refresh: 'Actualizar',
        empty: 'No hay mensajes enviados para esta reserva.',
        loadError: 'No se pudo cargar el historial de mensajes.',
        endpoint: 'Endpoint',
        eventCode: 'Código de evento',
        payload: 'Payload',
        response: 'Respuesta',
        statusProcessed: 'Enviado',
        statusPending: 'Pendiente',
        messageText: 'Mensaje',
        messageTextUnavailable: 'El texto del mensaje no está disponible.',
        httpStatus: 'HTTP {{status}}',
        attempts: '{{count}} intentos',
        resubmit: 'Reenviar mensaje',
        resubmitSuccess: 'Mensaje reenviado correctamente.',
        resubmitError: 'No se pudo reenviar el mensaje. Inténtelo de nuevo.',
        resubmitConfirmTitle: '¿Reenviar mensaje?',
        resubmitConfirmMessage:
          '¿Está seguro de que desea reenviar este mensaje?',
      },
      statusActions: {
        confirm: 'Confirmación',
        refuse: 'Rechazar',
        eliminate: 'Eliminar',
        arrived: 'Llegado',
        notArrived: 'No llegado',
        notifyUser: 'Notificar al usuario para acudir al local',
        bookingAlreadyProcessed: 'Reserva ya procesada',
        moveToConfirm: 'Mover a confirmado',
      },
      statuses: {
        IN_ATTESA: 'En espera',
        MODIFICATO_DA_UTENTE: 'Modificado por usuario',
        ELIMINATO_DA_UTENTE: 'Eliminado por usuario',
        MODIFICA_CONFERMATA: 'Modificación confirmada',
        MODIFICA_RIFIUTATA: 'Modificación rechazada',
        PRE_CONFERMA_PAGAMENTO: 'Pre-confirmación de pago',
        CONFERMATO: 'Confirmado',
        AVVISATO_LISTA_ATTESA: 'Lista de espera avisada',
        ARRIVATO: 'Llegado',
        NON_ARRIVATO: 'No se presentó',
        RIFIUTATO: 'Rechazado',
        LISTA_ATTESA: 'Lista de espera',
        ELIMINATO: 'Cancelado',
      },
    },
    payment: {
      column: 'Pagos',
      close: 'Cerrar',
      connectRequired:
        'Stripe Connect no está listo para este local. Completa el onboarding para gestionar pagos.',
      loadError:
        'No se pudieron cargar las operaciones de pago de esta reserva.',
      linkCopied: 'Enlace de pago copiado.',
      linkCopyError: 'No se pudo copiar el enlace de pago.',
      linkReadyTitle: 'Enlace de depósito listo',
      linkReadyDescription:
        'El enlace está listo para enviarse por tus canales.',
      badge: {
        title: 'Pagos',
        cardPresent: 'Tarjeta presente',
        cardMissing: 'Sin tarjeta',
        paidPendingOps: 'Pagado · operaciones pendientes',
        manage: 'Gestionar',
        noCard: 'Sin tarjeta asociada',
        verifiedSuccess: 'Verificada con éxito',
        depositAuthorizedCapture: 'Depósito autorizado · Cobrar',
        depositCaptured: 'Depósito cobrado',
        depositRefunded: 'Depósito reembolsado',
        depositPartiallyRefunded: 'Depósito reembolsado parcialmente',
        depositPrefix: 'Depósito',
      },
      tooltip: {
        cardPresent: 'Abrir los datos de tarjeta guardados del local',
        cardMissing: 'Enviar al cliente la solicitud de datos de tarjeta',
        openOperations:
          'Abrir el depósito o las operaciones de pago de la reserva',
        openVerified: 'Abrir los detalles de pago y de la tarjeta verificada',
        noCard: 'Sin tarjeta asociada. Abre para gestionar los pagos',
      },
      status: {
        notRequired: 'No requerido',
        pending: 'Esperando el pago',
        awaitingConfirmation: 'Pago enviado, esperando confirmación de Stripe',
        cardVerified: 'Tarjeta verificada',
        authorized: 'Sin cobrar',
        paid: 'Pagado',
        partiallyPaid: 'Parcialmente pagado',
        failed: 'Fallido',
        canceled: 'Cancelado',
        refundPending: 'Reembolso en curso',
        partiallyRefunded: 'Reembolsado parcialmente',
        refunded: 'Reembolsado',
        refundFailed: 'Reembolso fallido',
      },
      actions: {
        copyLink: 'Copiar enlace',
        refund: 'Reembolsar',
      },
      modal: {
        title: 'Depósitos',
        description:
          'Revisa las preautorizaciones, genera un enlace y decide si cobrar o liberar el depósito.',
        bookingLabel: 'Reserva {{code}}',
        pendingWarning:
          'Uno o más pagos están completados, pero aún hay operaciones o enlaces pendientes.',
        requested: 'Solicitado',
        captured: 'Cobrado',
        authorized: 'Autorizado',
        netCaptured: 'Cobrado neto',
        refunded: 'Reembolsado',
        descriptionLabel: 'Descripción',
        operationsTab: 'Operaciones ({{count}})',
        newRequestTab: 'Nueva solicitud',
        historyTitle: 'Historial de operaciones',
        historyDescription:
          'Depósitos y autorizaciones vinculados a esta reserva.',
        emptyTitle: 'Aún no hay operaciones',
        emptyDescription:
          'Abre la pestaña “Nueva solicitud” para generar el primer enlace.',
        createRequest: 'Crear solicitud',
      },
      create: {
        title: 'Crear un enlace de depósito',
        description:
          'El cliente autoriza el importe; después podrás cobarlo o liberarlo.',
        amountLabel: 'Importe del depósito (€)',
        descriptionLabel: 'Descripción opcional',
        descriptionPlaceholder: 'Ejemplo: depósito para la reserva',
        authorizeOnlyHint:
          'El importe solo se autoriza. Podrás cobarlo o liberarlo más adelante.',
        submit: 'Generar enlace de depósito',
        invalidAmount: 'Introduce un importe mayor que cero.',
        error: 'No se pudo crear el enlace de Stripe.',
        success: 'Enlace de depósito creado.',
        sent: 'Enlace de depósito creado y enviado.',
        linkCreatedMessageFailed:
          'Enlace creado, pero no se envió el mensaje de solicitud de depósito.',
      },
      refund: {
        title: 'Reembolsar pago',
        maxAmount: 'Importe máximo reembolsable: {{amount}}',
        amountLabel: 'Importe (€)',
        reasonLabel: 'Motivo',
        invalidAmount:
          'Introduce un importe válido dentro del máximo reembolsable.',
        started: 'Reembolso iniciado.',
        completed: 'Reembolso completado.',
        error:
          'El reembolso falló. Revisa el estado en Stripe e inténtalo de nuevo.',
        reasons: {
          requested_by_customer: 'Solicitado por el cliente',
          duplicate: 'Pago duplicado',
          fraudulent: 'Fraudulento',
        },
      },
    },
    create: {
      title: 'Crear reserva',
      cancel: 'Cancelar',
      submit: 'Crear reserva',
      customerDetails: 'Datos del cliente',
      bookingDetails: 'Detalles de la reserva',
      firstName: 'Nombre *',
      firstNamePlaceholder: 'Introduce el nombre',
      lastName: 'Apellido *',
      lastNamePlaceholder: 'Introduce el apellido',
      email: 'Email',
      emailPlaceholder: 'Introduce el email',
      phone: 'Número de teléfono *',
      phonePlaceholder: 'Introduce el número de teléfono',
      nickName: 'Apodo',
      nickNamePlaceholder: 'Introduce el apodo',
      bookingImage: 'Imagen de la reserva',
      pdfDocument: 'Documento PDF',
      bookingTime: 'Hora de la reserva *',
      bookingTimePlaceholder: 'Selecciona la hora',
      guests: 'Comensales *',
      guestsPlaceholder: 'Introduce comensales',
      children: 'Niños *',
      childrenPlaceholder: 'Introduce niños',
      customerRequests: 'Peticiones del cliente',
      customerRequestsPlaceholder: 'Introduce las peticiones del cliente',
      restaurantNotes: 'Notas del restaurante',
      restaurantNotesPlaceholder: 'Introduce las notas del restaurante',
    },
    calendar: {
      selected: 'Seleccionado: {{date}}',
      dotsHint: 'Los puntos indican reservas disponibles',
    },
  },
  forms: {
    title: 'Formularios de reservas',
    subtitle:
      'Gestiona todos los formularios activos, suspendidos o archivados',
    addDataCollectionForm: 'Añadir formulario',
    searchPlaceholder: 'Buscar formularios por nombre',
    noDataTitle: 'No hay formularios',
    noDataDesc:
      'Aún no has creado ningún formulario. Empieza creando tu primer formulario para recopilar reservas e interacciones.',
    status: 'Estado',
    active: 'Activo',
    suspended: 'Suspendido',
    url: 'URL',
    qrCode: 'Código QR',
    preview: 'Vista previa',
    googleBooking: 'Google Booking',
    googleReservation: 'Google Reservation',
    copyUrl: 'Copiar URL',
    downloadQr: 'Descargar QR',
    editForm: 'Editar formulario',
    dublicateUrl: 'Duplicar formulario',
    statusActive: 'Activo',
    statusInactive: 'Inactivo',
    statusDraft: 'Borrador',
    statsCount: '{{count}} formularios',
    updatedAgo: 'Actualizado hace 2 minutos',
    deleteForm: 'Eliminar formulario',
    share: {
      label: 'Compartir formulario',
      whatsapp: 'WhatsApp',
      instagram: 'Instagram',
      twitter: 'Twitter / X',
      facebook: 'Facebook',
      defaultText: 'Reserva con nosotros',
      instagramCopied: 'Enlace copiado. Pégalo en Instagram para compartir.',
      instagramOpened:
        'Instagram abierto. Enlace del formulario copiado — pégalo para compartir.',
      copyFailed: 'No se pudo copiar el enlace. Inténtalo de nuevo.',
    },
    createTitle: 'Crear formulario',
    formName: 'Nombre del formulario',
    formNamePlaceholder: 'Introduce el nombre del formulario',
    createForm: 'Crear formulario',
    toast: {
      createSuccess: 'Formulario creado correctamente.',
      createError: 'No se pudo crear el formulario. Inténtalo de nuevo.',
      deleteSuccess: 'Formulario eliminado correctamente.',
      deleteError: 'No se pudo eliminar el formulario. Inténtalo de nuevo.',
      duplicateSuccess: 'Formulario duplicado correctamente.',
      duplicateError: 'No se pudo duplicar el formulario. Inténtalo de nuevo.',
      statusActivateSuccess: 'Formulario activado correctamente.',
      statusSuspendSuccess: 'Formulario suspendido correctamente.',
      statusActivateError:
        'No se pudo activar el formulario. Inténtalo de nuevo.',
      statusSuspendError:
        'No se pudo suspender el formulario. Inténtalo de nuevo.',
      googleReservationEnabled: '{{name}} Google Reservation habilitado',
      googleReservationDisabled: '{{name}} Google Reservation deshabilitado',
      googleReservationError:
        'No se pudo actualizar Google Reservation. Inténtalo de nuevo.',
      logoUploadSuccess: 'Logo subido correctamente.',
      logoUploadError: 'No se pudo subir el logo.',
      logoRemoveSuccess: 'Logo eliminado correctamente.',
      logoRemoveError: 'No se pudo eliminar el logo.',
      bannerUploadSuccess: 'Banner subido correctamente.',
      bannerUploadError: 'No se pudo subir el banner.',
      bannerRemoveSuccess: 'Banner eliminado correctamente.',
      bannerRemoveError: 'No se pudo eliminar el banner.',
    },
    deleteModel: {
      deleteTitle: '¿Eliminar formulario?',
      deleteDescription:
        '¿Seguro que quieres eliminar este formulario? Esta acción no se puede deshacer.',
    },
    validation: {
      formNameMin: 'El nombre debe tener al menos 2 caracteres',
      formNameMax: 'El nombre no puede superar los 50 caracteres',
    },
    scanQrAccess: 'Escanea el código QR para acceder:',
    qrClose: 'Cerrar',
    qrDownload: 'Descargar',
    edit: {
      pageTitle: 'Consentimiento de datos personales',
      editTitle: 'Editar {{name}}',
      subtitle: 'Configura el formulario de reserva',
      saveChanges: 'Guardar cambios',
      close: 'Cerrar',
      toast: {
        saveSuccess: 'Formulario guardado correctamente.',
        saveError: 'No se pudo guardar el formulario.',
      },
      tabListLabel: 'Secciones de edición del formulario',
      uploading: 'Subiendo...',
      removing: 'Eliminando...',
      tabs: {
        general: 'General',
        capacity: 'Capacidad',
        schedule: 'Horario',
        formFields: 'Campos del formulario',
        preferences: 'Preferencias',
        services: 'Servicios',
      },
      general: {
        media: 'Multimedia',
        logo: 'Logo',
        banner: 'Imagen de banner',
        bannerHint: 'Imagen de banner — arrastra y suelta o explora',
        basicDetails: 'Datos básicos',
        formName: 'Nombre del formulario',
        customerName: 'Formulario Emoticono',
        address: 'Dirección del local para mostrar en el formulario',
        customThankYouPage: 'Página de agradecimiento personalizada',
        buttonTextOnBookingForm: 'Texto del botón en el formulario de reserva',
        policy: 'Política',
        webLink: 'Enlace web',
      },
      capacity: {
        hint: 'Límites de reserva y reglas de mesa para este formulario.',
        sections: {
          coverLimits: 'Límites de cubiertos',
          timeSettings: 'Ajustes de franjas horarias',
          policies: 'Políticas',
        },
        maxGuests: 'Cubiertos máximos antes de la llamada',
        maxGuestsHint:
          '¿Cuál es el número máximo de cubiertos que acepta tu establecimiento antes de exigir una llamada?',
        maxTableGuests: 'Máximo de invitados por mesa',
        maxTableGuestsHint:
          'Establece el número máximo de invitados que puedes aceptar en una sola mesa. Si dejas el valor en 0, el máximo será igual a la capacidad total del local.',
        minGuests: 'Mínimo de cubiertos por mesa',
        minGuestsHint: 'Tamaño mínimo de grupo para una sola mesa.',
        phoneOverbooking: 'Móvil Overbooking',
        phoneOverbookingHint:
          'Teléfono mostrado cuando los invitados superan el máximo por mesa. Formato: PREFIJO + NÚMERO (p. ej. +39 3444393343).',
        cut: 'Corte',
        cutHint:
          'Divide la disponibilidad en franjas de reserva de esta duración.',
        cutSlotOption:
          'Dividir las franjas horarias en franjas de {{minutes}} minutos',
        stayOnTable: 'Permanencia en la mesa',
        stayOnTableHint: 'Duración prevista de la comida por reserva.',
        minutes: '{{count}} min',
        dogsAccepted: 'Se admiten perros',
        dogsAcceptedHint: 'Permite a los clientes indicar que traen un perro.',
        menuSelectionMandatory: 'Selección de menú obligatoria',
        menuSelectionMandatoryHint:
          'Si está activo, el huésped debe elegir un menú (o “Sin menú”) para cada persona para que el total coincida con adultos + niños antes de continuar.',
        showRestaurantNoMenu: 'Mostrar tarjeta de restaurante sin menú',
        showRestaurantNoMenuHint:
          'Si está activo, el formulario público muestra una tarjeta que explica que el restaurante no tiene menú fijo.',
      },
      schedule: {
        shiftOverview: 'Resumen de turnos',
        weeklyHours: 'Horario semanal',
        addTimeSlot: 'Añadir franja horaria',
        day: 'Día',
        open: 'Abierto',
        closed: 'Cerrado',
        hours: 'Horas',
        editSlot: 'Editar franja',
        deleteSlot: 'Eliminar franja',
        deleteSlotConfirmTitle: '¿Eliminar esta franja horaria?',
        deleteSlotConfirmDesc:
          'Se eliminará la franja seleccionada de este día.',
        deleteSlotConfirmMultiDay:
          'Este servicio usa el mismo código en {{count}} días. Al eliminarlo se quitará de todos esos días.',
        extraordinarySection: 'Aperturas / cierres extraordinarios',
        addException: 'Añadir excepción',
        dateRange: 'Rango de fechas',
        type: 'Tipo',
        closedOpenDay: 'Día cerrado/abierto',
        timeSlots: 'Franjas horarias',
        cuttingMin: 'Corte (min)',
        maxGuests: 'Máx. invitados',
        noShifts: 'Aún no hay turnos configurados.',
        emptyWeeklyHours:
          'No hay horario semanal configurado. Usa + para crear un horario para todos los días.',
        emptyExtraordinary:
          'No hay aperturas o cierres extraordinarios configurados.',
        createForAllDays: 'Crear configuración general para todos los días',
        resetConfiguration: 'Restablecer configuración',
        saveConfiguration: 'Guardar configuración',
        resetConfirmTitle: '¿Eliminar todas las configuraciones existentes?',
        resetConfirmDesc:
          'Esto eliminará todos los ajustes de horario de este formulario.',
        resetProceed: 'Continuar',
        tags: 'Etiquetas',
        tagsPlaceholder: 'Introduce etiquetas separadas por comas',
        tagsPickerDesc: 'Selecciona etiquetas para esta franja horaria.',
        tagsPickerEmpty: 'No hay etiquetas disponibles para esta sede.',
        allowOverbook: 'Permitir overbooking',
        alertMessage: 'Mensaje de alerta',
        alertMessagePlaceholder:
          'Introduce un mensaje de alerta para este rango horario',
        enableOverbookBlocking: 'Activar bloqueo de overbooking',
        disableOverbookBlocking: 'Desactivar bloqueo de overbooking',
        deposit: {
          tooltipInactive: 'Configurar depósito / verificación de tarjeta',
          tooltipValidation: 'Stripe: solo verificación de tarjeta',
          tooltipDeposit: 'Depósito Stripe: {{amount}} por comensal',
          tooltipMixed: 'Configuración de Stripe mixta entre franjas',
          allStandardBands: 'Todas las franjas estándar',
          allExtraordinaryBands: 'Todas las franjas extraordinarias',
          singleBand: 'Franja horaria seleccionada',
          depositTitle: 'Depósito',
          checkCardTitle: 'Verificar tarjeta',
          bulkHint:
            'La configuración se aplicará en una sola operación a todas las franjas de esta sección.',
          requestTitle: 'Solicitar una tarjeta de crédito',
          requestDesc:
            'Puede solicitar solo la verificación de la tarjeta o también un depósito preautorizado.',
          validationTitle: 'Validación de tarjeta',
          validationDesc:
            'Verificar y vincular la tarjeta sin bloquear un importe.',
          depositOptionTitle: 'Depósito',
          depositOptionDesc:
            'Validar la tarjeta y preautorizar el importe configurado.',
          amountLabel: 'Importe EUR',
          amountHint:
            'Introduzca un importe mayor que cero para activar el depósito.',
          saveConfiguration: 'Guardar configuración',
          close: 'Cerrar',
          toastSaveSuccess: 'Configuración de depósito guardada.',
          toastSaveError: 'Error al guardar la configuración de depósito.',
        },
        legacyCreditCard: {
          enable: 'Activar la solicitud directa de datos de tarjeta',
          disable: 'Desactivar la solicitud directa de datos de tarjeta',
        },
        maxGuestsPerSlot: 'Máx. cobertura por franja horaria',
        customizeCutter:
          'Personalizar el corte de franja en la configuración estándar',
        noSlotsForDay: 'No hay franjas para este día. Usa + para añadir una.',
        slotClosedOnDate: 'Franja no disponible el {{date}}',
        alertModalTitle:
          'Alerta para el cliente al seleccionar una franja horaria',
        maxGuestsModalTitle:
          'Establece el número máximo de plazas para esta franja {{range}}',
        maxGuestsModalDesc:
          'Elige el número máximo de invitados permitidos para esta franja horaria.',
        cutterModalTitle: 'Actualizar corte de franja horaria',
        days: {
          monday: 'Lunes',
          tuesday: 'Martes',
          wednesday: 'Miércoles',
          thursday: 'Jueves',
          friday: 'Viernes',
          saturday: 'Sábado',
          sunday: 'Domingo',
        },
        addTimeAllDays: {
          title: 'Añadir hora a todos los días',
          subtitle:
            'Las horas disponibles respetan las franjas horarias del local y las ya configuradas.',
          chosenTime: 'Hora elegida',
          duration: 'Duración: {{duration}}',
          startAt: '1. Empieza a las',
          endsAt: '2. Termina a las',
          allOkDesc: 'Puedes guardar este horario para todos los días.',
          saveOnAllDays: 'Guardar en todos los días',
        },
        slotTime: {
          title: 'Hora de la franja',
          subtitle: 'Primero elige cuándo empieza. Luego elige cuándo termina.',
          allOk: 'Todo correcto',
          allOkDesc: 'Puedes guardar esta hora.',
          saveTime: 'Guardar hora',
          timeRangeInvalid: 'La hora de fin debe ser posterior a la de inicio.',
          timeRangeConflict:
            'Este intervalo horario entra en conflicto con otro. Edita o elimina uno de los intervalos.',
          occupiedTitle: 'Horarios ya ocupados',
          occupiedHint:
            'La segunda lista evita estos horarios automáticamente.',
          cannotSave: 'Cambia el horario',
          cannotSaveDesc: 'No hay una hora de fin válida para este inicio.',
          cannotSaveButton: 'No se puede guardar',
          durationMinutes: '{{count}} minutos',
          durationHours: '{{count}} horas',
          durationHoursMinutes: '{{hours}} horas y {{minutes}} minutos',
          selectService: 'Selecciona un servicio',
          selectServiceDesc:
            'Elige una franja horaria para añadir su horario de apertura.',
          noLocalSlots:
            'Los horarios se eliminaron en la configuración local. Créalos de nuevo allí y luego añádelos a este calendario.',
          selectedCount: '{{count}} servicios seleccionados',
          alreadyAdded: 'Ya añadido',
        },
        extraordinary: {
          badge: 'Configuración extraordinaria',
          createRange: 'Crear rango',
          editRange: 'Editar rango',
          description: 'Descripción',
          descriptionPlaceholder: 'P. ej. Ferragosto, evento privado...',
          rangeDate: 'Fecha del rango',
          rangeDatePlaceholder: 'Selecciona el rango de fechas',
          clearDates: 'Limpiar',
          applyDates: 'Aplicar',
          open: 'Abierto',
          create: 'Crear',
          update: 'Actualizar',
          validation: 'La descripción y el rango de fechas son obligatorios.',
          dateRequired: 'Selecciona un rango de fechas.',
          dateOrder: 'La fecha de fin no puede ser anterior a la de inicio.',
          timeRangeInvalid: 'La hora de fin debe ser posterior a la de inicio.',
          addConfiguration: 'Añadir configuración extraordinaria',
          saveConfiguration: 'Guardar configuración',
          addTimeSlot: 'Añadir franja horaria',
          deleteRange: 'Eliminar rango',
          deleteConfirmTitle: '¿Eliminar rango extraordinario?',
          deleteConfirmDesc:
            'Esto eliminará permanentemente la configuración de este rango de fechas.',
          toast: {
            createSuccess: 'Rango extraordinario creado.',
            updateSuccess: 'Rango extraordinario actualizado.',
            saveError: 'No se pudo guardar el rango extraordinario.',
            deleteSuccess: 'Rango extraordinario eliminado.',
            deleteError: 'No se pudo eliminar el rango extraordinario.',
          },
        },
        toast: {
          saveSuccess: 'Configuración de horario guardada.',
          saveError: 'No se pudo guardar el horario.',
          resetSuccess: 'Configuración de horario restablecida.',
          resetError: 'No se pudo restablecer el horario.',
          addSlotSuccess: 'Franja horaria añadida.',
          addSlotError: 'No se pudo añadir la franja horaria.',
          deleteSlotSuccess: 'Franja horaria eliminada.',
          deleteSlotError: 'No se pudo eliminar la franja horaria.',
          updateTimeSuccess: 'Franja horaria actualizada.',
          updateTimeError: 'No se pudo actualizar la franja horaria.',
          dayToggleSuccess: 'Estado del día actualizado.',
          dayToggleError: 'No se pudo actualizar el estado del día.',
          configurationMissing:
            'La configuración del día aún no está disponible.',
          slotNotReady:
            'La franja horaria no está lista. Actualiza e inténtalo de nuevo.',
          overbookToggleSuccess: 'Ajuste de overbooking actualizado.',
          overbookToggleError:
            'No se pudo actualizar el ajuste de overbooking.',
          legacyCreditCardToggleSuccess:
            'Solicitud de datos de tarjeta actualizada.',
          legacyCreditCardToggleError:
            'Error al modificar la solicitud de datos de tarjeta.',
          tagsSaveSuccess: 'Etiquetas guardadas.',
          tagsLoadError: 'No se pudieron cargar las etiquetas.',
          tagsSaveError: 'No se pudieron guardar las etiquetas.',
          tagsDeleteSuccess: 'Etiquetas eliminadas.',
          alertSaveSuccess: 'Mensaje de alerta guardado.',
          alertSaveError: 'No se pudo guardar el mensaje de alerta.',
          alertDeleteSuccess: 'Mensaje de alerta eliminado.',
          maxGuestsInvalid: 'Introduce un número válido de invitados.',
          maxGuestsSaveSuccess: 'Máximo de invitados actualizado.',
          maxGuestsSaveError: 'No se pudo actualizar el máximo de invitados.',
          cutterSaveSuccess: 'Corte de franja horaria actualizado.',
          cutterSaveError: 'No se pudo actualizar el corte de franja horaria.',
        },
      },
      formFields: {
        hint: 'Elige qué campos aparecen en el formulario público de reserva.',
        configure: 'Configurar campos del formulario',
        configureDesc:
          'Activa los campos mostrados a los clientes durante la reserva.',
        name: 'Nombre',
        surname: 'Apellido',
        phone: 'Teléfono',
        email: 'Email',
        dateOfBirth: 'Fecha de nacimiento',
        zipCode: 'Código postal (CAP)',
        customerNotes: 'Notas del cliente',
        field: 'Campo',
        mandatory: 'Obligatorio',
        nonMandatory: 'No obligatorio',
      },
      preferences: {
        sectionTitle: 'Opciones adicionales',
        sectionDescription:
          'Configuraciones adicionales; permite a tus clientes comunicar preferencias, alergias o intolerancias más fácilmente.',
        singleChoicePredefined:
          'Rellena la lista como se sugiere para crear opciones de preferencia de elección única.',
        singleChoiceAdditional:
          'Crea una opción de preferencia adicional de elección única',
        multiChoicePredefined:
          'Rellena la lista como se sugiere para crear opciones de preferencia de elección múltiple.',
        multiChoiceAdditional:
          'Crea una opción de preferencia adicional de elección múltiple',
        singleChoiceOptionsPlaceholder:
          'Ejemplos: Sala exterior | Sala interior | Bar',
        multiChoiceOptionsPlaceholder: 'Ej: Lactosa | Celíaco | Cacahuetes',
        singleChoiceFieldPlaceholder: 'Ej: Nombre del campo de elección única',
        multiChoiceFieldPlaceholder:
          'Ej: Nombre del campo de elección múltiple',
        pipeSeparatedNote: 'Nota: Separa los valores con el carácter |.',
        importRooms: 'Importar mis salas',
        importRoomsSuccess: 'Salas importadas correctamente.',
        importRoomsError: 'No se pudieron importar las salas.',
        importRoomsEmpty: 'No se han encontrado salas para esta sucursal.',
        importRoomsNotFound:
          'No se han encontrado salas para esta sucursal. Comprueba el código de sucursal o crea salas primero.',
        suggestAllergens: 'Sugiéreme una lista completa de alérgenos',
        validation: {
          fillRequired:
            'Si Mandatory o Non-mandatory está marcado, completa la etiqueta y las opciones.',
          labelRequired:
            'La etiqueta es obligatoria cuando la opción está activa.',
          optionsRequired:
            'Añade al menos una opción separada por | cuando la opción está activa.',
        },
      },
      services: {
        sectionTitle: 'Sección de productos y servicios adicionales',
        sectionDescription:
          'Crea una lista de productos y servicios para asociar al formulario de reserva.',
        mandatoryLabel:
          'Quiero que la elección de productos o servicios sea obligatoria',
        title: 'Productos y servicios adicionales',
        description: 'Ofrece extras durante el proceso de reserva.',
        add: 'Añadir producto o servicio',
        name: 'Nombre',
        descriptionCol: 'Descripción',
        price: 'Precio',
        pax: 'Pax',
        empty: 'Aún no hay productos o servicios vinculados a este formulario.',
        pickerTitle: 'Lista de servicios/productos',
        pickerDesc:
          'Selecciona uno o más elementos para asociar a este formulario.',
        pickerEmpty:
          'No hay servicios o productos disponibles para esta sucursal.',
        deleteTitle: '¿Eliminar servicio/producto?',
        deleteDesc:
          'Esto eliminará el elemento seleccionado de este formulario de reserva.',
        toast: {
          saveSuccess: 'Servicios y productos actualizados.',
          saveError: 'No se pudieron actualizar los servicios y productos.',
          deleteSuccess: 'Servicio/producto eliminado.',
          deleteError: 'No se pudo eliminar el servicio/producto.',
          loadError: 'No se pudieron cargar los servicios y productos.',
        },
      },
      formConfiguration: 'Configuración del formulario',
      formConfigurationDesc: 'Configuraciones generales del formulario',
      formImage: 'Imagen del formulario',
      formImageHint:
        'Recomendamos un logo PNG con fondo transparente. Haz doble clic en la imagen para redirigir de inmediato.',
      formBackgroundImage: 'Imagen de fondo del formulario',
      imageUpload: 'Subir imagen',
      removeImage: 'Eliminar imagen',
      redirectAfterCompilation: 'Redirigir tras la cumplimentación',
      linkForm: 'Enlace del formulario',
      buttonText: 'Texto del botón',
      dataFields: 'Campos de datos',
      requestEmail: 'Solicitar email',
      requestAddress: 'Solicitar dirección',
      requestNationality: 'Solicitar nacionalidad',
      requestZipCode: 'Solicitar código postal',
      requestDateOfBirth: 'Solicitar fecha de nacimiento',
      tagsApplied: 'Etiquetas aplicadas al formulario',
      tagsPlaceholder: 'Seleccionar etiquetas',
      tagsHint: 'Nombres de etiquetas separados por comas',
      validation: {
        buttonTextRequired: 'El texto del botón es obligatorio',
      },
    },
  },
  customers: {
    title: 'Mis clientes',
    subtitle: 'Clientes de {{branch}}',
    subtitleDefault: 'Ver y gestionar tu lista de clientes',
    statsCount: '{{count}} clientes',
    searchPlaceholder: 'Buscar nombre, correo o teléfono',
    customer: 'Cliente',
    surname: 'Apellido',
    name: 'Nombre',
    email: 'Correo',
    phone: 'Teléfono',
    contacts: 'Contactos',
    dateOfBirth: 'Fecha de nacimiento',
    reservations: 'Reservas',
    noShow: 'No Show',
    tags: 'Etiquetas',
    visits: 'Visitas',
    presenceCount: '{{count}} presencias',
    noShowCount: '{{count}} No show',
    registrationDate: 'Registrado',
    itemsPerPage: 'Elementos por página',
    range: '{{start}} - {{end}} de {{total}}',
    noBranch: 'Selecciona una sucursal para ver los clientes',
    empty: 'No se encontraron clientes',
    emptyDesc: 'Aún no hay clientes para esta sucursal.',
    downloadPdf: 'Descargar pdf',
    downloadPdfSuccess: 'PDF del historial de reservas descargado.',
    downloadPdfError: 'No se pudo descargar el PDF del historial de reservas.',
    downloadPdfEmpty: 'No se encontraron reservas para este cliente.',
    downloadPdfMissingPhone: 'Este cliente no tiene número de teléfono.',
    importExport: {
      menuLabel: 'Importar/exportar',
      import: 'Importar lista de clientes',
      export: 'Exportar lista de clientes',
      downloadSample: 'Descargar lista de ejemplo',
      downloadSampleSuccess: 'Archivo de ejemplo descargado.',
      downloadSampleError: 'No se pudo descargar el archivo de ejemplo.',
      exportSuccess: 'Lista de clientes exportada.',
      exportError: 'No se pudo exportar la lista de clientes.',
      dialogTitle: 'Importar lista de clientes',
      dialogDescription:
        'Selecciona un archivo CSV o XLSX para importar clientes.',
      chooseFile: 'Elegir archivo',
      selectedFile: 'Archivo seleccionado',
      upload: 'Subir',
      uploading: 'Subiendo lista de clientes...',
      cancel: 'Cancelar',
      close: 'Cerrar',
      invalidType: 'Selecciona un archivo CSV o XLSX.',
      importError:
        'No se pudo importar la lista de clientes. Inténtalo de nuevo.',
      importAnother: 'Importar otro archivo',
      totalRecords: 'Registros totales',
      successfulImports: 'Importaciones correctas',
      failedImports: 'Importaciones fallidas',
    },
    create: {
      new: 'Nuevo cliente',
      title: 'Crear cliente',
      action: 'Cliente',
      tooltip: 'Añadir cliente',
      submit: 'Crear cliente',
      cancel: 'Cancelar',
      firstName: 'Nombre',
      firstNamePlaceholder: 'Introduce el nombre',
      lastName: 'Apellido',
      lastNamePlaceholder: 'Introduce el apellido',
      mobile: 'Número de móvil',
      mobilePlaceholder: 'Introduce el número de móvil',
      emailPlaceholder: 'Introduce el correo electrónico',
      nickname: 'Apodo del cliente',
      nicknamePlaceholder: 'Introduce el apodo',
      tagsTitle: 'Etiquetas del cliente',
      tagsSubtitle: 'Selecciona las etiquetas asociadas al cliente.',
      tagsPlaceholder: 'Seleccionar etiquetas',
      success: 'Cliente creado correctamente.',
      error: 'No se pudo crear el cliente. Inténtalo de nuevo.',
      loading: 'Creando cliente...',
    },
    bookingHistoryPdf: {
      title: 'Historial de reservas',
      generatedMeta: 'Generado el {{date}} · {{count}} reservas',
      customerCode: 'Código de cliente: {{code}}',
      phone: 'Teléfono',
      email: 'Correo',
      birthDate: 'Fecha de nacimiento',
      address: 'Dirección',
      registration: 'Registro',
      tags: 'Etiquetas',
      notAvailable: 'No disponible',
      guestsWithChildren: '{{guests}} + {{children}} niños',
      columns: {
        date: 'Fecha',
        time: 'Hora',
        code: 'Código',
        status: 'Estado',
        guests: 'Invitados',
        source: 'Fuente',
      },
      sources: {
        WEB: 'Web',
        APP_CUSTOMER: 'Portal',
        APP: 'App',
      },
    },
    filters: {
      title: 'Filtros',
      tags: 'Etiquetas',
      birthYears: 'Años de nacimiento',
      birthMonth: 'Mes de nacimiento',
      clearAll: 'Borrar todos los filtros',
      noTags: 'No hay etiquetas disponibles',
      onlyWithEmail: 'correo configurado',
      marketingConsent: 'consentimiento de marketing',
      blacklist: 'clientes en lista negra',
      phonePrefix: 'Código de país',
      allPhonePrefixes: 'Todos los códigos',
      searchOptions: 'Buscar...',
      noSearchResults: 'No hay opciones coincidentes',
      minimumBookings: 'Reservas mínimas',
      selectMinimumBookings: 'Seleccionar reservas mínimas',
      form: 'Formulario',
      selectForm: 'Todos los formularios',
      noForms: 'No hay formularios disponibles',
      bookingPeriodTitle: 'Reservas en el período',
      bookingRangeLabel: 'Rango de reservas',
      bookingRangePlaceholder: 'Seleccionar rango de reservas',
      removePeriod: 'Eliminar período',
      minimumBookingsOptions: {
        '1': 'Al menos 1 reserva',
        '2': 'Al menos 2 reservas',
        '3': 'Al menos 3 reservas',
        '4': 'Al menos 4 reservas',
        '5': 'Al menos 5 reservas',
        '10': 'Al menos 10 reservas',
        '15': 'Al menos 15 reservas',
        '20': 'Al menos 20 reservas',
        '30': 'Al menos 30 reservas',
        '50': 'Al menos 50 reservas',
      },
      months: {
        '1': 'Enero',
        '2': 'Febrero',
        '3': 'Marzo',
        '4': 'Abril',
        '5': 'Mayo',
        '6': 'Junio',
        '7': 'Julio',
        '8': 'Agosto',
        '9': 'Septiembre',
        '10': 'Octubre',
        '11': 'Noviembre',
        '12': 'Diciembre',
      },
    },
  },
  customerDetails: {
    title: 'Detalles del Cliente',
    subtitle: 'Detalles del Cliente para {{branch}}',
    subtitleDefault: 'Ver y administrar los detalles del cliente',
    deleteModelTitle: '¿Eliminar cliente?',
    deleteModelDescription:
      'Al eliminar el cliente también se eliminarán las reservas asociadas.',
    deleteModelConfirmText: 'Sí, eliminar',
    deleteModelCancelText: 'Cancelar',
    card: {
      title: 'Ficha de cliente',
      close: 'Cerrar',
      delete: 'Eliminar',
      update: 'Actualizar cliente',
      name: 'Nombre',
      surname: 'Apellido',
      nickname: 'Apodo',
      mobile: 'Móvil',
      address: 'Dirección',
      city: 'Ciudad',
      province: 'Provincia',
      postalCode: 'Código postal',
      country: 'País',
      personalDataTitle: 'Datos personales',
      personalDataSubtitle: 'Nombre, apellido y contactos principales.',
      phoneTitle: 'Introduce tu número de teléfono para continuar',
      phoneSubtitle: 'Código de país y número móvil.',
      addressTitle: 'Dirección',
      addressSubtitle: 'Datos geográficos y contacto del cliente.',
      birthDateTitle: 'Fecha de nacimiento',
      birthDateSubtitle: 'Formato de almacenamiento: yyyy-MM-dd.',
      tagsTitle: 'Etiqueta de cliente',
      tagsSubtitle: 'Selecciona las etiquetas asociadas al cliente.',
      tagsPlaceholder: 'Seleccionar etiquetas',
      updateSuccess: 'Cliente actualizado correctamente.',
      updateError: 'No se pudo actualizar el cliente. Inténtalo de nuevo.',
      deleteSuccess: 'Cliente eliminado correctamente.',
      deleteError: 'No se pudo eliminar el cliente. Inténtalo de nuevo.',
    },
  },
  dcfQuestions: {
    title: 'Preguntas y etiquetas de respuestas',
    description:
      'Recopila datos del cliente y respuestas en un formulario. Asigna etiquetas a preguntas respondidas y opciones seleccionadas.',
    backendRequired:
      'Las preguntas requieren actualizar la API. Puedes configurarlas aquí, pero no guardarlas hasta que el servicio las admita.',
    saveMismatch:
      'La API no devolvió las preguntas guardadas. Tus cambios siguen aquí. Contacta con el equipo de API.',
    unavailable:
      'Este formulario aún no acepta respuestas. Inténtalo más tarde.',
  },
  dataCollectionForms: {
    title: 'Formulario de Recopilación de Datos',
    subtitle: 'Formularios presentes',
    addDataCollectionForm: 'Agregar Formulario',
    searchPlaceholder: 'Buscar formularios de recopilación de datos por nombre',
    noDataTitle: 'No hay formularios presentes',
    noDataDesc:
      'Crea tu primer formulario de recopilación de datos para empezar a recopilar leads y interacciones.',
    form: 'Formulario',
    tags: 'Etiquetas',
    tagsMore: '+{{count}} más',
    tagsLess: 'Menos',
    status: 'Estado',
    interactions: 'Interacciones',
    interactionsCount: '{{count}} interacciones',
    url: 'URL',
    qrCode: 'Código QR',
    preview: 'Vista previa',
    copyUrl: 'Copiar URL',
    downloadQr: 'Descargar QR',
    editDataCollectionForm: 'Editar formulario de recopilación de datos',
    statusActive: 'Activo',
    statusInactive: 'Inactivo',
    statusSuspended: 'Suspendido',
    statusDraft: 'Borrador',
    statsCount: '{{count}} formularios de recopilación de datos',
    updatedAgo: 'Actualizado hace 2 minutos',
    deleteDataCollectionForm: 'Eliminar formulario de recopilación de datos',
    duplicate: 'Duplicar',
    createTitle: 'Crear formulario de recopilación de datos',
    formName: 'Nombre del formulario',
    formNamePlaceholder: 'Ingrese el nombre del formulario',
    createForm: 'Crear formulario',
    deleteModel: {
      deleteTitle: '¿Eliminar formulario de recopilación de datos?',
      deleteDescription:
        '¿Está seguro de que desea eliminar este formulario? Esta acción no se puede deshacer.',
    },
    validation: {
      formNameMin: 'El nombre debe tener al menos 2 caracteres',
      formNameMax: 'El nombre no puede exceder 255 caracteres',
    },
    scanQrAccess: 'Escanee el código QR para acceder a:',
    qrClose: 'Cerrar',
    qrDownload: 'Descargar',
    toast: {
      createSuccess: 'Formulario creado correctamente.',
      createError: 'No se pudo crear el formulario. Inténtalo de nuevo.',
      updateSuccess: 'Formulario actualizado correctamente.',
      updateError: 'No se pudo actualizar el formulario. Inténtalo de nuevo.',
      logoUploadSuccess: 'Logo subido correctamente.',
      logoUploadError: 'No se pudo subir el logo.',
      logoRemoveSuccess: 'Logo eliminado correctamente.',
      logoRemoveError: 'No se pudo eliminar el logo.',
      bgUploadSuccess: 'Fondo subido correctamente.',
      bgUploadError: 'No se pudo subir el fondo.',
      bgRemoveSuccess: 'Fondo eliminado correctamente.',
      bgRemoveError: 'No se pudo eliminar el fondo.',
      statusUpdateSuccess: 'Estado actualizado correctamente.',
      statusUpdateError: 'No se pudo actualizar el estado. Inténtalo de nuevo.',
      deleteSuccess: 'Formulario eliminado correctamente.',
      deleteError: 'No se pudo eliminar el formulario. Inténtalo de nuevo.',
      duplicateSuccess: 'Formulario duplicado correctamente.',
      duplicateError: 'No se pudo duplicar el formulario. Inténtalo de nuevo.',
    },
    edit: {
      pageTitle: 'Consentimiento de datos personales',
      subtitle: 'Configurar el formulario de recopilación de datos',
      close: 'Cerrar',
      formConfiguration: 'Configuración del formulario',
      formConfigurationDesc:
        'Configuración general del formulario de recopilación de datos',
      formImage: 'Imagen del formulario',
      formImageHint:
        'Recomendamos un logo PNG con fondo transparente. Doble clic en la imagen para redirigir.',
      formBackgroundImage: 'Imagen de fondo del formulario',
      imageUpload: 'Subir imagen',
      removeImage: 'Eliminar imagen',
      redirectAfterCompilation: 'Redirección después de la compilación',
      linkForm: 'Enlace del formulario',
      buttonText: 'Texto del botón',
      description: 'Descripción',
      descriptionPlaceholder:
        'Texto que se muestra a la izquierda del formulario público',
      dataFields: 'Campos de datos',
      requestEmail: 'Solicitar email',
      requestAddress: 'Solicitar dirección leave',
      requestNationality: 'Solicitar nacionalidad',
      requestZipCode: 'Solicitar código postal',
      requestDateOfBirth: 'Solicitar fecha de nacimiento',
      tagsApplied: 'Etiquetas aplicadas al formulario',
      tagsOnDownload: 'Etiquetas aplicadas al descargar',
      tagsOnUse: 'Etiquetas asignadas después de guardar los datos',
      tagsPlaceholder: 'Seleccionar etiquetas',
      tagsHint: 'Nombres de etiquetas separados por comas',
      uploading: 'Subiendo...',
      removing: 'Eliminando...',
      validation: {
        redirectUrlRequired: 'La URL de redirección es obligatoria.',
        redirectUrlInvalid:
          'Introduce una URL válida (p. ej. https://example.com).',
      },
    },
  },
  publicDataCollectionForm: {
    documentTitle: 'Formulario de recogida de datos',
    loading: 'Cargando formulario',
    missingFormCode: 'Falta el código del formulario en la URL.',
    loadError: 'No se pudo cargar este formulario de recogida de datos.',
    unavailableTitle: 'Formulario no disponible',
    unavailableMessage:
      'Este formulario de recogida de datos está suspendido o no disponible.',
    personalInfo: 'Información personal',
    prefixLabel: 'Prefijo',
    phoneLabel: 'Móvil',
    phonePlaceholder: 'Tu número',
    firstName: 'Nombre',
    firstNamePlaceholder: 'Tu nombre',
    lastName: 'Apellido',
    lastNamePlaceholder: 'Tu apellido',
    email: 'Correo',
    emailPlaceholder: 'nombre@ejemplo.es',
    optional: 'Opcional',
    cap: 'Código postal',
    capPlaceholder: 'Ej. 09124',
    birthDate: 'Fecha de nacimiento',
    address: 'Dirección',
    addressPlaceholder: 'Calle y número',
    nationality: 'Nacionalidad',
    nationalityPlaceholder: 'Tu nacionalidad',
    requiredNote: '* Campos obligatorios',
    consentsTitle: 'Tus preferencias',
    privacyConsent: 'Privacidad y tratamiento de datos',
    marketingConsent: 'Consentimiento de marketing y comunicación',
    profilingConsent: 'Consentimiento de perfilado',
    detailsLink: 'Información',
    selectAll: 'Seleccionar todo',
    submit: 'Acceder a la página',
    welcomeBackTitle: '¡Bienvenido de nuevo, {{name}}!',
    welcomeBackTitleGeneric: '¡Bienvenido de nuevo!',
    welcomeBackMessage:
      'Ya tenemos tus datos. Puedes añadir otro contacto si es para otra persona.',
    addAnotherContact: 'Añadir otro contacto',
    toast: {
      success: 'Datos enviados correctamente.',
      error: 'No se pudieron enviar los datos. Inténtalo de nuevo.',
    },
    thankYou: {
      title: '¡Gracias!',
      message: 'Tus datos se han enviado correctamente.',
      continue: 'Continuar',
    },
    validation: {
      prefixRequired: 'Selecciona un prefijo internacional.',
      phoneMin: 'Introduce un número de móvil válido.',
      firstNameRequired: 'El nombre es obligatorio.',
      lastNameRequired: 'El apellido es obligatorio.',
      emailRequired: 'El correo es obligatorio.',
      emailInvalid: 'Introduce un correo válido.',
      birthDateInvalid: 'Introduce una fecha válida que no sea futura.',
      privacyRequired: 'El consentimiento de privacidad es obligatorio.',
    },
  },
  bulkMessage: {
    title: 'Envío en masa',
    subtitle: 'Gestiona campañas de WhatsApp, Email y SMS para {{branch}}',
    subtitleDefault: 'Ver y administrar tus campañas de mensajes masivos',
    statsCount: '{{count}} campañas',
    searchPlaceholder: 'Buscar envío masivo...',
    campaignName: 'Nombre de la campaña',
    campaignNamePlaceholder: 'Introduce el nombre de la campaña',
    addCampaign: 'Nueva campaña',
    creating: 'Creando...',
    createTitle: 'Crear nueva campaña de envío de mensajes',
    createDesc: 'Añade una nueva campaña de mensajes masivos para tu sucursal.',
    close: 'Cerrar',
    duplicate: 'Duplicar',
    editCampaign: 'Editar campaña',
    viewDetails: 'Ver detalles',
    pause: 'Pausar campaña',
    resume: 'Reanudar envío',
    delete: 'Eliminar campaña',
    noBranch: 'Selecciona una sucursal para ver los mensajes masivos',
    empty: 'No se encontraron campañas',
    emptyDesc:
      'Todavía no hay campañas de mensajes masivos para esta sucursal.',
    channels: {
      whatsappWithImage: 'WHATSAPP CON IMAGEN',
    },
    columns: {
      name: 'Nombre',
      type: 'Tipo',
      state: 'Estado',
      progress: 'Progreso',
      selectedCustomers: 'Clientes seleccionados',
      status: 'Estado',
      channel: 'Canal',
      messageType: 'Tipo de mensaje',
      customer: 'Cliente',
      reference: 'Referencia',
      attempts: 'Intentos',
      createdAt: 'Creado el',
    },
    statusFilter: {
      all: 'Todos los estados',
      pending: 'Pendiente',
      sent: 'Enviado',
      delivered: 'Entregado',
      failed: 'Fallido',
    },
    noDataTitle: 'No se encontraron mensajes',
    noDataDesc:
      'Todavía no hay eventos de entrega de mensajes para esta sucursal.',
    deleteModal: {
      title: '¿Eliminar campaña?',
      description:
        '¿Seguro que quieres eliminar esta campaña? Esta acción no se puede deshacer.',
    },
    details: {
      pageTitle: 'Campaña {{name}}',
      subtitle: 'Progreso de la campaña de envío masivo',
      empty: 'No se encontraron resultados',
      emptyDesc: 'Todavía no hay eventos de entrega para esta campaña.',
      loadError:
        'No se pudieron cargar los eventos de entrega. Inténtalo de nuevo.',
      emailLabel: 'Email:',
      phoneLabel: 'Tel:',
      chatId: 'Chat {{id}}',
      httpStatus: 'HTTP {{status}}',
      resubmit: 'Reenviar mensaje',
      resubmitSuccess: 'Evento vuelto a poner en cola correctamente.',
      resubmitError:
        'No se pudo volver a poner el mensaje en cola. Inténtalo de nuevo.',
      resubmitConfirmTitle: 'El intento anterior se completó correctamente',
      resubmitConfirmDescription:
        '¿Seguro que quieres enviar este mensaje de nuevo?',
      resubmitConfirmAction: 'Sí, enviar de nuevo',
      columns: {
        recipient: 'Destinatario',
        message: 'Mensaje',
        channel: 'Canal',
        status: 'Resultado',
        details: 'Detalles',
      },
    },
    validation: {
      nameMin: 'El nombre de la campaña debe tener al menos 2 caracteres',
      nameMax: 'El nombre de la campaña no puede superar los 120 caracteres',
    },
    toast: {
      createSuccess: 'Campaña creada correctamente.',
      createError: 'No se pudo crear la campaña. Inténtalo de nuevo.',
      updateSuccess: 'Campaña guardada correctamente.',
      updateError: 'No se pudo guardar la campaña. Inténtalo de nuevo.',
      deleteSuccess: 'Campaña eliminada correctamente.',
      deleteError: 'No se pudo eliminar la campaña. Inténtalo de nuevo.',
      duplicateSuccess: 'Campaña duplicada correctamente.',
      duplicateError: 'No se pudo duplicar la campaña. Inténtalo de nuevo.',
      imageRemoveSuccess: 'Imagen adjunta eliminada correctamente.',
      imageRemoveError: 'No se pudo eliminar la imagen adjunta.',
      imageUploadSuccess: 'Imagen subida correctamente.',
      imageUploadError: 'No se pudo subir la imagen. Inténtalo de nuevo.',
      imageTypeError: 'Solo se permiten imágenes JPG y PNG.',
      imageSizeError: 'La imagen debe ser de 5 MB o menos.',
      startSuccess:
        'El envío masivo ha comenzado. Supervisa la lista para ver el progreso.',
      startError: 'No se pudo iniciar la campaña. Inténtalo de nuevo.',
      pauseSuccess: 'Campaña pausada correctamente.',
      pauseError:
        'No se pudo pausar la campaña. Inténtalo de nuevo en un par de minutos.',
      resumeSuccess: 'Campaña reanudada correctamente.',
      resumeError:
        'No se pudo reanudar la campaña. Inténtalo de nuevo en un par de minutos.',
    },
    edit: {
      pageTitle: 'Editar {{name}}',
      subtitle:
        'Configura los detalles de la campaña y el contenido del mensaje',
      startCampaign: 'Iniciar campaña',
      start: {
        confirmTitle: '¿Continuamos?',
        confirmDescription:
          'Vas a enviar un mensaje masivo de tipo {{channel}} a {{count}} clientes.',
        confirmAction: 'Sí, continuar',
        noRecipients:
          'No hay destinatarios. Añade destinatarios para iniciar la campaña.',
        emptyMessage: 'Introduce un mensaje para iniciar la campaña.',
        emptySubject: 'Introduce un asunto para iniciar la campaña de email.',
        missingImage:
          'Añade una imagen para iniciar la campaña de WhatsApp con imagen.',
        missingTemplate:
          'Selecciona una plantilla para iniciar la campaña de WhatsApp Business.',
        corruptTemplateVariables:
          'Las variables de esta plantilla están vacías o no disponibles. La plantilla parece corrupta. Elige otra plantilla.',
        corruptTemplateLanguages:
          'Esta plantilla no tiene idiomas asociados y parece corrupta. Elige otra plantilla.',
      },
      saveConfiguration: 'Guardar configuración',
      configTitle: 'Configuración de mensaje masivo',
      configDesc:
        'Configura campañas de WhatsApp y email para enviar a clientes seleccionados.',
      configuration: 'Configuración',
      configurationDesc:
        'Define el nombre de la campaña y el canal de envío masivo.',
      audience: 'Audiencia',
      selectedCustomers: 'Clientes seleccionados',
      customersReady: '{{count}} clientes listos para el envío masivo',
      selectCustomers: 'Seleccionar clientes',
      selectCustomersSoon: 'La selección de clientes estará disponible pronto.',
      channel: 'Canal',
      whatsappTitle: 'Mensaje de WhatsApp',
      whatsappDesc: 'Configura el contenido del mensaje de WhatsApp.',
      whatsappPlaceholder: 'Escribe tu mensaje aquí...',
      whatsappBusinessTitle: 'Plantilla de WhatsApp Business',
      whatsappBusinessDesc:
        'Selecciona plantillas aprobadas por Meta para enviar vía WhatsApp Business API.',
      whatsappBusinessEmptyDesc:
        'Selecciona una plantilla aprobada por Meta para configurar el envío vía WhatsApp Business API.',
      selectedTemplate: 'Plantilla seleccionada',
      noTemplateSelected: 'Ninguna plantilla seleccionada',
      selectTemplate: 'Seleccionar plantilla',
      selectTemplateTooltip:
        'Selecciona una plantilla de las aprobadas por Meta',
      selectTemplateSoon:
        'La selección de plantillas estará disponible pronto.',
      templateModal: {
        title: 'Selecciona una plantilla',
        description:
          'Elige una de las plantillas aprobadas en WhatsApp Business API.',
        messageTypeBadge: 'Para {{messageType}}',
        countBadge: '{{count}} plantillas',
        countBadgeFiltered: '{{count}} de {{total}}',
        searchPlaceholder: 'Buscar plantilla por nombre...',
        empty: 'No hay plantillas disponibles',
        selectAction: 'Seleccionar plantilla',
        recommended:
          'Plantilla recomendada según el tipo de mensaje seleccionado ({{messageType}})',
        bodyPreview: 'Body Preview',
        noPreview: 'No hay vista previa del cuerpo.',
        noCta: 'No CTA',
        ctaEnabled: 'CTA Enabled',
        corruptHint:
          'Esta plantilla parece corrupta y no se puede usar. Probablemente se modificó manualmente en Facebook Business Manager y ya no respeta la estructura requerida por WhatsApp Business API.',
        loadError: 'No se pudieron cargar las plantillas',
        loadErrorDesc:
          'Error al obtener las plantillas de WhatsApp Business. Inténtalo de nuevo.',
        selectSuccess: 'Plantilla seleccionada correctamente.',
        selectError: 'No se pudo seleccionar la plantilla. Inténtalo de nuevo.',
      },
      whatsappWithImageTitle: 'WhatsApp con imagen',
      whatsappWithImageDesc:
        'Configura el mensaje, la imagen adjunta y los marcadores dinámicos.',
      whatsappMessageText: 'Texto del mensaje de WhatsApp',
      emailTitle: 'Email masivo',
      emailDesc: 'Configura email, adjuntos y contenido HTML.',
      emailSubject: 'Asunto del email',
      emailSubjectPlaceholder: 'Ej: Promo especial para ti 🎉',
      emailBody: 'Contenido del email',
      emailBodyHint: 'Si insertas un enlace, deja un espacio',
      applyTemplate: 'Aplicar plantilla',
      sendTest: 'Enviar mensaje de prueba',
      attachedImage: 'Imagen adjunta',
      uploadImage: 'Subir imagen',
      removeImage: 'Eliminar imagen',
      uploading: 'Subiendo...',
      removing: 'Eliminando...',
      test: {
        title: 'Envío de prueba',
        emailTitle: 'Envío de prueba',
        oopsTitle: 'Oops...',
        ok: 'OK',
        send: 'Enviar',
        testSending: 'Envío de prueba',
        backwards: 'Atrás',
        emptyMessage: 'Introduce un mensaje para probar el envío.',
        emptySubject: 'Introduce un asunto de email para probar el envío.',
        missingImage:
          'No hay imagen asociada a esta campaña. Añade una para probar el envío con imagen.',
        mobilePlaceholder: 'Teléfono móvil',
        emptyPhone: 'Introduce un número de móvil para probar el envío.',
        emptyEmail: 'Introduce un email para probar el envío.',
        emailLabel: 'Email:',
        emailPlaceholder: 'Introduce el email donde quieres recibir la prueba',
        apiSoon: 'La API de envío de prueba estará disponible pronto.',
        sendSuccess: 'Mensaje de prueba enviado correctamente.',
        sendError:
          'No se pudo enviar el mensaje de prueba. Inténtalo de nuevo.',
      },
    },
    selectCustomers: {
      pageTitle: 'Asignar clientes a {{name}}',
      subtitle: 'Filtra y selecciona clientes para esta campaña',
      subtitleWithBranch: 'Filtra y selecciona clientes para {{branch}}',
      searchPlaceholder: 'Buscar cliente...',
      selected: 'Seleccionados',
      clearSelection: 'Borrar selección',
      removeAll: 'Eliminar todos',
      saveList: 'Guardar lista de clientes',
      bookingPeriodTitle: 'Reservas en el periodo',
      bookingRangeLabel: 'Rango de reservas',
      bookingRangePlaceholder: 'Seleccionar rango de reservas',
      removePeriod: 'Eliminar periodo',
      marketingConsentShort: 'Consentimiento marketing',
      listTitle: 'Lista de clientes',
      listDesc: 'Gestionar la selección de clientes',
      selectPage: 'Seleccionar página',
      deselectPage: 'Deseleccionar página',
      selectAll: 'Seleccionar todo',
      deselectAll: 'Deseleccionar todo',
      progressAdding: 'Añadiendo {{current}} de {{total}}',
      selectRow: 'Seleccionar cliente',
      results: 'Resultados',
      page: 'Página',
      noEmail: 'Sin email',
      columns: {
        customer: 'Cliente',
        phone: 'Teléfono',
        birth: 'Nacimiento',
        bookings: 'Reservas',
        tags: 'Etiquetas',
      },
      toast: {
        saveSuccess: 'Lista de clientes guardada correctamente.',
        saveError:
          'No se pudo guardar la lista de clientes. Inténtalo de nuevo.',
        updateError:
          'No se pudo actualizar la selección de clientes. Inténtalo de nuevo.',
        selectAllSuccess: 'Se seleccionaron {{count}} clientes.',
        selectAllPartial:
          'Se añadieron {{added}} clientes y luego falló la solicitud. Intenta Seleccionar todo de nuevo.',
        clearSuccess: 'Todos los clientes eliminados de la selección.',
        clearError:
          'No se pudo borrar la selección de clientes. Inténtalo de nuevo.',
      },
    },
  },
  fidelity: {
    title: 'Fidelidad',
    subtitleDefault: 'Ver y administrar tus fidelidades',
    statsCount: '{{count}} fidelidades',
    searchPlaceholder: 'Buscar una fidelidad por nombre...',
    addProgram: 'Nueva fidelidad',
    creating: 'Creando...',
    saving: 'Guardando...',
    createTitle: 'Crear fidelidad',
    createDesc: 'Añade una nueva fidelidad para tu sucursal.',
    createActiveConflict:
      'Ya existe una fidelidad {{type}} activa "{{name}}" para esta sucursal.',
    createActiveConflictAction:
      'Desactiva primero la fidelidad existente, o deja esta inactiva.',
    createActiveBlocked:
      'Desactiva Fidelidad activa para continuar, o desactiva "{{name}}" en la lista.',
    edit: {
      pageTitle: 'Modificar fidelidad',
    },
    programName: 'Nombre de la fidelidad',
    programNamePlaceholder: 'Introduce el nombre de la fidelidad',
    description: 'Descripción',
    descriptionPlaceholder: 'Introduce la descripción de la fidelidad',
    policyMessageCard: 'Política / Tarjeta de mensaje',
    tagsOnDownload: 'Etiquetas aplicadas al descargar',
    tagsOnUse: 'Etiquetas aplicadas al usar',
    tagsPlaceholder: 'Seleccionar etiquetas',
    type: 'Tipo',
    typePoints: 'Puntos',
    typeDiscount: 'Descuento',
    typeDiscountPrepaid: 'Descuento / prepago',
    customerCards: 'Tarjetas de clientes',
    noThresholds: 'No hay umbrales de puntos configurados.',
    codeLinePrepaid:
      'Programa con crédito prepago y descuento aplicado a la tarjeta.',
    color: 'Color',
    value: 'Valor',
    active: 'Activo',
    inactive: 'Inactivo',
    activeProgram: 'Fidelidad activa',
    discountApplied: 'Descuento aplicado (%)',
    codeLineRange: 'De {{from}} a {{to}} euros',
    codeLineDiscount: '{{value}}% de descuento',
    thresholdCount: '{{count}} umbrales',
    customersCount: '{{count}} clientes',
    thresholdsTitle: 'Umbrales de puntos',
    thresholdsHint: 'El backend reordena los umbrales y cierra los huecos.',
    addThreshold: 'Umbral',
    updateThreshold: 'Actualizar umbral',
    thresholdFrom: 'Desde euros*',
    thresholdTo: 'Hasta euros*',
    thresholdPointsLabel: 'Puntos*',
    thresholdLabel: 'Etiqueta',
    thresholdColor: 'Color del umbral',
    editProgram: 'Editar fidelidad',
    deleteProgram: 'Eliminar fidelidad',
    duplicate: 'Duplicar',
    editThreshold: 'Editar umbral',
    deleteThreshold: 'Eliminar umbral',
    noBranch: 'Selecciona una sucursal para ver las fidelidades',
    empty: 'No hay fidelidad configurada',
    emptyDesc:
      'Crea una fidelidad de puntos o descuento para empezar a generar tarjetas de cliente.',
    cardsTitle: 'Tarjetas de cliente',
    cardsSubtitle: 'Tarjetas emitidas, QR y saldos',
    cardsEmpty:
      'No se encontraron tarjetas de fidelización para esta fidelidad.',
    searchCustomerPlaceholder: 'Sobre el cliente, la tarjeta...',
    createCard: 'Crear tarjeta',
    refreshCards: 'Actualizar tarjetas',
    update: 'Actualizar',
    scanQrCode: 'Escanear código QR',
    useCard: 'Usar tarjeta',
    toggleCardStatus: 'Cambiar estado usado/disponible',
    operationHistory: 'Historial de operaciones',
    qrCodeTitle: 'Escanear código QR',
    downloadQr: 'Descargar',
    historyTitle: 'Historial de la tarjeta de fidelización',
    historyEmpty: 'No hay operaciones disponibles para esta tarjeta.',
    postedBy: 'Publicado por',
    branchCode: 'Sucursal',
    fidelityCode: 'Fidelización',
    customerId: 'ID del cliente',
    pointsSuffix: 'puntos',
    amountSuffix: 'euros',
    cardAvailable: 'disponible',
    cardUsed: 'usada',
    columns: {
      qr: 'QR',
      customer: 'Cliente',
      contacts: 'Contactos',
      card: 'Tarjeta',
      balance: 'Saldo',
      state: 'Estado',
      activation: 'Activación',
      threshold: 'Umbral',
      label: 'Etiqueta',
      points: 'Puntos',
      color: 'Color',
    },
    createCardPage: {
      title: 'Elegir cliente',
      subtitle: 'Selecciona un cliente para emitir una nueva tarjeta.',
      subtitleWithProgram: 'Clientes de {{branch}} · {{program}}',
      newCard: 'Nueva tarjeta',
      chooseCustomer: 'Elegir cliente',
      searchPlaceholder: 'Buscar por nombre, teléfono o correo...',
      results: 'Resultados',
      page: 'Página',
      availableCustomers: 'Clientes disponibles',
      availableCustomersDesc:
        'Selecciona un cliente para abrir la confirmación de creación.',
      noEmail: 'Sin correo',
      columns: {
        customer: 'Cliente',
        telephone: 'Teléfono',
        wood: 'Visitas',
        tags: 'Etiquetas',
      },
    },
    createCardConfirm: {
      title: '¿Quieres crear una tarjeta para este cliente?',
      confirm: 'Sí, crear tarjetas',
      creating: 'Creando...',
    },
    statCards: {
      programs: 'Fidelidad',
      active: 'Activos',
      types: 'Tipos',
    },
    deleteModal: {
      title: '¿Eliminar fidelidad?',
      description:
        '¿Seguro que deseas eliminar esta fidelidad? Esta acción no se puede deshacer.',
    },
    deleteThresholdModal: {
      title: '¿Eliminar umbral?',
      description:
        '¿Seguro que deseas eliminar este umbral? Esta acción no se puede deshacer.',
    },
    thresholdModal: {
      createTitle: 'Añadir umbral de puntos',
      editTitle: 'Cambiar umbral de puntos',
      description:
        'Configura el rango, los puntos, la etiqueta y el color del umbral.',
      lowerLimit: 'Límite inferior*',
      upperLimit: 'Límite superior*',
      pointsApplied: 'Puntos aplicados*',
    },
    toast: {
      createSuccess: 'Fidelidad creada correctamente.',
      createError: 'No se pudo crear la fidelidad. Inténtalo de nuevo.',
      updateSuccess: 'Fidelidad actualizada correctamente.',
      updateError: 'No se pudo actualizar la fidelidad. Inténtalo de nuevo.',
      deleteSuccess: 'Fidelidad eliminada correctamente.',
      deleteError: 'No se pudo eliminar la fidelidad. Inténtalo de nuevo.',
      duplicateSuccess: 'Fidelidad duplicada correctamente.',
      duplicateError: 'No se pudo duplicar la fidelidad. Inténtalo de nuevo.',
      thresholdCreateSuccess: 'Umbral creado correctamente.',
      thresholdCreateError: 'No se pudo crear el umbral.',
      thresholdUpdateSuccess: 'Umbral actualizado correctamente.',
      thresholdUpdateError: 'No se pudo actualizar el umbral.',
      thresholdDeleteSuccess: 'Umbral eliminado correctamente.',
      thresholdDeleteError: 'No se pudo eliminar el umbral.',
      cardCreateSuccess: 'Tarjeta de fidelización creada correctamente.',
      cardCreateError: 'No se pudo crear la tarjeta de fidelización.',
      cardUseSuccess: 'Tarjeta marcada como usada.',
      cardUseError: 'No se pudo actualizar el uso de la tarjeta.',
      cardToggleSuccess: 'Estado de la tarjeta actualizado correctamente.',
      cardToggleError: 'No se pudo actualizar el estado de la tarjeta.',
    },
    operations: {
      addPoints: 'Puntos añadidos',
      updatePoints: 'Puntos actualizados',
      removePoints: 'Puntos eliminados',
      addBillAmount: 'Importe de factura añadido',
      addPrepaidAmount: 'Importe prepago añadido',
      updatePrepaidAmount: 'Importe prepago actualizado',
      disableAccount: 'Cuenta desactivada',
      createAccount: 'Cuenta creada',
      used: 'Tarjeta usada',
    },
    registration: {
      title: 'Alta autónoma de clientes',
      description:
        'Comparte este enlace: los clientes pueden crear su propia tarjeta fidelity.',
      linkLabel: 'Enlace de inscripción fidelity',
      openForm: 'Abrir el formulario',
      copy: 'Copiar enlace',
      qr: 'Código QR',
    },
    views: {
      label: 'Programa fidelity',
      configuration: 'Configuración',
      configurationHint: 'Reglas, ventajas y mensajes',
      cards: 'Tarjetas de clientes',
      cardsHint: 'Tarjetas emitidas, QR y movimientos',
      allPrograms: 'Todos los programas fidelity',
    },
    wizard: {
      program: 'Programa y ventajas',
      messages: 'Mensajes',
      review: 'Resumen',
      back: 'Atrás',
      continue: 'Continuar',
      save: 'Guardar configuración',
      stepOf: '{{current}} / {{total}}',
      stepListLabel: 'Pasos de configuración de fidelidad',
      pointsIntro:
        'Define el programa, las reglas para acumular puntos y el mensaje de bienvenida.',
      discountIntro:
        'Define el porcentaje, el aspecto de la tarjeta y el mensaje de bienvenida.',
      discountNote:
        'Este programa usa un porcentaje de descuento y no acumula puntos. El saldo prepago se gestiona en la ficha del cliente.',
      benefitPoints: 'Acumulación de puntos',
      benefitDiscount: '{{value}}% de descuento',
      messageLabel: 'Mensaje de bienvenida',
      messagePlaceholder: 'Elige un mensaje fidelity',
      messageStatus: '{{status}} · WhatsApp {{whatsapp}} · Email {{email}}',
      messageActive: 'Activo',
      messageInactive: 'Inactivo',
      channelOn: 'activo',
      channelOff: 'inactivo',
      messageWarning:
        'Activa WhatsApp o el email de este mensaje antes de guardar.',
      messageLoadError: 'No se pudieron cargar los mensajes.',
      configureMessages: 'Configurar mensajes',
      rulesLabel: 'Reglas del programa',
      reviewTitle: 'Resumen del programa',
      name: 'Nombre',
      status: 'Estado',
      benefit: 'Ventaja',
      message: 'Mensaje',
      autoUpdates: 'Actualizaciones automáticas',
      autoUpdatesValue:
        'Un mensaje dedicado para cada operación, configurable en Mensajes.',
      createProgram: 'El programa',
      createBenefits: 'Ventajas',
      createPoints: 'Tramos de gasto',
      createRewards: 'Premios canjeables',
      createHistorical: 'Niveles acumulativos',
      createProgramDesc: 'Elige el nombre y el tipo de programa.',
      createPointsDesc:
        'Define una única regla de acumulación. Los mismos puntos alimentarán el saldo disponible y el histórico.',
      createRewardsDesc:
        'Configura los umbrales y premios canjeables. Al canjear, se descuentan los puntos del saldo disponible.',
      createHistoricalDesc:
        'Configura puntos, nombre, color y descuento de cada nivel, también desde 0 puntos. El QR Fidelity reconoce el descuento sin restar puntos históricos.',
      createBenefitsDesc:
        'Define las franjas de gasto para los puntos o el porcentaje de descuento.',
      createMessagesDesc:
        'Configura WhatsApp y el email de bienvenida con el QR de la tarjeta.',
      createReviewDesc:
        'Revisa ventajas, estado y mensaje antes de crear el programa.',
      createAction: 'Crear programa',
      afterSaveNote:
        'Podrás crear las tarjetas de cliente después de guardar el programa.',
      thresholdStepHint:
        'Para cada franja de gasto, indica cuántos puntos recibe el cliente.',
      thresholdReviewLine: 'De {{from}} a {{to}} €: {{points}} puntos',
    },
    validation: {
      nameMin: 'El nombre de la fidelidad debe tener al menos 2 caracteres',
      nameMax: 'El nombre de la fidelidad no puede superar 100 caracteres',
      colorInvalid: 'Introduce un color hexadecimal válido (p. ej. #FFFFFF)',
      thresholdRequired: 'Añade al menos un umbral.',
      thresholdRange:
        'El límite superior debe ser mayor o igual que el inferior.',
      thresholdLower: 'Introduce un límite inferior válido.',
      thresholdUpper: 'Introduce un límite superior válido.',
      thresholdPoints: 'Introduce puntos válidos.',
      thresholdLabelRequired: 'La etiqueta del umbral es obligatoria.',
      thresholdLabelMax: 'La etiqueta no puede superar 100 caracteres.',
    },
  },
  marketingGuide: {
    infoButton: 'Guía de marketing',
    title: 'Elige la herramienta de marketing adecuada',
    intro:
      'Las promociones y Fidelity pueden ofrecer ventajas, pero responden a objetivos distintos.',
    promotionTitle: 'Promociones · una campaña con fecha límite',
    promotionBody:
      'Usa una promoción para atraer nuevos clientes, reactivar a los ocasionales o impulsar un periodo tranquilo. El cliente participa mediante un enlace público o un QR y recibe un cupón; no necesita acumular puntos.\nEjemplo: “10 % de descuento hasta el domingo, con un gasto mínimo de 30 €, una vez por cliente.”',
    fidelityTitle: 'Fidelity · un motivo para volver',
    fidelityBody:
      'Usa Fidelity para premiar las visitas recurrentes. Las compras generan puntos según las reglas configuradas; al alcanzar un umbral, el cliente puede canjear un premio. El saldo y el historial quedan asociados a su tarjeta.\nEjemplo: “Acumula puntos con cada recibo y elige un premio al alcanzar un objetivo.”',
    rulesTitle: 'Reglas prácticas de marketing',
    rulesBody:
      '1. Define un objetivo claro: captar clientes, reactivarlos, aumentar las visitas o el gasto medio.\n2. Protege el margen: elige un descuento y un gasto mínimo sostenibles, y fija un límite de cantidad o presupuesto.\n3. Explica las condiciones: fechas, productos o canales válidos, gasto mínimo y compatibilidad.\n4. Facilita la comprensión y el canje; demasiadas condiciones reducen la participación.\n5. Mide los canjes y las compras posteriores, no solo los cupones emitidos o puntos acumulados.',
    togetherTitle: 'Pueden funcionar juntos',
    togetherBody:
      'Usa una promoción temporal para atraer al cliente y después invítalo a unirse a Fidelity para darle un motivo continuo para volver. Identifica claramente el cupón promocional y el premio Fidelity.',
  },
  promotions: {
    title: 'Promociones',
    titleWithBranch: 'Promo {{branch}}',
    subtitle: 'Lista de promociones para {{branch}}',
    subtitleDefault: 'Ver y administrar la lista de promociones',
    pageDescription:
      'Gestiona promociones, cupones y configuraciones promo del local',
    statsCount: '{{count}} promociones',
    searchPlaceholder: 'Buscar promociones por nombre...',
    promoName: 'Nombre de la promo',
    promoNamePlaceholder: 'Introduce el nombre de la promoción',
    description: 'Descripción',
    descriptionPlaceholder: 'Introduce la descripción de la promoción',
    used: 'Usados',
    usedCouponCount: '{{count}} cupón',
    downloaded: 'Descargados',
    messages: 'Mensajes',
    messagesTooltip: 'Mensajes enviados a través de esta promo',
    messageHistory: {
      title: 'Historial de mensajes',
      reference: 'Referencia {{code}}',
      empty: 'No hay mensajes disponibles.',
      refresh: 'Actualizar',
      loadError: 'No se pudieron cargar los mensajes.',
      attempts: '{{count}} intentos',
    },
    customerCoupons: {
      title: 'Cupones de clientes',
      subtitle: 'Promo: {{name}} · {{count}} cupones emitidos',
      discount: '{{percent}}% descuento',
      redeemed: 'Canjeado',
      notRedeemed: 'No canjeado',
      generatedOn: 'Generado el {{date}}',
      redeemedOn: 'Canjeado el {{date}}',
      operator: 'Operador: {{name}}',
      empty: 'Aún no se han emitido cupones.',
      loadError: 'No se pudieron cargar los cupones de clientes.',
      loading: 'Cargando cupones...',
    },
    status: 'Estado',
    active: 'Activa',
    expired: 'Caducada',
    url: 'URL',
    qrCode: 'Código QR',
    preview: 'Vista previa',
    copyUrl: 'Copiar URL',
    duplicate: 'Duplicar',
    stop: 'Detener',
    restart: 'Reiniciar',
    editPromotion: 'Editar promoción',
    delete: 'Eliminar promoción',
    addPromotion: 'Añadir promoción',
    creating: 'Creando...',
    createTitle: 'Crear Promoción',
    createDesc: 'Añade una nueva campaña de mensajes para tu sucursal.',
    createWizard: {
      detailsDesc:
        'Define el nombre, los cupones disponibles y el descuento. Podrás personalizar el formulario del cliente después de crearla.',
      messageDesc:
        'Elige uno de los tres mensajes configurados en Ajustes → Mensajes.',
      reviewHint: 'Revisa la configuración antes de crear la promoción.',
      stepCount: '{{current}} / {{total}}',
      back: 'Atrás',
      continue: 'Continuar',
      createAndCustomize: 'Crear y personalizar',
      couponHint: 'Usa 0 si la promoción no tiene un descuento porcentual.',
      automaticUpdatesLabel: 'Actualizaciones automáticas',
      automaticUpdates:
        'Un mensaje dedicado para cada acción, configurable en Mensajes.',
    },
    close: 'Cerrar',
    save: 'Guardar',
    saveChanges: 'Guardar cambios',
    buttonLabel: 'Texto del botón',
    buttonLabelPlaceholder: 'Introduce el texto del botón',
    redirectPage: 'Página de redirección',
    redirectPagePlaceholder: 'URL personalizada de página de agradecimiento',
    percentageDiscount: 'Descuento (%)',
    maxCouponCount: 'Cupones máximos',
    maxUsageCount: 'Uso máximo',
    itemsPerPage: 'Elementos por página',
    range: '{{start}} - {{end}} de {{total}}',
    noBranch: 'Seleccione una sucursal para ver promociones',
    empty: 'No hay promociones disponibles',
    emptyDesc:
      'Aún no has creado promociones para este lugar. Empieza creando tu primera promo.',
    deleteModal: {
      title: '¿Eliminar promoción?',
      description:
        '¿Estás seguro de que deseas eliminar esta promoción? Esta acción no se puede deshacer.',
    },
    validation: {
      promotionNameMin: 'El nombre debe tener al menos 2 caracteres',
      promotionNameMax: 'El nombre no puede superar 120 caracteres',
      couponMin: 'Introduce un número entero mayor que cero.',
      discountRange: 'Introduce un número entero de 0 a 100.',
      redirectPageInvalid:
        'Introduce una URL válida (p. ej. https://example.com).',
    },
    toast: {
      createSuccess: 'Promoción creada correctamente.',
      createError: 'No se pudo crear la promoción. Inténtalo de nuevo.',
      updateSuccess: 'Promoción actualizada correctamente.',
      updateError: 'No se pudo actualizar la promoción. Inténtalo de nuevo.',
      deleteSuccess: 'Promoción eliminada correctamente.',
      deleteError: 'No se pudo eliminar la promoción. Inténtalo de nuevo.',
      duplicateSuccess: 'Promoción duplicada correctamente.',
      duplicateError: 'No se pudo duplicar la promoción. Inténtalo de nuevo.',
      stopSuccess: 'Promoción detenida correctamente.',
      stopError: 'No se pudo detener la promoción. Inténtalo de nuevo.',
      restartSuccess: 'Promoción reiniciada correctamente.',
      restartError: 'No se pudo reiniciar la promoción. Inténtalo de nuevo.',
      logoUploadSuccess: 'Logo subido correctamente.',
      logoUploadError: 'No se pudo subir el logo.',
      logoRemoveSuccess: 'Logo eliminado correctamente.',
      logoRemoveError: 'No se pudo eliminar el logo.',
      posterUploadSuccess: 'Póster subido correctamente.',
      posterUploadError: 'No se pudo subir el póster.',
      posterRemoveSuccess: 'Póster eliminado correctamente.',
      posterRemoveError: 'No se pudo eliminar el póster.',
    },
    edit: {
      pageTitle: 'Editar promoción',
      subtitle: 'Configura el formulario y los ajustes de la promoción.',
      tabListLabel: 'Secciones de edición de promoción',
      wizard: {
        stepListLabel: 'Pasos de configuración de la promoción',
        form: 'Formulario del cliente',
        formDesc:
          'Personaliza el formulario que el cliente completa para solicitar el cupón.',
        rules: 'Reglas',
        rulesDesc:
          'Define el periodo, la disponibilidad, el descuento y los datos que pides al cliente.',
        message: 'Mensaje',
        messageDesc:
          'Elige uno de los tres mensajes configurados en Ajustes → Mensajes.',
        review: 'Resumen',
        reviewDesc:
          'Revisa la configuración y guarda. Los cambios no guardados se quedan en esta página.',
        editStep: 'Editar',
        period: 'Periodo',
        requestedFields: 'Datos solicitados',
        noDate: 'Sin fecha',
        none: 'Ninguno',
      },
      uploadImage: 'Subir imagen',
      removeImage: 'Eliminar imagen',
      uploading: 'Subiendo...',
      removing: 'Eliminando...',
      tabs: {
        formConfig: 'Configuración del formulario',
        promoConfig: 'Configuración de la promoción',
      },
      formConfig: {
        media: 'Medios',
        logo: 'Logo de la promo',
        details: 'Detalles del formulario',
        promoText: 'Texto de la promo',
        promoLink: 'Enlace de la promo',
      },
      promoConfig: {
        poster: 'Póster',
        posterLabel: 'Póster de la promo',
        options: 'Opciones',
        useOnlyOnce: 'Cupón utilizable solo una vez',
        useOnlyOnceDesc: 'Limita la descarga del cupón a una vez por cliente',
        showEmail: 'Solicitar email',
        showEmailDesc: 'Mostrar el campo email en el formulario promo',
        showAddress: 'Solicitar dirección',
        showAddressDesc: 'Mostrar el campo dirección en el formulario promo',
        showCap: 'Solicitar código postal',
        showCapDesc: 'Mostrar el campo código postal en el formulario promo',
        showDob: 'Solicitar fecha de nacimiento',
        showDobDesc:
          'Mostrar el campo fecha de nacimiento en el formulario promo',
        showCountry: 'Solicitar país',
        limits: 'Fechas y límites',
        startDate: 'Fecha de inicio',
        endDate: 'Fecha de fin',
        tags: 'Etiquetas',
        tagsOnDownload: 'Etiquetas aplicadas al descargar',
        tagsOnUse: 'Etiquetas aplicadas al usar',
        tagsPlaceholder: 'Seleccionar etiquetas',
        messaging: 'Mensajería',
        messageLabel: 'Mensaje para el cliente',
        messagePlaceholder:
          'Elige una de las tres configuraciones compartidas.',
        messageStatus: '{{status}} · WhatsApp: {{whatsapp}} · Email: {{email}}',
        messageActive: 'Configuración activa',
        messageInactive: 'Configuración desactivada',
        channelOn: 'activo',
        channelOff: 'desactivado',
        messageWarning:
          'Activa al menos un canal en Mensajes para enviar esta comunicación.',
        configureMessages: 'Configura textos, canales y plantillas en Mensajes',
        messageLoadError:
          'No se pudieron cargar las configuraciones. Inténtalo de nuevo desde Mensajes.',
        sharedMessageNote:
          'Cada acción usa el mensaje dedicado configurado en Mensajes. PROMO_INFO está reservado a las actualizaciones de estado.',
        whatsappMessage: 'Mensaje WhatsApp del cupón',
        whatsappSubtitle: 'Mensaje enviado con el código QR de la promo',
        whatsappPlaceholder: 'Escribe tu mensaje aquí...',
        applyTemplate: 'Aplicar plantilla predeterminada',
        sendMessage: 'Enviar mensaje',
      },
      detailsTitle: 'Detalles de la promoción',
      detailsDesc: 'Actualiza la información y los límites de la campaña.',
      branchHint: 'Sucursal: {{branch}}',
    },
  },
  supplierOrdersPage: {
    receivedModified: 'Recibido con cambios',
    processedModified: 'Procesado pero modificado',
    processed: 'Procesado',
    title: 'Pedidos a proveedores',
    subtitle: 'Compras, entregas y comprobación de productos',
    history: 'Historial de pedidos',
    create: 'Crear pedido',
    search: 'Buscar pedido o producto…',
    supplier: 'Proveedor',
    allSuppliers: 'Todos los proveedores',
    invalidDates: 'Selecciona un intervalo de fechas válido.',
    onlyModified: 'Solo pedidos modificados',
    loadError: 'No se pudieron cargar los pedidos.',
    ordersOn: 'Pedidos del {{date}} · {{count}}',
    empty: 'No se encontraron pedidos',
    order: 'Pedido',
    delivery: 'Entrega',
    status: 'Estado',
    totalVat: 'Total con IVA',
    perPage: 'Por página',
    orderCount: '{{count}} pedidos',
    modified: 'Modificado',
    recipient: 'Destinatario',
    chooseRecipient: 'Elige un proveedor o una sede',
    branchSuffix: 'Sede',
    deliveryDate: 'Fecha de entrega',
    futureDate: 'Elige hoy o una fecha futura.',
    pastDate: 'La fecha de entrega debe ser hoy o una fecha futura.',
    receiveFrom: 'Recepción desde',
    receiveTo: 'Hasta',
    catalogSearch: 'Buscar en el catálogo…',
    catalogError: 'No se pudo cargar el catálogo.',
    noProducts: 'No hay productos disponibles',
    product: 'Producto',
    vatLine: '{{unit}} · IVA {{vat}}%',
    net: 'Neto',
    quantity: 'Cantidad {{name}}',
    removeNamed: 'Quitar {{name}}',
    remove: 'Quitar',
    add: 'Añadir',
    productCount: '{{count}} productos',
    totalLine: 'Total con IVA: {{amount}}',
    createHint:
      'Elige un destinatario, una fecha y al menos un producto con cantidad positiva. La hora final debe ser posterior a la inicial.',
    createError: 'No se pudo crear el pedido.',
    calendarTitle: 'Calendario de pedidos',
    otherDate: 'Otra fecha',
    loadPeriod: 'Cargar otro periodo',
    today: 'Hoy',
    previousDays: 'Desplazar a días anteriores',
    nextDays: 'Desplazar a días siguientes',
    window: '(±20 días)',
    incoming: 'En camino',
    toReview: 'Por revisar',
    calendarTotals: 'Totales · todos los proveedores',
    calendarError: 'Los recuentos del calendario no están disponibles.',
    calendarDays: 'Días del calendario de pedidos',
    incomingSummary: '{{count}} en camino',
    reviewSummary: '{{count}} por revisar',
    processedSummary: '{{count}} procesados',
    ordersSummary: '{{count}} pedidos, detalle de estados no disponible',
    countsUnavailable: 'recuentos no disponibles',
    calendarHint:
      'Cada día incluye pedidos programados, en camino y procesados. La búsqueda y el proveedor filtran la lista inferior.',
    exportJson: 'Exportar JSON',
    importJson: 'Importar JSON',
    exported: 'Archivo exportado.',
    importedCount: '{{count}} elementos importados.',
    exportError: 'La exportación ha fallado. Inténtalo de nuevo.',
    invalidJson: 'JSON no válido.',
    importProducts:
      'Introduce un array de productos con nombre, precio neto, IVA y unidad válidos.',
    importSuppliers:
      'Introduce un array de proveedores con un nombre en cada fila.',
    importError:
      'La importación ha fallado. Revisa los datos e inténtalo de nuevo.',
    importTitle: 'Importar {{label}}',
    importAction: 'Importar',
    jsonFile: 'Archivo JSON',
    fileTooLarge: 'El archivo supera 5 MB.',
    fileReadError: 'No se pudo leer el archivo.',
    importOrdersHint:
      'La importación crea pedidos nuevos para la sede seleccionada. Importar de nuevo crea más pedidos.',
    jsonArray: 'Array JSON',
    ordersLabel: 'pedidos',
    productsLabel: 'productos',
    suppliersLabel: 'proveedores',
    statuses: {
      BOZZA: 'Borrador',
      PROGRAMMATO: 'Programado',
      IN_PREPARAZIONE: 'En preparación',
      IN_CONSEGNA: 'En entrega',
      DA_VERIFICARE: 'Por revisar',
      RICEVUTO: 'Recibido',
      NON_CONSEGNATO: 'No entregado',
      ANNULLATO: 'Anulado',
      INVIATO: 'Enviado',
      DA_CONFERMARE: 'Por confirmar',
      CONSEGNATO: 'Entregado',
    },
  },
  supplierOrderDetail: {
    matchProduct: 'Match to product',
    skipLine: 'Skip this line',
    duplicateMatch: 'Match each line to a different product.',
    continue: 'Continue',
    category: 'Category',
    close: 'Close',
    allCategories: 'All categories',
    personalWhatsApp: 'Personal WhatsApp',
    localWhatsApp: 'Restaurant WhatsApp',
    confirmSend: 'Send the order to the supplier using this channel?',
    confirmEdit: 'Save the updated order quantities?',
    checkQuantities: 'Check quantities',
    alreadyDelivered: 'Goods have arrived: proceed to verification',
    createReview: 'Order review',
    discardDraft: 'Discard the selected products?',
    savedShare:
      'Order saved. Would you like to choose how to send it to the supplier?',
    title: 'Pedido al proveedor',
    orderTitle: 'Pedido n.º {{id}}',
    back: 'Pedidos',
    unavailable: 'Este pedido no está disponible.',
    requestedDelivery: 'Entrega solicitada',
    changeDate: 'Cambiar fecha',
    orderTotal: 'Total del pedido con IVA',
    netLine: 'Base {{net}} · IVA {{vat}}',
    products: 'Productos pedidos',
    addReceived: 'Añadir producto recibido',
    verifyAll: 'Verificar todos',
    ordered: 'Pedida',
    sent: 'Enviada',
    unitNet: 'Neto unitario',
    received: 'Recibida',
    receivedPrice: 'Precio neto recibido',
    verified: 'Verificado',
    verifyNamed: 'Verificar {{name}}',
    receivedNamed: 'Cantidad recibida {{name}}',
    receivedPriceNamed: 'Precio recibido {{name}}',
    editOrder: 'Actualizar pedido',
    prepare: 'Preparar pedido',
    dispatch: 'Confirmar envío',
    arrived: 'Entrega llegada',
    cancelOrder: 'Anular pedido',
    notDelivered: 'No entregado',
    receiptTitle: 'Comprobar recepción',
    invoiceTotal: 'Total de factura con IVA',
    receivedProductsTotal: 'Total calculado de productos (IVA incluido)',
    invoiceTotalHint: 'Importe registrado al confirmar la recepción.',
    invoiceDraftHint:
      'Importe introducido para la recepción; se guardará al confirmar.',
    insertedAmount: 'Total introducido',
    expectedAmount: 'Total previsto del pedido',
    recordedAmount: 'Total registrado',
    receivedProductsTitle: 'Resumen de productos recibidos',
    lineTotal: 'Total de línea con IVA',
    receivedLineCalculation: '{{quantity}} × {{price}} + IVA {{vat}}%',
    invoiceGoodsDifference: 'Diferencia factura − productos: {{amount}}',
    pendingVerification: 'Por verificar',
    verifiedProgress: '{{checked}} de {{total}} filas verificadas',
    receiptBlockedTitle: 'Para habilitar “Confirmar recepción”:',
    receiptNeedsProducts: 'Añade al menos un producto recibido.',
    receiptNeedsChecks:
      'Marca “Verificado” en las filas pendientes: {{count}}.',
    receiptInvalidLines:
      'Corrige cantidades o precios recibidos no válidos (cero o más). Filas: {{count}}.',
    receiptInvalidInvoice:
      'Introduce un total de factura válido, igual o superior a cero.',
    receiptSaving: 'Espera a que se guarde el pedido.',
    receiptUploading: 'Espera a que se suba la foto de la factura.',
    receiptAnalyzing: 'Espera a que termine el análisis de la factura.',
    goToVerification: 'Ir a la siguiente fila por verificar',
    receiptReady:
      'Todas las filas están verificadas. Puedes confirmar la recepción.',
    receiptTotalDifference:
      'El total de factura {{invoice}} difiere del total de productos recibidos {{products}}. Revisa los importes; esta diferencia no bloquea la confirmación.',
    movedToHistory: 'Mensaje trasladado al historial de pedidos',
    receiptHint:
      'Revisa la cantidad recibida y el precio neto, y marca “Verificado” en cada fila. Cambiar la cantidad o el precio elimina la verificación de esa fila.',
    confirmReceipt: 'Confirmar recepción',
    invoice: 'Factura',
    invoiceAlt: 'Foto de la factura',
    invoicePhoto: 'Foto de la factura',
    choosePhoto: 'Elegir archivo',
    ocrLabel: 'Notas opcionales',
    ocrPlaceholder:
      'Notas opcionales para el análisis con IA. Déjalo vacío para analizar solo la foto.',
    ocrRunning: 'Analizando factura con IA…',
    ocrFailed: 'Error al analizar. Prueba otra foto o añade notas.',
    clearPhotoAndText: 'Borrar foto y texto',
    readingPhotosTitle: 'Cargando foto…',
    ocrProcessingTitle: 'Analizando factura con IA…',
    ocrProcessingHint:
      'Leyendo la foto en el servidor. Puede tardar unos segundos.',
    aiAnalyzingTitle: 'Analizando factura con IA…',
    aiAnalyzingHint:
      'Leyendo la foto en el servidor. Puede tardar unos segundos.',
    photoUploadingTitle: 'Guardando foto de factura…',
    photoUploadingHint: 'Subiendo la imagen antes del análisis.',
    analyze: 'Analizar factura',
    reviewExtracted: 'Revisa los valores extraídos antes de aplicarlos.',
    applyMatches: 'Aplicar a los productos coincidentes',
    photoType: 'Sube una imagen JPG o PNG de hasta 5 MB.',
    imageInvalid: 'Imagen no válida.',
    imageReadError: 'No se pudo leer la imagen.',
    photoSaved: 'Foto de la factura guardada.',
    photoSaveError: 'No se pudo guardar la foto de la factura.',
    extractError: 'El análisis de la factura ha fallado.',
    communications: 'Mensajes al proveedor',
    configureMessages: 'Configurar mensajes',
    communicationsError: 'No se pudieron cargar los mensajes.',
    openSupplier: 'Abrir página del proveedor',
    recipientEmail: 'Correo del destinatario',
    sendEmail: 'Enviar correo',
    updated: 'Pedido actualizado.',
    operationError: 'La operación ha fallado.',
    sendFailed: 'El envío ha fallado.',
    messageSent: 'Mensaje enviado.',
    requestLogged: 'Solicitud registrada. Revisa el resultado en los mensajes.',
    communicationError: 'El mensaje ha fallado.',
    saveDate: 'Guardar fecha',
    invalidDate: 'Fecha no válida.',
    confirmTitle: 'Confirmar acción',
    confirmAction: 'Confirmar',
    confirmBody: '¿Actualizar este pedido?',
    confirmReceive:
      'La recepción se guardará con las cantidades y los precios verificados.',
    unexpectedProduct: 'Producto recibido no previsto',
    catalogUnavailable: 'El catálogo no está disponible.',
  },
  supplierOrderHistory: {
    title: 'Historial de pedidos',
    subtitle: 'Busca compras por proveedor, producto y fecha de entrega',
    back: 'Pedidos a proveedores',
    dateRange: 'Fechas de entrega',
    load: 'Cargar historial',
    loading: 'Cargando…',
    reset: 'Restablecer filtros',
    orders: 'Pedidos',
    ordered: 'Pedido',
    received: 'Recibido',
    vatProducts: 'Con IVA · productos filtrados',
    allResults: 'Todos los resultados filtrados',
    search: 'Buscar por nombre de producto…',
    hint: 'Todos los estados, incluidos los archivados · Importes con IVA',
    summaryError: 'No se pudo cargar el resumen.',
    dirty:
      'Filtros modificados: pulsa “Cargar historial” para actualizar los resultados y las exportaciones.',
    loadError: 'No se pudo cargar el historial.',
    results: 'Resultados de la búsqueda',
    resultsMeta:
      '{{count}} pedidos · La exportación incluye todos los resultados filtrados.',
    excel: 'Excel',
    pdf: 'PDF',
    exporting: 'Exportando…',
    empty: 'No se encontraron pedidos con estos filtros',
    deliveryDate: 'Fecha de entrega',
    products: 'Productos',
    filteredTotal: 'Productos filtrados · con IVA',
    exported: 'Exportados {{orders}} pedidos y {{rows}} líneas de producto.',
    exportError: 'La exportación ha fallado.',
    allSuppliers: 'Todos los proveedores',
    exportSheetProducts: 'Productos',
    exportSheetSearch: 'Búsqueda',
    exportTitle: 'Historial de pedidos a proveedores',
    exportOrder: 'Pedido',
    exportDelivery: 'Fecha de entrega',
    exportSupplier: 'Proveedor',
    exportSupplierCode: 'Código de proveedor',
    exportStatus: 'Estado',
    exportProduct: 'Producto',
    exportUnit: 'Unidad',
    exportOrderedQty: 'Cantidad pedida',
    exportSentQty: 'Cantidad enviada',
    exportReceivedQty: 'Cantidad recibida',
    exportOrderedNet: 'Precio neto pedido',
    exportReceivedNet: 'Precio neto recibido',
    exportVat: 'IVA %',
    exportOrderedTotal: 'Total pedido con IVA',
    exportReceivedTotal: 'Total recibido con IVA',
    exportQtyChange: 'Cambio de cantidad',
    exportPriceChange: 'Cambio de precio',
    exportYes: 'Sí',
    exportNo: 'No',
    exportBranch: 'Sede',
    exportDeliveryFrom: 'Entrega desde',
    exportDeliveryTo: 'Entrega hasta',
    exportProductName: 'Nombre del producto',
    exportAllProducts: 'Todos',
    exportStatuses: 'Estados',
    exportAllStatuses: 'Todos, incluidos los archivados',
    exportOrderCount: 'Total de pedidos',
    exportOrderedAmount: 'Importe pedido con IVA',
    exportReceivedAmount: 'Importe recibido con IVA',
    exportProductRows: 'Líneas de producto',
    exportPage:
      'Página {{page}} de {{pages}} · {{orders}} pedidos · {{rows}} líneas de producto',
  },
  suppliersPage: {
    title: 'Proveedores',
    subtitle: 'Contactos y catálogos para los pedidos de la sede',
    create: 'Nuevo proveedor',
    active: 'Activos',
    archived: 'Archivados',
    search: 'Buscar proveedor, correo o NIF…',
    noBranch: 'Selecciona una sede.',
    loadError: 'No se pudieron cargar los proveedores.',
    supplier: 'Proveedor',
    contacts: 'Contactos',
    catalog: 'Catálogo',
    openCatalog: 'Abrir catálogo',
    productCount: '{{count}} productos',
    vatLine: 'NIF {{vat}}',
    editNamed: 'Editar {{name}}',
    archiveNamed: 'Archivar {{name}}',
    restoreNamed: 'Restaurar {{name}}',
    empty: 'No se encontraron proveedores.',
    archiveTitle: 'Archivar proveedor',
    restoreTitle: 'Restaurar proveedor',
    archiveAction: 'Archivar',
    restoreAction: 'Restaurar',
    archiveDesc:
      '¿Archivar {{name}}? Se conservan el catálogo y el historial de pedidos. El proveedor no se podrá elegir para nuevos pedidos hasta restaurarlo.',
    restoreDesc: '¿Restaurar {{name}} entre los proveedores activos?',
    saveError: 'No se pudo guardar el proveedor. Inténtalo de nuevo.',
    archiveError:
      'No se pudo actualizar el archivo del proveedor. Inténtalo de nuevo.',
    editTitle: 'Editar proveedor',
    groupContacts: 'Datos y contactos',
    groupFiscal: 'Datos fiscales',
    groupAddress: 'Dirección',
    legalName: 'Razón social',
    email: 'Correo',
    phone: 'Teléfono',
    pec: 'Correo certificado',
    vat: 'NIF',
    taxCode: 'Código fiscal',
    street: 'Dirección',
    city: 'Ciudad',
    postcode: 'Código postal',
    country: 'País',
    fromInvoice: 'Desde factura',
    invoiceTitle: 'Proveedor desde factura',
    createSupplier: 'Crear proveedor',
    invoicePhoto: 'Foto de la factura',
    photosSelected: '{{count}} fotos seleccionadas',
    removePhotos: 'Quitar fotos',
    readingPhotos: 'Leyendo fotos…',
    tooManyPhotos: 'Selecciona hasta 5 imágenes JPG o PNG, de 5 MB cada una.',
    invalidImage: 'Imagen no válida.',
    imageReadError: 'No se pudo leer la foto.',
    photosReadError: 'No se pudieron leer las fotos. Inténtalo de nuevo.',
    analyze: 'Analizar',
    existing: 'Proveedores ya registrados',
    analyzeError: 'No se pudo analizar la factura.',
    createError: 'No se pudo crear el proveedor.',
  },
  menu: {
    ingredientsPage: {
      title: 'Ingredientes',
      subtitle: 'Ingredientes opcionales, categorías y suplementos',
      search: 'Buscar ingrediente…',
      createIngredient: 'Crear ingrediente',
      createCategory: 'Crear categoría',
      ingredients: 'Ingredientes',
      categories: 'Categorías',
      loadError: 'No se pudo cargar el catálogo.',
      operationError: 'La operación ha fallado. Inténtalo de nuevo.',
      filterCategory: 'Filtrar categoría',
      allCategories: 'Todas las categorías',
      pageHint:
        'Las categorías agrupan los ingredientes de esta página. Una categoría puede continuar en la página siguiente.',
      available: 'Disponible',
      unavailable: 'No disponible',
      editNamed: 'Editar {{name}}',
      deleteNamed: 'Eliminar {{name}}',
      emptyCategory: 'No hay ingredientes en esta categoría.',
      emptyPage:
        'No hay ingredientes en esta página. Cambia la búsqueda o vuelve a la página anterior.',
      perPage: 'Ingredientes por página',
      createCategoryFirst: 'Crea una categoría para empezar.',
      categoryOrder:
        'Orden del catálogo. Cada receta puede personalizar el orden de sus categorías.',
      moveUp: 'Subir {{name}}',
      moveDown: 'Bajar {{name}}',
      newIngredient: 'Nuevo ingrediente',
      editIngredient: 'Editar ingrediente',
      newCategory: 'Nueva categoría',
      editCategory: 'Editar categoría',
      ingredientDesc: 'Nombre, categoría y suplemento.',
      categoryDesc: 'Nombre y traducciones de la categoría.',
      name: 'Nombre *',
      category: 'Categoría *',
      select: 'Seleccionar',
      supplement: 'Suplemento € *',
      description: 'Descripción',
      translations: 'Traducciones',
      english: 'English',
      spanish: 'Español',
      french: 'Français',
      deleteTitle: 'Eliminar del catálogo',
      deleteDesc:
        '¿Eliminar “{{name}}”? Los ingredientes vinculados a platos y las categorías que no están vacías están protegidos.',
      delete: 'Eliminar',
    },
    recipeEditor: {
      createTitle: 'Crear receta',
      tabsLabel: 'Receta y traducciones',
      configurationTab: 'Receta',
      basePrice: 'Precio base',
      dishName: 'Nombre del plato',
      typeTitle: 'Tipo de plato',
      simpleTitle: 'Receta sencilla',
      simpleDesc: 'Nombre, descripción y alérgenos',
      customizableTitle: 'Receta personalizable',
      customizableDesc: 'Base + ingredientes a elegir',
      compositeTitle: 'Menú combinado',
      compositeDesc: 'Platos sencillos a precio fijo',
      wineTitle: 'Vino / Champán',
      wineDesc: 'Botella, copa y características',
      includedDishes: 'Platos incluidos',
      searchDishes: 'Buscar plato…',
      includedSections: 'Sección de los platos incluidos',
      allSections: 'Todas las secciones',
      selectionHint:
        '{{count}} platos seleccionados. Las selecciones se conservan al cambiar de página o buscar.',
      cannotInclude: '{{reason}}: no se puede incluir',
      weightedDish: 'Precio por peso',
      noDishes: 'No se encontraron platos.',
      dishesPerPage: 'Platos por página',
      range: '{{from}}–{{to}} de {{total}}',
      previous: 'Anterior',
      next: 'Siguiente',
      minTwo: 'Selecciona al menos dos platos sencillos.',
      selectableIngredients: 'Ingredientes seleccionables',
      minimumTotal: 'Mínimo total',
      maximumTotal: 'Máximo total',
      searchIngredients: 'Buscar ingrediente…',
      ingredientLoadError: 'No se pudieron cargar los ingredientes. Reintentar',
      disabled: 'Deshabilitado',
      ingredientPage: '{{total}} ingredientes · página {{page}}',
      categoryFilter: 'Categoría',
      allCategories: 'Todas las categorías',
      noIngredients: 'No se encontraron ingredientes.',
      categoryOrderHelp:
        'Usa las flechas para ordenar las categorías que verá el cliente al personalizar la receta. Solo aparecen categorías con ingredientes seleccionados. El orden no cambia ingredientes ni precios.',
      categoryOrder: 'Orden de las categorías en la receta',
      moveEarlier: 'Mover {{name}} antes',
      moveLater: 'Mover {{name}} después',
      availability: 'Disponibilidad y precios',
      restaurantMenu: 'Menú del restaurante',
      takeaway: 'Para llevar',
      delivery: 'Entrega a domicilio',
      enabled: 'Habilitado',
      channelPrice: 'Precio de {{channel}} (€)',
      invalidChoices:
        'Selecciona los ingredientes y comprueba el mínimo y máximo de opciones.',
      invalidPrices: 'Precios no válidos: utiliza como máximo dos decimales.',
      invalidComposition:
        'Las recetas personalizables y los menús combinados requieren un precio fijo y no pueden ser vinos.',
      invalidForm: 'Comprueba el nombre, el precio y los campos obligatorios.',
      numericError: 'Introduce un número válido.',
      nonNegativeError: 'El valor debe ser igual o mayor que cero.',
      wineRed: 'Tinto',
      wineWhite: 'Blanco',
      wineRose: 'Rosado',
      wineChampagne: 'Champán',
      wineSparkling: 'Espumoso',
      wineOther: 'Otro',
    },
    title: 'Menú',
    disabled: {
      badge: 'Sección no habilitada',
      title: 'Esta sección no está habilitada',
      description:
        'Esta función está actualmente deshabilitada para tu cuenta. Contacta con soporte para saber más.',
      infoTitle: '¿Necesitas acceso a esta sección?',
      infoDescription:
        'Contacta a nuestro equipo de soporte y te ayudaremos a comenzar.',
      footerHelp: '¿Aún necesitas ayuda?',
      footerHelpDesc:
        'Nuestro equipo de soporte está disponible 24/7 para asistirte.',
    },
    subtitleDefault: 'Consulta y gestiona tus menús digitales',
    statsCount: '{{count}} menú',
    searchPlaceholder: 'Buscar menús por nombre...',
    menuName: 'Nombre del menú',
    menuNamePlaceholder: 'Introduce el nombre del menú',
    description: 'Descripción',
    descriptionPlaceholder: 'Introduce la descripción del menú',
    newMenu: 'Nuevo menú',
    createTitle: 'Crear nuevo menú',
    createAction: 'Crear',
    url: 'URL',
    qrCode: 'Código QR',
    preview: 'Vista previa',
    copyUrl: 'Copiar URL',
    editMenu: 'Editar menú',
    deleteMenu: 'Eliminar menú',
    duplicate: 'Duplicar',
    close: 'Cerrar',
    saveChanges: 'Guardar',
    noBranch: 'Selecciona una sucursal para ver los menús',
    empty: 'No se encontraron menús',
    emptyDesc: 'Aún no hay menús para esta sucursal.',
    validation: {
      nameRequired: 'El nombre del menú es obligatorio',
      nameMax: 'El nombre del menú no puede superar los 100 caracteres',
      descriptionMax: 'La descripción no puede superar los 500 caracteres',
    },
    deleteModal: {
      title: '¿Eliminar menú?',
      description:
        '¿Seguro que quieres eliminar este menú? Esta acción no se puede deshacer.',
    },
    toast: {
      createSuccess: 'Menú creado correctamente.',
      createError: 'No se pudo crear el menú. Inténtalo de nuevo.',
      updateSuccess: 'Menú actualizado correctamente.',
      updateError: 'No se pudo actualizar el menú. Inténtalo de nuevo.',
      deleteSuccess: 'Menú eliminado correctamente.',
      deleteError: 'No se pudo eliminar el menú. Inténtalo de nuevo.',
      duplicateSuccess: 'Menú duplicado correctamente.',
      duplicateError: 'No se pudo duplicar el menú. Inténtalo de nuevo.',
      logoUploadSuccess: 'Logo subido correctamente.',
      logoUploadError: 'No se pudo subir el logo.',
      logoRemoveSuccess: 'Logo eliminado correctamente.',
      logoRemoveError: 'No se pudo eliminar el logo.',
      backgroundUploadSuccess: 'Fondo subido correctamente.',
      backgroundUploadError: 'No se pudo subir el fondo.',
      backgroundRemoveSuccess: 'Fondo eliminado correctamente.',
      backgroundRemoveError: 'No se pudo eliminar el fondo.',
      templateUpdateSuccess: 'Plantilla actualizada correctamente.',
      templateUpdateError: 'No se pudo actualizar la plantilla.',
    },
    edit: {
      pageTitle: 'Editar detalles del menú',
      subtitle: 'Configura el contenido, el estilo y las secciones del menú.',
      tabListLabel: 'Pestañas de edición del menú',
      withPhoto: 'con foto',
      withoutPhoto: 'sin foto',
      uploading: 'Subiendo...',
      removing: 'Eliminando...',
      tabs: {
        titleDescription: 'Título y descripción',
        menuStyle: 'Estilo del menú',
        sectionsRecipes: 'Secciones y recetas',
      },
      informationTitle: 'Información del menú',
      informationDesc: 'Configura el nombre del menú y las traducciones.',
      languages: {
        italian: 'Italiano',
        italianMain: 'Idioma principal',
        english: 'Inglés',
        englishTranslation: 'Traducción al inglés',
        spanish: 'Español',
        spanishTranslation: 'Traducción al español',
        french: 'Francés',
        frenchTranslation: 'Traducción al francés',
      },
      descriptions: {
        italian: 'Descripción del menú',
        english: 'Descripción en inglés',
        spanish: 'Descripción en español',
        french: 'Descripción en francés',
      },
      style: {
        appearanceTitle: 'Apariencia del menú',
        appearanceDesc: 'Sube el logo y la imagen de fondo de tu menú.',
        logo: 'Seleccionar logo del menú',
        logoHint: 'Recomendamos un logo cuadrado (PNG), máx. 5 MB.',
        background: 'Seleccionar imagen de fondo del menú',
        backgroundHint: 'Recomendamos una imagen horizontal, máx. 5 MB.',
        removeImage: 'Eliminar imagen',
        templatesTitle: 'Selecciona el estilo a aplicar al menú',
        templatesDesc: 'Elige una plantilla para la presentación de tu menú.',
        previewStyleMenu: 'Vista previa del estilo del menú',
        select: 'Seleccionar',
        selected: 'Seleccionado',
        active: 'Activo',
      },
    },
    sections: {
      title: 'Secciones y recetas',
      description: 'Organiza tu menú en secciones y añade recetas.',
      addSection: 'Crear nueva sección',
      searchPlaceholder:
        'Buscar secciones o recetas por nombre, descripción o código...',
      noSearchResults: 'No hay secciones ni recetas coincidentes',
      name: 'Nombre de la sección',
      namePlaceholder: 'Introduce el nombre de la sección',
      descriptionPlaceholder: 'Introduce la descripción de la sección',
      createTitle: 'Crear sección en el menú {{menu}}',
      createAction: 'Crear',
      editTitle: 'Editar objeto del menú',
      editDesc: 'Gestiona el contenido, las traducciones y los ingredientes',
      dishDrink: 'Plato/Bebida',
      missingTranslations: 'Faltan traducciones',
      missingTranslationsTooltip:
        'El nombre o la descripción de esta sección no se han traducido a todos los idiomas del menú; se mostrará la versión italiana si se cambia el idioma',
      addRecipe: 'Añadir receta/producto a {{section}}',
      sortAlphabetically: 'Ordenar recetas A-Z',
      moveLeft: 'Mover sección a la izquierda',
      moveRight: 'Mover sección a la derecha',
      settings: 'Ajustes de la sección',
      delete: 'Eliminar sección',
      menuPrice: 'Precio del menú',
      sectionVisibility: 'Sección visible',
      sectionVisibilityHint:
        'Mostrar esta sección en los menús del restaurante, entrega y recogida.',
      sectionHidden: 'Sección oculta',
      hideSectionPrices: 'Ocultar precios de la sección',
      showSectionPrices: 'Mostrar precios de la sección',
      visibleSection: 'Sección visible',
      visibility: 'Mostrar / ocultar sección',
      fixedMenuPrice: 'MENÚ FIJO ({{price}}€)',
      empty: 'Aún no hay secciones',
      emptyDesc: 'Usa el botón más para crear tu primera sección.',
      emptyRecipes: 'Aún no se han añadido recetas a esta sección.',
      recipeCount: '{{count}} recetas en esta sección',
      deleteModal: {
        title: '¿Eliminar sección?',
        description:
          '¿Seguro que quieres eliminar esta sección? Esta acción no se puede deshacer.',
      },
      validation: {
        nameRequired: 'El nombre de la sección es obligatorio',
        nameMax: 'El nombre de la sección no puede superar los 100 caracteres',
        descriptionMax: 'La descripción no puede superar los 500 caracteres',
      },
      toast: {
        createSuccess: 'Sección creada correctamente.',
        createError: 'No se pudo crear la sección. Inténtalo de nuevo.',
        updateSuccess: 'Sección actualizada correctamente.',
        updateError: 'No se pudo actualizar la sección. Inténtalo de nuevo.',
        deleteSuccess: 'Sección eliminada correctamente.',
        deleteError: 'No se pudo eliminar la sección. Inténtalo de nuevo.',
        sortSuccess: 'Recetas ordenadas alfabéticamente.',
        sortError: 'No se pudieron ordenar las recetas. Inténtalo de nuevo.',
      },
    },
    recipes: {
      editTitle: 'Editar objeto del menú',
      editDesc: 'Gestiona el contenido, las traducciones y los ingredientes',
      imageTitle: 'Imagen',
      productPhoto: 'Foto del producto',
      winePhoto: 'Foto del vino',
      wineChampagne: 'Vino/Champán',
      imageHintCreate:
        'Selecciona una imagen para subir. Puedes añadir el nombre de la receta antes de guardar.',
      generalTitle: 'Información general',
      generalDesc: 'Nombre, descripción y configuración',
      priceSection: 'Precio',
      priceHint: 'Introduce el precio final del producto',
      price: 'Precio €',
      bottlePrice: 'Precio de la botella',
      priceBottleLabel: 'Precio botella (€)',
      chalice: 'Copa',
      pricePerGlass: 'Precio por copa',
      alcoholPercentage: 'Alcohol (%) - Ej: 14',
      wineType: 'Tipo de vino',
      wineTypePlaceholder:
        'Tipo de vino (p. ej. Tinto, Blanco, Rosado, Champán...)',
      blend: 'Ensamblaje',
      blendPlaceholder:
        'Ensamblaje (p. ej. 50% Negroamaro, 20% Nero di Troia, 30% Malvasia Nera)',
      yearOfProduction: 'Año de producción',
      cellar: 'Bodega',
      countryRegion: 'País/Región',
      provenance: 'Procedencia',
      pricePerWeight:
        '¿El precio se indica por unidad de peso (p. ej. kg, litros) en lugar de por porción?',
      unitOfMeasurement: 'Unidad de medida',
      unitPlaceholder: 'Unidad de medida (ej: Kg, Lt, Pz)',
      assignAllergens: 'Asignar alérgenos',
      productConfiguration: 'Configuración del producto',
      allergensTitle: 'Alérgenos',
      bestSeller: 'Más vendido',
      bestSellerDesc:
        'Destaca este producto como uno de los más pedidos y hazlo visualmente más importante en el menú.',
      available: 'Disponible',
      availableDesc:
        'Muestra esta receta a los clientes en tu menú digital y hazla pedible.',
      notAvailable: 'No disponible',
      active: 'Activo',
      visible: 'Visible',
      delete: 'Eliminar receta/producto',
      dragHandle: 'Arrastra para reordenar la receta',
      deleteModal: {
        title: '¿Eliminar receta?',
        description:
          '¿Seguro que quieres eliminar esta receta? Esta acción no se puede deshacer.',
      },
      validation: {
        nameRequired: 'El nombre de la receta es obligatorio',
      },
      toast: {
        createSuccess: 'Receta creada correctamente.',
        createError: 'No se pudo crear la receta. Inténtalo de nuevo.',
        updateSuccess: 'Receta actualizada correctamente.',
        updateError: 'No se pudo actualizar la receta. Inténtalo de nuevo.',
        deleteSuccess: 'Receta eliminada correctamente.',
        deleteError: 'No se pudo eliminar la receta. Inténtalo de nuevo.',
        moveSuccess: 'Orden de la receta actualizado.',
        moveError:
          'No se pudo actualizar el orden de la receta. Inténtalo de nuevo.',
        saveBeforeImage: 'Guarda la receta antes de subir una imagen.',
        imageUploadSuccess: 'Imagen de la receta subida correctamente.',
        imageUploadError: 'No se pudo subir la imagen de la receta.',
        imageRemoveSuccess: 'Imagen de la receta eliminada correctamente.',
        imageRemoveError: 'No se pudo eliminar la imagen de la receta.',
      },
    },
    allergens: {
      cereals: 'Cereales con gluten',
      gluten: 'Gluten',
      crustaceans: 'Crustáceos',
      molluscs: 'Moluscos',
      fish: 'Pescado',
      eggs: 'Huevos',
      peanuts: 'Cacahuetes',
      soybean: 'Soja',
      mushrooms: 'Champiñones',
      nuts: 'Frutos de cáscara',
      celery: 'Apio',
      mustard: 'Mostaza',
      sesame: 'Sésamo',
      sulfur: 'Dióxido de azufre',
      lupins: 'Altramuces',
      lactose: 'Lactosa',
      corn: 'Maíz',
      pork: 'Cerdo',
      beef: 'Ternera',
      poultry: 'Aves',
      nickel: 'Níquel',
      legumes: 'Legumbres',
      citrus: 'Cítricos',
      cacao: 'Cacao',
      honey: 'Miel',
    },
    translate: {
      action: 'Traducir',
      progressTitle: 'Traducción en curso...',
      progressDesc: 'Espera a que se completen las traducciones.',
      translationsTitle: 'Traducciones',
      success: 'Traducciones aplicadas correctamente.',
      error: 'No se pudo traducir. Inténtalo de nuevo.',
    },
    previewPage: {
      pageTitle: 'Vista previa del menú',
      loading: 'Cargando menú',
      menuLabel: 'Menú',
      sectionLabel: 'Sección',
      priceLabel: 'Precio',
      noPhoto: 'No hay foto disponible',
      bestSeller: 'Best',
      allergensTitle: 'Alérgenos',
      poweredBy: 'Desarrollado por PRO20',
      scrollCue: 'Ir al menú',
      storyContinue: 'Menú',
      close: 'Cerrar',
      wine: {
        grape: 'Variedad de uva',
        cellar: 'Bodega',
        year: 'Año',
        region: 'Región',
        alcohol: 'Alcohol',
        glass: 'Copa',
        bottle: 'Botella',
      },
    },
  },
  landingPages: {
    title: 'Páginas de Destino',
    subtitle: 'Lista de Páginas de Destino para {{branch}}',
    subtitleDefault: 'Ver y administrar la lista de páginas de destino',
    searchPlaceholder: 'Buscar páginas de destino por nombre',
    searchPlaceholder2: 'Ingrese el nombre de la página de destino',
    landingPageName: 'Nombre de la Página de Destino',
    itemsPerPage: 'Elementos por página',
    range: '{{start}} - {{end}} de {{total}}',
    noBranch: 'Seleccione una sucursal para ver las páginas de destino',
    empty: 'No se encontraron páginas de destino',
    emptyDesc: 'Todavía no hay páginas de destino para esta sucursal.',
    addLandingPage: 'Agregar Página de Destino',
    newLanding: 'Nueva Página de Destino',
    newLandingDesc: 'Cree una nueva página de destino para su sucursal.',
    createLanding: 'Crear página de destino',
    statsCount: '{{count}} páginas de destino',
    present: 'presentes',
    url: 'URL',
    qrCode: 'Código QR',
    preview: 'Vista previa',
    showPreview: 'Mostrar vista previa',
    copyUrl: 'Copiar URL',
    editLanding: 'Editar página de destino',
    deleteLanding: 'Eliminar página de destino',
    duplicate: 'Duplicar',
    validation: {
      landingPageNameMin: 'El nombre debe tener al menos 2 caracteres',
      landingPageNameMax: 'El nombre no puede exceder los 50 caracteres',
    },
    deleteModal: {
      title: '¿Eliminar página de destino?',
      description:
        '¿Está seguro de que desea eliminar esta página de destino? Esta acción no se puede deshacer.',
    },
    toast: {
      createSuccess: 'Página de destino creada correctamente.',
      createError: 'No se pudo crear la página de destino. Inténtelo de nuevo.',
      deleteSuccess: 'Página de destino eliminada correctamente.',
      deleteError:
        'No se pudo eliminar la página de destino. Inténtelo de nuevo.',
      duplicateSuccess: 'Página de destino duplicada correctamente.',
      duplicateError:
        'No se pudo duplicar la página de destino. Inténtelo de nuevo.',
      updateSuccess: 'Página de destino actualizada correctamente.',
      updateError:
        'No se pudo actualizar la página de destino. Inténtelo de nuevo.',
      logoUploadSuccess: 'Logo subido correctamente.',
      logoUploadError: 'No se pudo subir el logo.',
      logoRemoveSuccess: 'Logo eliminado correctamente.',
      logoRemoveError: 'No se pudo eliminar el logo.',
      backgroundUploadSuccess: 'Fondo subido correctamente.',
      backgroundUploadError: 'No se pudo subir el fondo.',
      backgroundRemoveSuccess: 'Fondo eliminado correctamente.',
      backgroundRemoveError: 'No se pudo eliminar el fondo.',
      buttonSaveSuccess: 'Botón guardado correctamente.',
      buttonSaveError: 'No se pudo guardar el botón.',
      buttonDeleteSuccess: 'Botón eliminado correctamente.',
      buttonDeleteError: 'No se pudo eliminar el botón.',
    },
    edit: {
      pageTitle: 'Editar página de destino',
      subtitle: 'Configure medios y botones de acción.',
      formConfiguration: 'Configuración de la página de destino',
      formConfigurationDesc: 'Configure medios y botones de acción.',
      linkForm: 'Enlace de la página de destino',
      close: 'Cerrar',
      mediaTitle: 'Medios',
      mediaDescription:
        'Suba un logo y una imagen de fondo para su página de destino.',
      detailsTitle: 'Detalles',
      logo: 'Logo',
      background: 'Imagen de fondo',
      imageUpload: 'Haga clic o arrastre para subir una imagen',
      removeImage: 'Eliminar imagen',
      uploading: 'Subiendo...',
      removing: 'Eliminando...',
      buttons: {
        title: 'Botones de la página',
        description:
          'Agregue botones de acción en su página de destino. Use el icono ✓ para guardar cada fila.',
        text: 'Texto del botón',
        link: 'Enlace',
        color: 'Fondo',
        textColor: 'Color del texto',
        add: 'Agregar botón',
        save: 'Guardar botón',
        delete: 'Eliminar botón',
        empty: 'No hay botones. Haga clic en + para agregar uno.',
        textPlaceholder: 'ej. Reservar ahora',
        linkPlaceholder: 'https://',
        validationRequired: 'El texto y el enlace del botón son obligatorios.',
      },
    },
    public: {
      documentTitle: 'Página de aterrizaje',
      loading: 'Cargando página de aterrizaje...',
      unavailableTitle: 'Página no disponible',
      unavailableDesc:
        'No se pudo encontrar esta página de aterrizaje o ya no está disponible.',
      buttonFallback: 'Abrir',
    },
  },
  tags: {
    title: 'Etiquetas',
    titleWithBranch: 'Etiquetas {{branch}}',
    pageDescription: 'No hay etiquetas',
    subtitle: 'Lista de etiquetas para {{branch}}',
    subtitleDefault: 'Ver y gestionar la lista de etiquetas',
    searchPlaceholder: 'Buscar etiquetas por nombre',
    searchPlaceholder2: 'Introduzca el nombre de la etiqueta',
    tagName: 'Nombre de la etiqueta',
    itemsPerPage: 'Elementos por página',
    range: '{{start}} - {{end}} de {{total}}',
    noBranch: 'Seleccione una sucursal para ver las etiquetas',
    empty: 'No hay etiquetas',
    emptyDesc:
      'Crea la primera etiqueta para organizar mejor clientes, reservas y segmentaciones de marketing.',
    addTag: 'Agregar etiqueta',
    createTitle: 'Agregar etiqueta',
    createDesc: 'Cree una nueva etiqueta para su sucursal.',
    editTitle: 'Editar etiqueta',
    editDesc: 'Actualice el nombre de la etiqueta.',
    editTag: 'Editar etiqueta',
    duplicate: 'Duplicar',
    typeLabel: 'TAG',
    statsCount: '{{count}} etiquetas presentes',
    updatedAgo: 'Actualizado hace 2 minutos',
    creating: 'Creando...',
    saving: 'Guardando...',
    saveChanges: 'Guardar cambios',
    toast: {
      createSuccess: 'Etiqueta creada correctamente.',
      createError: 'No se pudo crear la etiqueta. Inténtelo de nuevo.',
      updateSuccess: 'Etiqueta actualizada correctamente.',
      updateError: 'No se pudo actualizar la etiqueta. Inténtelo de nuevo.',
      deleteSuccess: 'Etiqueta eliminada correctamente.',
      deleteError: 'No se pudo eliminar la etiqueta. Inténtelo de nuevo.',
      duplicateSuccess: 'Etiqueta duplicada correctamente.',
      duplicateError: 'No se pudo duplicar la etiqueta. Inténtelo de nuevo.',
    },
    validation: {
      tagNameMin: 'El nombre de la etiqueta debe tener al menos 2 caracteres',
      tagNameMax: 'El nombre de la etiqueta no puede superar los 50 caracteres',
    },
    deleteTag: 'Eliminar etiqueta',
    deleteModel: {
      deleteTitle: '¿Eliminar etiqueta?',
      deleteDescription:
        '¿Está seguro de que desea eliminar esta etiqueta? Esta acción no se puede deshacer.',
    },
  },
  publicRelations: {
    title: 'Relaciones Públicas',
    titleWithBranch: 'RP {{branch}}',
    pageDescription: 'No hay RP',
    subtitle: 'Lista de RP para {{branch}}',
    subtitleDefault: 'Ver y gestionar la lista de RP',
    searchPlaceholder: 'Buscar RP por nombre',
    searchPlaceholder2: 'Introduzca el nombre de RP',
    prName: 'Nombre de RP',
    itemsPerPage: 'Elementos por página',
    range: '{{start}} - {{end}} de {{total}}',
    noBranch: 'Seleccione una sucursal para ver las RP',
    empty: 'No hay RP',
    emptyDesc:
      'Crea la primera RP para organizar referidos, colaboraciones y gestión de clientes.',
    addPr: 'Agregar RP',
    createTitle: 'Agregar RP',
    createDesc:
      'Cree un nuevo contacto de relaciones públicas para su sucursal.',
    editTitle: 'Editar RP',
    editDesc: 'Actualice el nombre del contacto de relaciones públicas.',
    editPr: 'Editar RP',
    duplicate: 'Duplicar',
    typeLabel: 'PR MANAGER',
    statsCount: '{{count}} RP presentes',
    updatedAgo: 'Actualizado hace 2 minutos',
    creating: 'Creando...',
    saving: 'Guardando...',
    saveChanges: 'Guardar cambios',
    toast: {
      createSuccess: 'RP creada correctamente.',
      createError: 'No se pudo crear la RP. Inténtelo de nuevo.',
      updateSuccess: 'RP actualizada correctamente.',
      updateError: 'No se pudo actualizar la RP. Inténtelo de nuevo.',
      deleteSuccess: 'RP eliminada correctamente.',
      deleteError: 'No se pudo eliminar la RP. Inténtelo de nuevo.',
      duplicateSuccess: 'RP duplicada correctamente.',
      duplicateError: 'No se pudo duplicar la RP. Inténtelo de nuevo.',
    },
    validation: {
      prNameMin: 'El nombre de RP debe tener al menos 2 caracteres',
      prNameMax: 'El nombre de RP no puede superar los 50 caracteres',
    },
    deletePR: 'Eliminar PR',
    deleteModel: {
      deleteTitle: '¿Eliminar RP?',
      deleteDescription:
        '¿Está seguro de que desea eliminar esta RP? Esta acción no se puede deshacer.',
    },
  },
  staff: {
    collaborators: {
      title: 'Colaboradores',
      subtitle: 'Colaboradores para {{branch}}',
      subtitleDefault: 'Ver y administrar la lista de colaboradores',
      searchPlaceholder:
        'Buscar colaboradores por nombre, apellido, correo electrónico, teléfono o rol',
      firstName: 'Nombre',
      lastName: 'Apellido',
      firstNamePlaceholder: 'Introduce el nombre',
      lastNamePlaceholder: 'Introduce el apellido',
      email: 'Correo Electrónico',
      phone: 'Número de Teléfono',
      role: 'Rol',
      itemsPerPage: 'Elementos por página',
      viewDetails: 'Ver Detalles',
      sharedCredentials: 'Credenciales Compartidas',
      addCollaborator: 'Agregar Colaborador',
      editCollaborator: 'Editar Colaborador',
      deleteCollaborator: 'Eliminar Colaborador',
      changePassword: {
        action: 'Cambiar contraseña',
        title: 'Cambiar contraseña',
        subtitle: 'Establece una nueva contraseña para {{name}}',
        newPassword: 'Nueva contraseña',
        newPasswordPlaceholder: 'Introduce contraseña de 6 dígitos',
        confirmPassword: 'Confirmar contraseña',
        confirmPasswordPlaceholder: 'Confirma contraseña de 6 dígitos',
        submit: 'Guardar contraseña',
        validation: {
          passwordDigits: 'La contraseña debe tener exactamente 6 dígitos',
          passwordMismatch: 'Las contraseñas no coinciden',
        },
      },
      range: '{{start}} - {{end}} de {{total}}',
      noBranch: 'Seleccione una sucursal para ver colaboradores',
      empty: 'No se encontraron colaboradores',
      emptyDesc: 'Todavía no hay colaboradores para esta sucursal.',
      emailPlaceholder: 'Introduce el correo electrónico',
      phonePlaceholder: 'Introduce el número de teléfono',
      username: 'Nombre de usuario',
      usernamePlaceholder: 'Introduce el nombre de usuario',
      password: 'Contraseña',
      passwordPlaceholder: 'Introduce la contraseña',
      gender: 'Género',
      selectGender: 'Seleccionar género',
      male: 'Hombre',
      female: 'Mujer',
      selectRole: 'Seleccionar rol',
      remunerationType: 'Tipo de remuneración',
      selectRemunerationType: 'Seleccionar tipo de remuneración',
      hourly: 'Por hora',
      daily: 'Diaria',
      monthly: 'Mensual',
      sendCredentials: 'Enviar credenciales',
      roles: {
        receptionist: 'Recepcionista',
        assistantCook: 'Ayudante de cocina',
        administrator: 'Administrador',
        bartender: 'Barman',
        chef: 'Chef',
        dishwasher: 'Lavaplatos',
        pizzaChef: 'Pizzero',
        director: 'Director',
        waiter: 'Camarero',
        publicRelations: 'Relaciones públicas',
        deputyDirector: 'Subdirector',
      },
      validation: {
        firstNameMin: 'El nombre debe tener al menos 2 caracteres',
        lastNameMin: 'El apellido debe tener al menos 2 caracteres',
        firstNameMax: 'El nombre no puede superar los 40 caracteres',
        lastNameMax: 'El apellido no puede superar los 40 caracteres',
        emailInvalid: 'Introduce una dirección de correo válida',
        phoneInvalid: 'El teléfono debe contener entre 6 y 15 dígitos',
        countryCodeInvalid: 'Selecciona un prefijo internacional válido',
        usernameMin: 'El nombre de usuario debe tener al menos 4 caracteres',
        usernameMax: 'El nombre de usuario no puede superar los 30 caracteres',
        passwordMin: 'La contraseña debe tener al menos 8 caracteres',
        passwordDigits: 'La contraseña debe tener exactamente 6 dígitos',
        genderRequired: 'Selecciona un género',
        roleRequired: 'Selecciona un rol',
        remunerationRequired: 'Selecciona un tipo de remuneración',
      },
      deleteModel: {
        deleteTitle: '¿Eliminar colaborador?',
        deleteDescription:
          '¿Está seguro de que desea eliminar este colaborador? Esta acción no se puede deshacer.',
      },
      collaboratorDetails: {
        title: 'Detalles del Colaborador',
        subtitle:
          'Ver la información del colaborador y los detalles laborales.',
        personalDetails: 'Detalles Personales',
        employmentDetails: 'Detalles Laborales',
        jobDescription: 'Descripción del Puesto',
        dateOfBirth: 'Fecha de Nacimiento',
        branchCode: 'Código de Sucursal',
        contractType: 'Tipo de Contrato',
        startDateInduction: 'Fecha Inicio Inducción',
        endDateInduction: 'Fecha Fin Inducción',
        mobileDeviceAccessDetail: 'Detalle de Acceso al Dispositivo Móvil',
        uniqueId: 'ID Único',
        platform: 'Plataforma',
        model: 'Modelo',
        machine: 'Máquina',
        release: 'Release',
        fcmToken: 'Token FCM',
        registrationDate: 'Fecha de Registro',
        noMobileDevices: 'No hay dispositivos móviles registrados.',
        close: 'Cerrar',
        contractTypes: {
          determined: 'Determinado',
          indeterminate: 'Indeterminado',
        },
        visible: 'Visible',
        fired: 'Despedido',
        retribution: 'Retribución',
        yes: 'Sí',
        no: 'No',
      },
      toast: {
        createSuccess: 'Colaborador creado con éxito.',
        createError: 'No se pudo crear el colaborador. Inténtelo de nuevo.',
        updateSuccess: 'Colaborador actualizado con éxito.',
        updateError:
          'No se pudo actualizar el colaborador. Inténtelo de nuevo.',
        deleteSuccess: 'Colaborador eliminado con éxito.',
        deleteError: 'No se pudo eliminar el colaborador. Inténtelo de nuevo.',
        sendCredentialsSoon:
          'El envío de credenciales estará disponible pronto.',
        sendCredentialsSuccess: 'Credenciales enviadas correctamente.',
        sendCredentialsError:
          'No se pudieron enviar las credenciales. Inténtalo de nuevo.',
        sendCredentialsMissingPhone:
          'Este colaborador no tiene un número de teléfono válido.',
        sendCredentialsMissingCredentials:
          'Faltan el nombre de usuario o la contraseña de este colaborador.',
        changePasswordSuccess: 'Contraseña actualizada con éxito.',
        changePasswordError:
          'No se pudo actualizar la contraseña. Inténtelo de nuevo.',
      },
      credentialsMessage:
        'Tus credenciales de Proventi:\nUsername: {{username}}\nPassword: {{password}}',
    },
    collaboratorReports: {
      title: 'Informes de Colaboradores',
      titleWithMonth: 'Informe de {{branch}} de {{monthYear}}',
      subtitle: 'Informes de colaboradores para {{branch}}',
      subtitleDefault:
        'Ver y administrar la lista de informes de colaboradores',
      noReportsPresent: 'Ningún informe presente',
      searchPlaceholder: 'Buscar por nombre',
      name: 'Nombre',
      lunch: 'Almuerzo',
      cena: 'Cena',
      illness: 'Enfermedad',
      holiday: 'Vacaciones',
      free: 'Libre',
      hoursWorked: 'Horas Trabajadas',
      itemsPerPage: 'Elementos por página',
      range: '{{start}} - {{end}} de {{total}}',
      noBranch: 'Seleccione una sucursal para ver informes de colaboradores',
      empty: 'No se encontraron informes de colaboradores',
      emptyDesc: 'Todavía no hay informes de colaboradores para esta sucursal.',
      previousMonth: 'Mes anterior',
      nextMonth: 'Mes siguiente',
      downloadPdf: 'Descargar informe de {{monthYear}}',
      downloadPdfSuccess: 'PDF del informe de colaboradores descargado.',
      downloadPdfError:
        'No se pudo descargar el PDF del informe de colaboradores.',
      pdfTitle: 'Informes de Colaboradores',
      pdfMeta:
        '{{branch}} · {{monthYear}} · {{count}} colaboradores · Generado el {{date}}',
    },
  },
  extraServices: {
    title: 'Servicios y Productos Adicionales',
    subtitle:
      'Gestiona servicios, productos y amenidades disponibles para {{branchName}}',
    subtitleDefault: 'Ver y administrar la lista de servicios adicionales',
    statsCount: '{{count}} servicios adicionales',
    searchPlaceholder: 'Buscar un servicio o producto...',
    noBranch: 'Seleccione una sucursal para ver servicios adicionales',
    empty: 'No hay servicios adicionales',
    emptyDesc:
      'Aún no has configurado servicios o productos adicionales. Añade amenidades para mejorar la experiencia de tus clientes.',
    itemsPerPage: 'Elementos por página',
    range: '{{start}} - {{end}} de {{total}}',
    columns: {
      service: 'Servicio',
      price: 'Precio',
      guests: 'Huéspedes',
      state: 'Estado',
    },
    active: 'Activo',
    inactive: 'Inactivo',
    createTitle: 'Crear nuevo servicio',
    createDesc: 'Añade un nuevo servicio o producto para tu sucursal.',
    editTitle: 'Editar servicio',
    editDesc: 'Actualizar información del servicio',
    createService: 'Crear servicio',
    addService: 'Añadir servicio',
    creating: 'Creando...',
    saveChanges: 'Guardar cambios',
    close: 'Cerrar',
    name: 'Nombre',
    namePlaceholder: 'Introduce el nombre del servicio',
    description: 'Descripción',
    descriptionPlaceholder: 'Introduce la descripción',
    price: 'Precio (€)',
    pricePlaceholder: '0',
    guests: 'Número de huéspedes',
    guestsPlaceholder: '1',
    activeService: 'Servicio activo',
    serviceImage: 'Imagen del servicio',
    uploadImage: 'Subir imagen',
    removeImage: 'Eliminar imagen',
    uploading: 'Subiendo...',
    removing: 'Eliminando...',
    editService: 'Editar servicio',
    duplicate: 'Duplicar',
    deleteService: 'Eliminar servicio',
    deleteModal: {
      title: '¿Eliminar servicio?',
      description:
        '¿Estás seguro de que deseas eliminar este servicio? Esta acción no se puede deshacer.',
    },
    validation: {
      nameRequired: 'El nombre es obligatorio',
      nameMax: 'El nombre no puede superar 120 caracteres',
      descriptionMax: 'La descripción no puede superar 300 caracteres',
      numberInvalid: 'Introduce solo un número válido',
      priceMin: 'El precio no puede ser negativo',
      guestsMin: 'El número de huéspedes no puede ser negativo',
      guestsInteger: 'El número de huéspedes debe ser un número entero',
    },
    toast: {
      createSuccess: 'Servicio creado correctamente.',
      createError: 'No se pudo crear el servicio. Inténtalo de nuevo.',
      updateSuccess: 'Servicio actualizado correctamente.',
      updateError: 'No se pudo actualizar el servicio. Inténtalo de nuevo.',
      deleteSuccess: 'Servicio eliminado correctamente.',
      deleteError: 'No se pudo eliminar el servicio. Inténtalo de nuevo.',
      duplicateSuccess: 'Servicio duplicado correctamente.',
      duplicateError: 'No se pudo duplicar el servicio. Inténtalo de nuevo.',
      uploadSuccess: 'Imagen subida correctamente.',
      uploadError: 'No se pudo subir la imagen.',
      removeImageSuccess: 'Imagen eliminada correctamente.',
      removeImageError: 'No se pudo eliminar la imagen.',
    },
  },
  formMenu: {
    title: 'Form Menu',
    subtitleDefault: 'Ver y gestionar los Form Menus de tu sucursal',
    statsCount: '{{count}} Form Menus',
    searchPlaceholder: 'Buscar un Form Menu...',
    noBranch: 'Selecciona una sucursal para ver los Form Menus',
    empty: 'Sin Form Menu',
    emptyDesc:
      'Aún no has configurado ningún Form Menu. Añade uno para empezar.',
    columns: {
      item: 'Nombre del menú',
      status: 'Estado',
      price: 'Precio',
      totalBooking: 'Total de reservas',
      forms: 'Formularios',
      state: 'Estado',
    },
    formsCount: '{{count}} formularios',
    active: 'Activo',
    inactive: 'Inactivo',
    createTitle: 'Crear Form Menu',
    createDesc: 'Añade un nuevo Form Menu para tu sucursal.',
    editTitle: 'Editar Form Menu',
    editDesc: 'Actualiza la información del Form Menu',
    createItem: 'Crear',
    addItem: 'Añadir Form Menu',
    saveChanges: 'Guardar cambios',
    close: 'Cerrar',
    name: 'Nombre del menú',
    namePlaceholder: 'Introduce el nombre del Form Menu',
    description: 'Descripción',
    descriptionPlaceholder: 'Introduce la descripción',
    price: 'Precio (€)',
    pricePlaceholder: '0',
    forms: 'Formularios',
    formsPlaceholder: 'Selecciona formularios',
    activeItem: 'Form Menu activo',
    itemImage: 'Imagen del menú',
    uploadImage: 'Subir imagen',
    removeImage: 'Eliminar imagen',
    uploading: 'Subiendo...',
    weekdaysLabel: 'Días de la semana',
    selectAllWeekdays: 'Seleccionar todos',
    clearWeekdays: 'Limpiar',
    availabilityDates: {
      title: 'Fechas de disponibilidad del menú',
      dateRange: 'Rango de fechas',
      placeholder: 'Selecciona un rango',
      empty:
        'Aún no hay fechas de disponibilidad. Añade un rango para empezar.',
      addRange: 'Añadir rango',
      createTitle: 'Añadir rango de fechas',
      editTitle: 'Editar rango de fechas',
      create: 'Añadir',
      update: 'Actualizar',
      editRange: 'Editar rango',
      deleteRange: 'Eliminar rango',
      deleteTitle: '¿Eliminar rango de fechas?',
      deleteDescription:
        '¿Seguro que quieres eliminar este rango de disponibilidad?',
      dateRequired: 'Selecciona un rango de fechas.',
      dateOrder: 'La fecha de fin debe ser igual o posterior al inicio.',
      dateOverlap:
        'Este rango se solapa con fechas de disponibilidad existentes. Elige fechas libres.',
    },
    weekdays: {
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo',
    },
    editItem: 'Editar Form Menu',
    duplicate: 'Duplicar',
    editNotFound: 'Este Form Menu no se encontró o ya no está disponible.',
    deleteItem: 'Eliminar Form Menu',
    deleteModal: {
      title: '¿Eliminar Form Menu?',
      description:
        '¿Seguro que quieres eliminar este Form Menu? Esta acción no se puede deshacer.',
    },
    validation: {
      nameRequired: 'El nombre es obligatorio',
      nameMax: 'El nombre no puede superar 120 caracteres',
      descriptionMax: 'La descripción no puede superar 300 caracteres',
      numberInvalid: 'Introduce solo un número válido',
      priceMin: 'El precio no puede ser negativo',
    },
    toast: {
      createSuccess: 'Form Menu creado correctamente.',
      createError: 'No se pudo crear el Form Menu. Inténtalo de nuevo.',
      updateSuccess: 'Form Menu actualizado correctamente.',
      updateError: 'No se pudo actualizar el Form Menu. Inténtalo de nuevo.',
      deleteSuccess: 'Form Menu eliminado correctamente.',
      deleteError: 'No se pudo eliminar el Form Menu. Inténtalo de nuevo.',
      duplicateSuccess: 'Form Menu duplicado correctamente.',
      duplicateError: 'No se pudo duplicar el Form Menu. Inténtalo de nuevo.',
      showEnabled: 'Form Menu ahora visible.',
      showDisabled: 'Form Menu ahora oculto.',
      statusError:
        'No se pudo actualizar el estado del Form Menu. Inténtalo de nuevo.',
      uploadSuccess: 'Imagen subida correctamente.',
      uploadError: 'No se pudo subir la imagen.',
      removeImageSuccess: 'Imagen eliminada correctamente.',
      removeImageError: 'No se pudo eliminar la imagen.',
      dateRangeCreateSuccess: 'Rango de disponibilidad añadido.',
      dateRangeCreateError: 'No se pudo añadir el rango de disponibilidad.',
      dateRangeUpdateSuccess: 'Rango de disponibilidad actualizado.',
      dateRangeUpdateError: 'No se pudo actualizar el rango de disponibilidad.',
      dateRangeDeleteSuccess: 'Rango de disponibilidad eliminado.',
      dateRangeDeleteError: 'No se pudo eliminar el rango de disponibilidad.',
      dateRangeLoadError: 'No se pudo cargar el rango de disponibilidad.',
    },
  },
  events: {
    title: 'Eventos',
    subtitleDefault: 'Ver y gestionar los eventos de la sucursal',
    statsCount: '{{count}} eventos',
    searchPlaceholder: 'Buscar eventos...',
    filterDatePlaceholder: 'Filtrar por fecha',
    reset: 'Restablecer',
    noBranch: 'Selecciona una sucursal para ver los eventos',
    empty: 'No hay eventos',
    emptyDesc: 'Aún no has creado eventos. Añade un evento para empezar.',
    columns: {
      event: 'Evento',
      start: 'Inicio',
      end: 'Fin',
      capacity: 'Capacidad',
      totalBooking: 'Total Booking',
      forms: 'Formularios',
      status: 'Estado',
    },
    formsCount: '{{count}} formularios',
    createTitle: 'Crear evento',
    createDesc: 'Añade un nuevo evento para tu sucursal.',
    editTitle: 'Editar evento',
    editDesc: 'Actualiza la información del evento',
    createEvent: 'Crear evento',
    createItem: 'Crear',
    addEvent: 'Añadir evento',
    saveChanges: 'Guardar cambios',
    close: 'Cerrar',
    name: 'Nombre',
    namePlaceholder: 'Introduce el nombre del evento',
    description: 'Descripción',
    descriptionPlaceholder: 'Introduce la descripción',
    startDate: 'Fecha de inicio',
    startTime: 'Hora de inicio',
    endDate: 'Fecha de fin',
    endTime: 'Hora de fin',
    capacity: 'Capacidad máxima',
    capacityPlaceholder: '100',
    visible: 'Mostrar capacidad máxima',
    forms: 'Formularios',
    formsPlaceholder: 'Seleccionar formularios',
    mandatory: 'Evento obligatorio',
    weekdaysLabel: 'Días de la semana',
    selectAllWeekdays: 'Seleccionar todos',
    clearWeekdays: 'Borrar',
    availabilityDates: {
      title: 'Fechas de disponibilidad del evento',
      dateRange: 'Rango de fechas',
      placeholder: 'Seleccionar rango de fechas',
      empty:
        'Aún no hay fechas de disponibilidad. Añade un rango para empezar.',
      addRange: 'Añadir rango',
      createTitle: 'Añadir rango de fechas',
      editTitle: 'Editar rango de fechas',
      create: 'Añadir',
      update: 'Actualizar',
      editRange: 'Editar rango',
      deleteRange: 'Eliminar rango',
      deleteTitle: '¿Eliminar rango de fechas?',
      deleteDescription:
        '¿Seguro que quieres eliminar este rango de disponibilidad?',
      dateRequired: 'Selecciona un rango de fechas.',
      dateOrder: 'La fecha de fin debe ser igual o posterior a la de inicio.',
      dateOverlap:
        'Este rango se solapa con fechas existentes. Elige fechas libres.',
    },
    weekdays: {
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo',
    },
    eventImage: 'Imagen del evento',
    uploadImage: 'Subir imagen',
    removeImage: 'Eliminar imagen',
    uploading: 'Subiendo...',
    removing: 'Eliminando...',
    editEvent: 'Editar evento',
    duplicate: 'Duplicar',
    deleteEvent: 'Eliminar evento',
    deleteModal: {
      title: '¿Eliminar evento?',
      description:
        '¿Seguro que quieres eliminar este evento? Esta acción no se puede deshacer.',
    },
    validation: {
      nameRequired: 'El nombre es obligatorio',
      nameMax: 'El nombre no puede superar 120 caracteres',
      descriptionMax: 'La descripción no puede superar 500 caracteres',
      capacityInvalid: 'Introduce una capacidad válida',
      capacityInteger: 'La capacidad debe ser un número entero',
      capacityMin: 'La capacidad debe ser al menos 1',
      capacityMax: 'La capacidad no puede superar 999',
      startDateRequired: 'La fecha de inicio es obligatoria',
      startTimeRequired: 'La hora de inicio es obligatoria',
      endDateRequired: 'La fecha de fin es obligatoria',
      endTimeRequired: 'La hora de fin es obligatoria',
      endBeforeStart: 'El fin debe ser posterior al inicio',
    },
    toast: {
      createSuccess: 'Evento creado correctamente.',
      createError: 'No se pudo crear el evento. Inténtalo de nuevo.',
      updateSuccess: 'Evento actualizado correctamente.',
      updateError: 'No se pudo actualizar el evento. Inténtalo de nuevo.',
      deleteSuccess: 'Evento eliminado correctamente.',
      deleteError: 'No se pudo eliminar el evento. Inténtalo de nuevo.',
      duplicateSuccess: 'Evento duplicado correctamente.',
      duplicateError: 'No se pudo duplicar el evento. Inténtalo de nuevo.',
      showEnabled: 'El evento ahora es visible.',
      showDisabled: 'El evento ahora está oculto.',
      statusError:
        'No se pudo actualizar el estado del evento. Inténtalo de nuevo.',
      uploadSuccess: 'Imagen subida correctamente.',
      uploadError: 'No se pudo subir la imagen.',
      saveBeforeImage:
        'Completa los datos obligatorios del evento antes de subir una imagen.',
      removeImageSuccess: 'Imagen eliminada correctamente.',
      removeImageError: 'No se pudo eliminar la imagen.',
      dateRangeCreateSuccess: 'Rango de disponibilidad añadido.',
      dateRangeCreateError: 'No se pudo añadir el rango de disponibilidad.',
      dateRangeUpdateSuccess: 'Rango de disponibilidad actualizado.',
      dateRangeUpdateError: 'No se pudo actualizar el rango de disponibilidad.',
      dateRangeDeleteSuccess: 'Rango de disponibilidad eliminado.',
      dateRangeDeleteError: 'No se pudo eliminar el rango de disponibilidad.',
      dateRangeLoadError: 'No se pudo cargar el rango de disponibilidad.',
    },
  },
  reports: {
    title: 'Informes',
    subtitle: 'Panel de analítica e informes',
    comingSoon: 'Informes próximamente',
    comingSoonDesc:
      'Analítica avanzada e informes personalizados estarán disponibles en una versión futura.',
  },
  errors: {
    somethingWrong: 'Algo salió mal',
    unexpected: 'Ocurrió un error inesperado. Inténtalo de nuevo.',
    pageNotFound: 'Página no encontrada',
    pageNotFoundDesc: 'La página que buscas no existe o ha sido movida.',
    accessDenied: 'Acceso denegado',
    accessDeniedDesc:
      'No tienes permiso para acceder a este recurso. Contacta al administrador.',
    backToDashboard: 'Volver al panel',
    backToHome: 'Volver al inicio',
    publicHint:
      'Revisa el enlace que recibiste, o contacta al local si necesitas ayuda.',
    applicationError: 'Error de aplicación',
    reload: 'Recargar página',
  },
  publicPromo: {
    documentTitle: 'Promoción',
    loading: 'Cargando promoción',
    unavailableTitle: 'Promoción no disponible',
    unavailableDesc:
      'Este enlace de promoción falta, no es válido o ya no está activo.',
    expiredDesc: 'Esta promoción ha caducado y ya no está disponible.',
    comingSoonDesc: 'Esta promoción aún no está lista para recoger respuestas.',
    formUnavailableTitle: 'Formulario no disponible',
    formUnavailableMessage:
      'Este formulario de promoción está suspendido o no disponible.',
    badge: 'Promoción',
    requestTitle: 'Solicita tu cupón',
    requestSubtitle: 'Completa tus datos para solicitar la promoción.',
    optional: 'Opcional',
    birthDate: 'Fecha de nacimiento',
    cap: 'Código postal',
    capPlaceholder: 'Ej. 28001',
    address: 'Dirección',
    addressPlaceholder: 'Calle y número',
    nationality: 'Nacionalidad',
    nationalityPlaceholder: 'Tu nacionalidad',
    requiredNote: '* Campos obligatorios',
    submitting: 'Guardando…',
    personalInfo: 'Información personal',
    phoneLabel: 'Móvil',
    phonePlaceholder: 'Teléfono móvil',
    firstName: 'Nombre',
    firstNamePlaceholder: 'Nombre',
    lastName: 'Apellido',
    lastNamePlaceholder: 'Apellido',
    email: 'Correo',
    emailPlaceholder: 'Correo',
    consentsTitle: 'Consiento el tratamiento de datos personales',
    privacyConsent: 'Privacidad y tratamiento de datos',
    marketingConsent: 'Consentimiento de marketing y comunicación',
    profilingConsent: 'Consentimiento de perfilado',
    detailsLink: 'Detalle',
    selectAll: 'Seleccionar todo',
    submit: 'Solicitar el cupón',
    toast: {
      success: 'Cupón generado correctamente.',
      error: 'No se pudo generar el cupón. Inténtalo de nuevo.',
    },
    thankYou: {
      title: '¡Gracias!',
      message: 'Tu solicitud se ha enviado correctamente.',
      continue: 'Continuar',
    },
    lookupFound: 'Hemos encontrado tus datos y los hemos rellenado.',
    welcomeBack: 'Bienvenido de nuevo, {{firstName}}. Ya tenemos tus datos.',
    welcomeBackNoName: 'Bienvenido de nuevo. Ya tenemos tus datos.',
    alreadyHasCoupon: 'Ya tienes un cupón para esta promoción.',
    alreadyUsed: 'Este cupón ya se ha utilizado.',
    maxCoupons:
      'Ya no se pueden generar más cupones para esta promoción. Se ha alcanzado el límite.',
    lookupError:
      'No hemos podido comprobar este número. Puedes completar el formulario igualmente.',
    lookupNotFound: 'No se ha encontrado esta promoción.',
    lookupInvalid: 'Comprueba el número de móvil e inténtalo de nuevo.',
    resend: {
      title: '¿Enviar tu cupón por WhatsApp?',
      message:
        'Ya has solicitado un cupón. Podemos enviártelo de nuevo por WhatsApp con el código QR.',
      confirm: 'Enviar por WhatsApp',
      decline: 'Ahora no',
      close: 'Cerrar',
      success: 'Hemos enviado el cupón a tu WhatsApp.',
      error: 'No se ha podido enviar el cupón. Inténtalo de nuevo.',
    },
    validation: {
      prefixRequired: 'Selecciona un prefijo internacional.',
      phoneMin: 'Introduce un número de móvil válido.',
      phoneRange: 'Introduce de 6 a 15 dígitos.',
      firstNameRequired: 'El nombre es obligatorio.',
      lastNameRequired: 'El apellido es obligatorio.',
      emailRequired: 'El correo es obligatorio.',
      emailInvalid: 'Introduce un correo válido.',
      birthDateInvalid: 'Introduce una fecha válida que no sea futura.',
      privacyRequired: 'El consentimiento de privacidad es obligatorio.',
    },
  },
  publicFidelity: {
    documentTitle: 'Tarjeta fidelity',
    loading: 'Cargando programa fidelity',
    unavailableTitle: 'Programa no disponible',
    unavailableDesc:
      'Comprueba el enlace recibido o contacta con el restaurante.',
    badge: 'Tu programa de fidelidad',
    cardLabel: 'Tarjeta fidelity',
    previewPoints: 'Cada visita cuenta.',
    previewDiscount: 'Ventajas pensadas para ti.',
    requestTitle: 'Solicita tu tarjeta',
    requestSubtitle: 'Crea tu tarjeta personal y llévala siempre contigo.',
    optional: 'Opcional',
    requiredNote: '* Campos obligatorios',
    submitting: 'Guardando…',
    phoneLabel: 'Móvil',
    phonePlaceholder: 'Tu número',
    firstName: 'Nombre',
    firstNamePlaceholder: 'Tu nombre',
    lastName: 'Apellido',
    lastNamePlaceholder: 'Tu apellido',
    email: 'Email',
    emailPlaceholder: 'nombre@ejemplo.com',
    consentsTitle: 'Tus preferencias',
    privacyConsent: 'Privacidad y tratamiento de datos',
    marketingConsent: 'Consentimiento de marketing y comunicaciones',
    profilingConsent: 'Consentimiento de perfilado',
    detailsLink: 'Información',
    selectAll: 'Seleccionar todo',
    submit: 'Crear mi tarjeta',
    successTitle: 'Tu tarjeta está lista.',
    successSubtitle: 'Guarda el QR y muéstralo en el restaurante.',
    qrAlt: 'Código QR de tu tarjeta fidelity',
    openQr: 'Abrir y guardar el QR',
    welcomeBack: 'Bienvenido de nuevo, {{firstName}}. Ya tenemos tus datos.',
    welcomeBackNoName: 'Bienvenido de nuevo. Ya tenemos tus datos.',
    lookupError:
      'No hemos podido verificar este número. Aun así puedes completar el formulario.',
    lookupUnavailable: 'Este programa ya no está disponible.',
    lookupInvalid: 'Comprueba el número de móvil e inténtalo de nuevo.',
    requestLink: 'Solicitar el enlace de nuevo',
    requestLinkSending: 'Enviando solicitud…',
    requestLinkSent:
      'Si este contacto está asociado a una tarjeta activa, recibirás el enlace de nuevo.',
    requestLinkError: 'No se pudo solicitar el enlace. Inténtalo de nuevo.',

    errors: {
      alreadyRegistered:
        'Ya tienes una tarjeta para este programa. Usa el QR recibido o contacta con el restaurante para recuperarla.',
      unavailable: 'Este programa ya no está disponible.',
      generic:
        'No se ha podido crear la tarjeta. Inténtalo de nuevo en unos minutos o contacta con el restaurante.',
    },
    validation: {
      prefixRequired: 'Selecciona un prefijo.',
      phoneRange: 'Introduce de 6 a 15 dígitos.',
      firstNameRequired: 'Introduce el nombre.',
      lastNameRequired: 'Introduce el apellido.',
      emailInvalid: 'Comprueba el correo electrónico.',
      privacyRequired: 'El consentimiento de privacidad es obligatorio.',
    },
  },
  publicBookingForm: {
    imagePreview: {
      open: 'Ampliar imagen: {{name}}',
      close: 'Cerrar vista previa de la imagen',
      error: 'No se puede cargar la imagen.',
    },
    title: 'Reservación',
    welcome: 'Bienvenido a',
    loading: 'Cargando formulario de reserva',
    missingFormCode: 'Falta el código del formulario en la URL.',
    loadError: 'No se puede cargar este formulario de reserva.',
    inactiveTitle: 'Formulario no disponible',
    inactiveMessage: 'Este formulario de reserva no está activo actualmente.',
    invalidFormType: 'Este enlace no es un formulario de reserva.',
    steps: {
      date: 'Fecha',
      hours: 'Horas',
      menu: 'Menú',
      extra: 'Extra',
      details: 'Detalles',
      calendar: 'Calendario',
    },
    eventBanner: {
      countOne: '{{count}} evento este mes',
      count: '{{count}} eventos este mes',
      tapHint: 'Toca para ver qué días',
    },
    menuBanner: {
      countOne: '{{count}} menú este mes',
      count: '{{count}} menús este mes',
      tapHint: 'Toca para ver qué días',
    },
    calendar: {
      legendEvent: 'Evento especial',
      legendMenu: 'Menús disponibles',
    },
    restaurantNoMenu: {
      title: 'No menu',
      description: 'Número de comensales sin menú',
    },
    eventSheet: {
      fromTime: 'Desde las {{time}}',
      maxCovers: 'Máx. {{count}} cubiertos',
      gotIt: 'Continuar',
      continueWithEvent: 'Continuar por el evento',
      showMore: 'Ver más',
      showLess: 'Ver menos',
      close: 'Cerrar',
    },
    hours: {
      selectedDate: 'Fecha seleccionada',
      guestsLabel: 'Huéspedes (sin niños)',
      childrenLabel: 'Niños',
      timeSlotLabel: 'Franja horaria disponible',
      guestOption: '{{count}} Huéspedes',
      childrenOption: '{{count}} Niños',
      overlimit: 'Límite superado en {{count}}',
      overbooking: 'Overbooking de {{count}}',
      availabilityFinished: 'Disponibilidad agotada para esta franja horaria',
      attentionTitle: 'Atención',
      attentionMessage:
        'El número de huéspedes seleccionado supera la disponibilidad restante para esta franja horaria.',
      attentionOk: 'OK',
      callOverMaxOption: 'Llamar para mesas de más de {{count}} huéspedes',
      callOverMaxTitle: 'Info',
      callOverMaxMessageBefore: '¿Llamar al número',
      callOverMaxMessageAfter: 'para reservar una mesa?',
      callOverMaxHint:
        'Abriremos el marcador del teléfono para completar la llamada.',
      callOverMaxCall: 'Llamar',
      callOverMaxCancel: 'Cancelar',
    },
    extra: {
      badge: 'Extra',
      catalogTitle: 'Catálogo de servicios',
      mandatoryNotice:
        'La elección de al menos un producto/servicio es obligatoria.',
      serviceForGuests: 'Servicio/mesa para {{count}} huéspedes',
      decrease: 'Disminuir cantidad',
      increase: 'Aumentar cantidad',
    },
    menu: {
      badge: 'Menú',
      catalogTitle: 'Catálogo de menús',
      mandatoryQuantityHint:
        'Elige un menú (o “Sin menú”) para cada persona. El total de selecciones debe ser igual a {{count}} (adultos + niños).',
      decrease: 'Disminuir cantidad',
      increase: 'Aumentar cantidad',
    },
    recap: {
      title: 'Resumen de reserva',
      time: 'Horas',
      guests: 'Huéspedes',
      children: 'Niños',
      selectedServices: 'Servicios seleccionados',
      selectedMenus: 'Menús seleccionados',
      total: 'Total',
      paymentOnSite: 'Pago en el local',
    },
    details: {
      phoneTitle: 'Introduce tu teléfono para continuar',
      welcomeBackTitle: '¡Bienvenido de nuevo, {{name}}!',
      welcomeBackTitleGeneric: '¡Bienvenido de nuevo!',
      welcomeBackMessage:
        'Ya tenemos tus datos. Puedes continuar o actualizarlos si es necesario.',
      personalInfo: 'Información personal',
      consentsTitle: 'Consiento el tratamiento de datos personales',
      privacyConsent: 'Tratamiento de privacidad y datos',
      marketingConsent: 'Consentimiento de marketing y comunicación',
      commercialConsent:
        'Consentimiento de cesión de datos a terceros con fines comerciales',
      profilingConsent: 'Consentimiento de elaboración de perfiles',
      detailsLink: 'Detalles',
      selectAll: 'Seleccionar todo',
      consentModal: {
        title: 'INFORMACIÓN SOBRE EL TRATAMIENTO DE DATOS PERSONALES',
        subtitle:
          '(de conformidad con el artículo 13 del Reg. UE n.º 679/2016 - RGPD)',
        addressLabel: 'Dirección',
        emailLabel: 'Email',
        lastUpdated: 'Última actualización',
        section1Title: '1. Premisa',
        section1P1:
          'Esta información es facilitada por {{branchName}} (Código Fiscal y NIF), con domicilio social en {{branchAddress}} (en adelante, el "Responsable del tratamiento"). (en adelante, el "Responsable del tratamiento"), de conformidad con el Reglamento UE 2016/679 (RGPD) y el D.lgs. 196/2003 (Código de Privacidad), modificado por el <b>D.lgs. 101/2018.</b>',
        section1P2:
          'Esta información se aplica al tratamiento de datos personales realizado por el Responsable a través de la plataforma <b>"Pro20"</b> para la gestión de la actividad, reservas, servicios ofrecidos y comunicaciones con los clientes.',
        section1P3: 'La información se facilita a los Usuarios que:',
        section1Li1:
          '• Utilizan el sitio web del Responsable para solicitar información o reservar una mesa;',
        section1Li2:
          '• Facilitan voluntariamente sus datos mediante dispositivos móviles personales o locales (p. ej. tablets) para acceder a servicios como el programa de fidelización o Wi-Fi gratuito;',
        section1Li3:
          '• Se presentan físicamente en el establecimiento y facilitan sus datos para recibir newsletters, ofertas comerciales y material promocional de los productos y servicios del Responsable.',
        section2Title: '2. Responsable del tratamiento',
        section2Sensitive:
          'Los datos sensibles solo se tratarán si son estrictamente indispensables y con el consentimiento del Usuario, manifestado mediante acciones explícitas como la selección de una opción en línea (p. ej. "Acepto" o "Consiento") o la firma de un formulario en papel.',
        section3Title: '3. Datos tratados y métodos de adquisición',
        section3Intro: 'Los datos se adquieren de las siguientes formas:',
        section3Li1:
          '• A través de la plataforma PRO20 para la gestión de reservas y servicios ofrecidos.',
        section3Li2:
          '• A través del sitio web del Responsable, cuando el Usuario solicita información o reserva una mesa',
        section3Li3:
          '• Directamente en el local, cuando el Usuario facilita datos para acceder a servicios (Wi-Fi, fidelización, etc.).',
        section4Title: '4. Datos de navegación',
        section4Body:
          'Si el Usuario interactúa con el sitio web u otras plataformas digitales, se recogen automáticamente datos de navegación (p. ej. dirección IP, tipo de dispositivo, sistema operativo, páginas visitadas). El uso de cookies y herramientas similares se describe en la Cookie Policy, consultable en el sitio web del Responsable.',
        section5Title: '5. Finalidad del tratamiento',
        section51Title: '5.1 Finalidades principales',
        section51Intro: 'Los datos se tratan para:',
        section51Li1:
          '✔ Gestionar las reservas y servicios solicitados (fila-rápida, Wi-Fi, fidelización, cuestionarios de satisfacción, etc.)',
        section51Li2:
          '✔ Cumplir obligaciones legales, fiscales y administrativas',
        section51Li3:
          '✔ Proteger al personal y al servicio de conductas ilícitas o fraudulentas',
        section51Li4:
          '✔ Análisis estadísticos anónimos para mejorar la calidad de los servicios',
        section51Li5:
          '✔ Comunicarse con el Usuario sobre la reserva u otros servicios solicitados',
        section51Note:
          '📌 Facilitar los datos es necesario para acceder a los servicios: no facilitarlos puede impedir la reserva o la prestación del servicio.',
        section52Title:
          '5.2 Finalidades secundarias (Marketing y promociones): solo con consentimiento explícito.',
        section52Intro:
          'Previo consentimiento explícito, los datos podrán utilizarse para:',
        section52Li1:
          '📢 Envío de comunicaciones promocionales y ofertas personalizadas, por email, SMS u otros canales digitales',
        section52Li2:
          '📢 Estudios de mercado y encuestas de experiencia del cliente',
        section52Li3:
          '📢 Análisis de hábitos de consumo para ofrecer experiencias personalizadas.',
        section52Note:
          '⚠ El consentimiento de marketing es opcional y puede revocarse en cualquier momento escribiendo a {{email}}.',
        section6Title: '6. Destinatarios de los datos',
        section6Intro: 'Los datos recogidos podrán comunicarse a:',
        section6Li1:
          '• Personal interno autorizado del Responsable, para la gestión del servicio.',
        section6Li2:
          '• Proveedores de servicios TI y plataformas de gestión (p. ej. reservas online, email marketing, hosting web), conforme a acuerdos de confidencialidad.',
        section6Li3:
          '• Autoridades públicas, en caso de requerimientos legales u obligaciones normativas.',
        section6Note:
          '📌 Los datos NO se divulgarán ni se venderán a terceros.',
        section7Title: '7. Conservación de los datos',
        section7Intro: 'Los datos se conservarán para:',
        section7Li1:
          '📌 Finalidades administrativas y contables: el tiempo necesario para cumplir las obligaciones legales (hasta 10 años).',
        section7Li2:
          '📌 Finalidades de marketing: hasta la retirada del consentimiento o, sin interacciones, un máximo de 24 meses.',
        section7Li3:
          '📌 Datos de navegación: según lo establecido en la Cookie Policy.',
        section8Title: '8. Derechos del Usuario',
        section8Intro: 'Los usuarios tienen derecho a:',
        section8Li1:
          '✅ Acceder a sus datos personales y obtener información sobre su tratamiento.',
        section8Li2:
          '✅ Solicitar la rectificación o eliminación de los datos personales.',
        section8Li3:
          '✅ Limitar u oponerse al tratamiento por motivos legítimos.',
        section8Li4:
          '✅ Retirar el consentimiento en cualquier momento sin perjuiciar la licitud del tratamiento anterior.',
        section8Li5:
          '✅ Presentar una reclamación ante la Autoridad de Protección de Datos (www.garanteprivacy.it).',
        section9Title:
          '9. Consentimiento a la cesión de datos a terceros con fines comerciales',
        section9Li1:
          '✅ Consiento el tratamiento de mis datos personales con fines de marketing directo por terceros, incluso mediante su cesión a socios comerciales de sectores como editorial, tecnología, viajes, finanzas, etc. Dichos sujetos podrán contactarme por email, teléfono u otros canales para sus propias iniciativas promocionales. El consentimiento es opcional y puede revocarse en cualquier momento escribiendo a {{email}}.',
        section9Contact: '📧 Para ejercer sus derechos, contacte: {{email}}',
        section10Title: '10. Modificaciones de la información',
        section10Body:
          'El Responsable se reserva el derecho de actualizar esta información. Los cambios se comunicarán en el sitio web o por email si es necesario.',
      },
    },
    actions: {
      continue: 'Continuar',
      back: 'Atrás',
      proceedToDetails: 'Ir a los datos personales',
    },
    validation: {
      date: 'Selecciona una fecha.',
      hours: 'Completa huéspedes, niños y franja horaria.',
      menu: 'Revisa la selección del menú.',
      menuMandatory:
        'Elige los menús de modo que el total coincida con adultos + niños.',
      extra: 'Selecciona al menos un servicio.',
    },
    fieldErrors: {
      firstNameMin: 'El nombre es obligatorio.',
      firstNameInvalid: 'El nombre solo puede contener letras.',
      lastNameMin: 'El apellido debe tener al menos 2 caracteres.',
      lastNameInvalid: 'El apellido solo puede contener letras.',
      emailRequired: 'El correo electrónico es obligatorio.',
      emailInvalid: 'Introduce un correo electrónico válido.',
      postalCodeRequired: 'El código postal es obligatorio.',
      dateOfBirthRequired: 'La fecha de nacimiento es obligatoria.',
      specialRequestsRequired: 'Las solicitudes especiales son obligatorias.',
      singleChoiceRequired: 'Selecciona una opción.',
      multiChoiceRequired: 'Selecciona al menos una opción.',
      dateOfBirthInvalid: 'Introduce una fecha de nacimiento válida.',
      dateOfBirthFuture: 'La fecha de nacimiento no puede ser futura.',
      countryCodeInvalid: 'Selecciona un prefijo válido.',
      phoneRequired: 'El teléfono es obligatorio.',
      phoneInvalid: 'El teléfono solo debe contener dígitos.',
      phoneMin: 'El teléfono debe tener al menos 6 dígitos.',
      phoneMax: 'El teléfono puede tener como máximo 15 dígitos.',
      privacyRequired: 'El consentimiento de privacidad es obligatorio.',
      cardRequestConsentRequired:
        'Acepta los términos de verificación / depósito de la tarjeta.',
      bookingTimeInvalid: 'Selecciona una hora válida.',
      coverOverlimit:
        'Huéspedes y niños superan el máximo de cubiertos de este formulario.',
    },
    cardRequest: {
      title: 'Card required for this booking',
      notice:
        'Para confirmar esta reserva se requiere una tarjeta de pago para la franja seleccionada.',
      alreadyOnFile:
        'Ya tenemos una tarjeta para este número. Confirma la reserva para usarla.',
      acceptValidation:
        'Acepto la verificación de la tarjeta. No se cargará ni bloqueará ningún importe durante esta operación.',
      acceptDeposit:
        'Acepto la preautorización de {{amount}} en la tarjeta y las condiciones que regulan el cobro, la cancelación y el no-show.',
      acceptPayNow:
        'Confirmo el pago de {{amount}} y acepto las condiciones de cancelación, reembolso y no-show.',
      policyToggle: 'Leer la política de pago completa',
      policyTitle: 'Política de pago, tarjeta y cancelación',
      policy: {
        s1Title: '1. Identidad del establecimiento',
        s1Body:
          'La reserva y el pago los gestiona {{branchName}}, {{branchAddress}}. Stripe actúa exclusivamente como proveedor de pagos. Contacto: {{contact}}.',
        s2Title: '2. Verificación de la tarjeta',
        s2Body:
          'Cuando solo se solicita verificación, no se carga ni bloquea ningún importe. La tarjeta se verifica mediante Stripe.',
        s3Title: '3. Preautorización / depósito',
        s3Body:
          'Puede solicitarse una preautorización de {{amount}} ({{perGuest}} por comensal). El desbloqueo depende del banco emisor.',
        s4Title: '4. Pago anticipado',
        s4Body:
          'Si está configurado, puede cobrarse un anticipo o depósito según las reglas del establecimiento.',
        s5Title: '5. Cancelación y modificación',
        s5Body:
          'La cancelación gratuita puede aplicarse hasta el plazo del establecimiento. Después pueden retenerse o cobrarse importes.',
        s6Title: '6. No-show y retraso',
        s6Body:
          'En caso de no-show o retraso significativo, el establecimiento puede retener o cobrar importes según su política.',
        s7Title: '7. Reembolsos',
        s7Body:
          'Los reembolsos elegibles se procesan al mismo método de pago, en los plazos del establecimiento y del proveedor.',
        s8Title: '8. Conservación de la tarjeta',
        s8Body:
          'Los datos de la tarjeta pueden almacenarse tokenizados por Stripe y reutilizarse solo con consentimiento específico. PRO20 y el establecimiento no guardan el número completo.',
        s9Title: '9. Reclamaciones y asistencia',
        s9Body:
          'Para cancelaciones, reclamaciones o asistencia contacte al establecimiento ({{contact}}) indicando su código de reserva.',
        version: 'Versión {{version}}',
      },
    },
    legacyCard: {
      eyebrow: 'Verificación directa',
      title: 'Datos de la tarjeta de crédito',
      subtitle: 'El establecimiento no usa Stripe para esta reserva.',
      holder: 'Nombre en la tarjeta',
      number: 'Número de tarjeta',
      month: 'Mes',
      year: 'Año',
      cvc: 'CVV',
      circuit: '{{circuit}}',
      verified: 'Tarjeta verificada · termina en {{last4}}',
      cancel: 'Cancelar',
      verify: 'Verificar tarjeta',
      verifying: 'Verificando…',
      confirm: 'Confirmar',
      verifySuccess: 'Tarjeta verificada correctamente',
      verifyError: 'Los datos de la tarjeta no son válidos',
      requiredTitle: 'Tarjeta requerida',
      required: 'La tarjeta debe verificarse para completar la reserva.',
      storeWarning:
        'La reserva se ha creado, pero no se pudo guardar la tarjeta. Inténtalo de nuevo desde la solicitud de tarjeta.',
      saved: 'Tarjeta guardada · termina en {{last4}}',
      alreadyOnFile:
        'Ya hay una tarjeta asociada a este cliente. No hacen falta más datos.',
      pageLoadError: 'No se ha podido abrir esta solicitud de tarjeta.',
      saveError: 'No se ha podido guardar la tarjeta. Inténtalo de nuevo.',
      fieldRequired: 'Campo obligatorio',
      fieldInvalid: 'Valor no válido',
      circuits: {
        VISA: 'Visa',
        MASTERCARD: 'Mastercard',
        AMERICAN_EXPRESS: 'American Express',
        DISCOVER: 'Discover',
        JCB: 'JCB',
        MAESTRO: 'Maestro',
      },
    },
    stripe: {
      verifyTitle: 'Verifica tu tarjeta',
      verifyDescription:
        'Introduce los datos de tu tarjeta para confirmar esta reserva. No se cobrará ningún importe ahora.',
      verifyConfirm: 'Verificar tarjeta',
      payTitle: 'Completar el pago',
      payDescription: 'Autoriza {{amount}} para confirmar esta reserva.',
      payConfirm: 'Pagar {{amount}}',
      initializing: 'Preparando el pago seguro…',
      processing: 'Procesando…',
      initError: 'No se pudo iniciar el pago con Stripe. Inténtalo de nuevo.',
      confirmError: 'No se pudo completar el pago. Inténtalo de nuevo.',
      verifyPending:
        'Stripe aún no ha confirmado la tarjeta. Inténtalo de nuevo.',
      cardRequired: 'La tarjeta debe verificarse para completar la reserva.',
      resumePayment: 'Completar el pago',
      resumeNotice:
        'Tu reserva está en espera de pago. Completa el pago para confirmarla: no se creará una reserva nueva.',
    },
    phonePlaceholder: 'Introduce el número de teléfono',
    unknownCustomer: 'Cliente desconocido',
    blacklist: 'Lista negra',
    customerDetails: 'Tus datos',
    bookingDetails: 'Detalles de la reserva',
    firstName: 'Nombre',
    lastName: 'Apellido',
    email: 'Correo electrónico',
    phone: 'Teléfono',
    postalCode: 'Código postal',
    dateOfBirth: 'Fecha de nacimiento',
    guests: 'Huéspedes',
    children: 'Niños',
    time: 'Hora',
    selectTime: 'Seleccionar hora',
    slotNotAvailable: 'La disponibilidad para este horario ha finalizado.',
    noAvailableTimes: 'No hay horarios disponibles para la fecha seleccionada.',
    specialRequests: 'Notas personales y solicitudes',
    comingWithDogs: 'Vengo con perros',
    extraServices: 'Servicios extra',
    amenityGuests: '{{count}} huéspedes',
    submit: 'Enviar reserva',
    submitError: 'No se pudo enviar tu reserva. Inténtalo de nuevo.',
    existingBooking: {
      title: 'Cuidado, ya existe una reserva',
      date: 'Fecha',
      time: 'Hora',
      guests: 'Comensales',
      children: 'Niños',
      event: 'Evento',
      menus: 'Menús',
      noMenu: 'Sin menú',
      services: 'Servicios',
      continueQuestion: '¿Quieres continuar y crear esta reserva de todos modos?',
      continue: 'Continuar',
    },
    thankYou: {
      title: '¡Gracias!',
      message:
        'Hemos recibido tu solicitud de reserva. Nos pondremos en contacto contigo pronto.',
      summaryTitle: 'Resumen de la reserva',
      date: 'Fecha',
      time: 'Hora',
      guests: 'Comensales',
      guestsValue: '{{guests}} Comensal ({{children}} Niños)',
      location: 'Ubicación',
      name: 'Nombre',
      phone: 'Teléfono',
      eventCode: 'Código del evento',
      eventName: 'Evento',
      menus: 'Menús',
      makeAnother: 'Hacer otra reserva',
      backHome: 'Volver al inicio',
      continue: 'Continuar',
    },
  },
  publicBookingUpdate: {
    title: 'Actualizar reserva',
    loading: 'Cargando reserva',
    missingBookingCode: 'Falta el código de reserva en la URL.',
    loadError: 'No se pudo cargar esta reserva.',
    formLoadError: 'No se pudo cargar la disponibilidad de esta reserva.',
    noFormTitle: 'Cambios en línea no disponibles',
    noFormMessage:
      'Esta reserva no se puede modificar en línea. Contacta directamente con el local.',
    notEditable: 'La reserva ya no es modificable',
    hello: 'Hola {{name}}',
    subtitle: 'Estás modificando tu reserva en {{branch}}',
    currentBadge: 'Actual: {{time}} · {{date}}',
    currentlyBooked: 'Reserva actual',
    current: {
      title: 'Tu reserva',
      client: 'Cliente',
      date: 'Fecha actual',
      time: 'Hora actual',
      guests: 'Comensales',
      children: 'Niños',
      event: 'Evento',
      menu: 'Menú',
      menuPeople: 'Personas',
      showDetails: 'Detalles',
      hideDetails: 'Ocultar',
    },
    pendingModification: {
      title: 'Ya hay una solicitud de modificación pendiente de confirmación',
      summary:
        '{{date}} · {{time}} · {{guests}} comensales, {{children}} niños',
      date: 'Fecha',
      time: 'Hora',
      guests: 'Comensales',
      children: 'Niños',
    },
    form: {
      title: 'Cambiar reserva',
      subtitle: 'Elige una nueva fecha, número de comensales y franja horaria.',
      selectedDate: 'Fecha seleccionada',
      guestsLabel: 'Comensales (niños no incluidos)',
      childrenLabel: 'Niños',
      timeSlotLabel: 'Franja horaria disponible',
      guestOption: '{{count}} Comensales',
      childrenOption: '{{count}} Niños',
      selectTime: 'Seleccionar hora',
      noAvailableTimes:
        'No hay horarios disponibles para la fecha seleccionada.',
      notesLabel: 'Notas y peticiones personales',
      notesPlaceholder: 'Añade una nota para el local…',
      readyToUpdate:
        'Revisa los cambios y pulsa Actualizar para enviar la solicitud de modificación.',
      calendarUnavailable:
        'El calendario no está disponible para esta reserva (falta formulario o sucursal).',
    },
    actions: {
      update: 'Actualizar reserva',
      updating: 'Actualizando…',
      delete: 'Eliminar',
      cancelReservation: 'Cancelar reserva',
      cancelling: 'Cancelando…',
    },
    deleteConfirm:
      '¿Seguro que quieres cancelar esta reserva? Esta acción no se puede deshacer.',
    deleteModal: {
      title: '¿Cancelar la reserva?',
      description:
        '¿Seguro que quieres cancelar esta reserva? Esta acción no se puede deshacer.',
    },
    toast: {
      updateSuccess: 'Solicitud de modificación enviada.',
      updateError: 'No se pudo actualizar la reserva. Inténtalo de nuevo.',
      validationError: 'Corrige los campos destacados antes de actualizar.',
      deleteSuccess: 'Reserva eliminada correctamente.',
      deleteError: 'No se pudo eliminar la reserva. Inténtalo de nuevo.',
    },
    thankYou: {
      badge: 'Solicitud enviada',
      title: 'Gracias',
      headerMessage:
        'Hemos recibido tu solicitud de modificación y responderemos lo antes posible.',
      statusLabel: 'Solicitud enviada',
      awaitingTitle: 'La modificación está pendiente de confirmación',
      awaitingMessage:
        'Hemos recibido tu solicitud de modificación y responderemos lo antes posible.',
      step1Title: 'Revisión del local',
      step1Message:
        'El personal comprobará la disponibilidad y los detalles de la solicitud.',
      step2Title: 'Respuesta automática',
      step2Message:
        'Recibirás una actualización en cuanto se acepte o rechace la modificación.',
      closeHint:
        'Puedes cerrar esta página: tu solicitud se ha registrado correctamente.',
    },
    result: {
      updatedTitle: 'Reserva actualizada',
      updatedMessage:
        'Tus cambios se han guardado. El local ha sido notificado de la actualización.',
      deletedTitle: 'Reserva eliminada',
      deletedMessage:
        'Tu reserva ha sido cancelada. Esperamos verte en otra ocasión.',
    },
  },
  backoffice2020: {
    title: 'Backoffice PRO20',
    logout: 'Cerrar sesión',
    login: {
      documentTitle: 'Inicio de sesión Backoffice',
      title: 'Inicia sesión en Backoffice',
      subtitle: 'Usa tu email y contraseña de administrador para continuar.',
      emailPlaceholder: 'Email',
      passwordPlaceholder: 'Contraseña',
      rememberCredentials: 'Recordar credenciales',
      signIn: 'Iniciar sesión',
      invalidCredentials: 'Email o contraseña no válidos.',
      adminBadge: 'Acceso admin',
      authorizedOnly: 'Solo personal autorizado',
      heroTitle: 'Opera la plataforma Pro20 con confianza',
      heroSubtitle:
        'Acceso seguro al backoffice para gestionar restaurantes, mensajería y controles globales.',
      heroCardTitle: 'Centro de control seguro',
      heroCardSubtitle: 'Operaciones de plataforma',
      heroPointOne: 'Gestiona restaurantes, personal y onboarding',
      heroPointTwo: 'Supervisa el estado de mensajería y alertas',
      heroPointThree: 'Aplica ajustes globales con plena trazabilidad',
      trustedBy: 'De confianza para operaciones seguras',
      trustAccessTitle: 'Acceso controlado',
      trustAccessDesc: 'Credenciales solo admin con sesiones protegidas',
      trustAuditTitle: 'Listo para auditoría',
      trustAuditDesc: 'Las acciones críticas quedan registradas',
      trustPlatformTitle: 'Escala de plataforma',
      trustPlatformDesc: 'Opera varios locales desde una consola',
      validation: {
        emailRequired: 'El email es obligatorio',
        emailInvalid: 'Introduce un email válido',
        emailMax: 'El email debe tener como máximo 255 caracteres',
        passwordMin: 'La contraseña debe tener al menos 8 caracteres',
        passwordMax: 'La contraseña debe tener como máximo 128 caracteres',
      },
    },
    whatsapp: 'WhatsApp',
    footer: 'BackOffice Pro20 © {{year}}',
    headingTitle: 'Bienvenido al panel operativo Pro20',
    headingWelcomeBack: '¡Bienvenido de nuevo, {{name}}!',
    headingWelcomeBackGeneric: '¡Bienvenido de nuevo!',
    actions: {
      announcements: 'Anuncios',
      notifications: 'Notificaciones',
      globalAlertTooltip: 'Alerta global en la app',
      expiredActivities: 'Actividades caducadas',
      expiredActivitiesTooltip: 'Activar/desactivar alertas de vencimiento',
      removeBanner: 'Eliminar banner',
      removeBannerTooltip: 'Eliminar el banner global de la app',
    },
    releaseMobile: {
      title: 'Release Mobile',
      currentVersion: 'Versión actual {{version}}',
      manageVersion: 'Gestionar versión',
      toast: {
        versionUpdated: 'Versión de la app actualizada',
        versionUpdateFailed: 'No se pudo actualizar la versión de la app',
        bannerRemoved: 'Banner global eliminado',
        bannerRemoveFailed: 'No se pudo eliminar el banner global',
      },
    },
    manageVersionModal: {
      title: 'Actualizar versión de la app',
      loading: 'Cargando versión actual…',
      loadError: 'No se pudo cargar la versión actual.',
      major: 'Major',
      majorHint: 'Cambios importantes',
      minor: 'Minor',
      minorHint: 'Nuevas funciones',
      bug: 'Bug',
      bugHint: 'Correcciones de la aplicación',
      patch: 'Patch',
      patchHint: 'Release Review',
      formatHint: 'El formato publicado será Major.Minor.Bug.Patch.',
      update: 'Actualizar versión',
    },
    removeBannerModal: {
      title: '¿Eliminar el banner global?',
      description: 'El mensaje ya no se mostrará en la app.',
      confirm: 'Eliminar banner',
      cancel: 'Cancelar',
    },
    globalAlertModal: {
      title: 'Introduce el nuevo mensaje de alerta global',
      confirmation: 'Confirmación',
      cancel: 'Cancelar',
      toast: {
        success: 'Alerta global actualizada',
        failed: 'No se pudo actualizar la alerta global',
      },
    },
    createActivityModal: {
      title: 'Crear sucursal',
      cancel: 'Cancelar',
      create: 'Crear',
      selectUser: 'Seleccionar usuario',
      selectUserPlaceholder: 'Selecciona un usuario',
      selectUserSearchPlaceholder: 'Buscar usuario por nombre o email',
      restaurantName: 'Nombre del restaurante',
      email: 'Email',
      phone: 'Teléfono',
      vatNumber: 'Número de IVA',
      companyName: 'Nombre de la empresa',
      sections: {
        marketing: 'Marketing',
        address: 'Dirección',
        geolocation: 'Geolocalización',
        integration: 'Integración',
      },
      gtmCode: 'Código GTM',
      gtmCodeHint: 'Código de Google Tag Manager',
      facebookPixel: 'Facebook Pixel',
      facebookPixelHint: 'El código del Pixel solo contiene números',
      address: 'Dirección',
      postalCode: 'Código postal',
      city: 'Ciudad',
      country: 'País',
      latitude: 'Latitud',
      latitudeHint: 'Ej. 45.464211',
      longitude: 'Longitud',
      longitudeHint: 'Ej. 9.191383',
      ipraticoApiKey: 'Clave API iPratico',
      ipraticoApiKeyHint: 'Clave API para la integración con iPracto',
      noUsers: 'No hay usuarios disponibles',
      toast: {
        created: 'Sucursal creada',
        createFailed: 'No se pudo crear la sucursal',
      },
      validation: {
        userRequired: 'La selección de usuario es obligatoria',
        restaurantRequired: 'El nombre del restaurante es obligatorio',
        emailInvalid: 'Introduce un email válido',
        facebookPixelNumeric: 'Facebook Pixel solo debe contener números',
      },
    },
    editActivity: {
      pageTitle: 'Editar actividad',
      breadcrumbRoot: 'backoffice',
      close: 'Cerrar',
      back: 'Volver a actividades',
      notFound: 'Actividad no encontrada.',
      update: 'Actualizar',
      enable: 'Habilitar',
      disabled: 'Deshabilitado',
      smsRemaining: 'SMS residuales',
      addSms: 'Añadir SMS',
      editSms: 'Editar SMS residuales',
      configureSmsModal: {
        title: 'Actualizar el número residual de SMS',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'Configurar SMS',
      },
      aiCreditsRemaining: 'Créditos de IA',
      editAiCredits: 'Editar créditos de IA',
      configureAiCreditsModal: {
        title: 'Actualizar el saldo de créditos de IA',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'Configurar créditos de IA',
      },
      emailSetup: 'Configuración de email',
      editEmailConfig: 'Editar configuración de email',
      restoreDefaultEmailConfig: 'Restaurar configuración predeterminada',
      emailFields: {
        mailResidue: 'Residuo de correo',
        customerEmail: 'Email del cliente',
        fromAddress: 'Dirección de origen',
        password: 'Contraseña',
        smtpServer: 'Servidor SMTP',
        smtpPort: 'Puerto SMTP',
      },
      configureEmailModal: {
        title: 'Actualizar el número residual de email',
        mailResidue: 'Residuo de correo',
        customerEmail: 'Email del cliente para mensajería personalizada',
        fromAddress: 'Dirección de origen',
        password: 'Contraseña generada',
        smtpServer: 'Servidor SMTP',
        smtpPort: 'Puerto SMTP',
        required: 'Este campo es obligatorio',
        save: 'Guardar configuración',
      },
      sections: {
        general: 'Datos generales',
        subscription: 'Suscripción',
        address: 'Dirección',
        coordinates: 'Coordenadas',
        marketing: 'Marketing',
        integrations: 'Integraciones',
        communications: 'Comunicaciones',
      },
      restaurantName: 'Nombre de sucursal',
      branchNameOnGoogle: 'Nombre de marca (Google)',
      email: 'Email',
      capacity: 'Capacidad del local',
      vatNumber: 'Número de IVA',
      companyName: 'Nombre de la empresa',
      phone: 'Móvil',
      url: 'URL',
      deadline: 'Fecha de vencimiento',
      address: 'Dirección',
      postalCode: 'Código postal',
      city: 'Ciudad',
      province: 'Provincia / Localidad',
      region: 'Región',
      country: 'País',
      longitude: 'Longitud',
      latitude: 'Latitud',
      gtmCode: 'Código GTM',
      facebookPixel: 'Código FB Pixel',
      ipraticoApiKey: 'iPratico XApiKey',
      ipraticoApiKeyPlaceholder: 'Introduce la clave',
      menuRestaurant: 'Menú del restaurante',
      menuRestaurantDesc:
        'Habilita el menú digital del restaurante para esta sucursal.',
      officialApi: 'API oficiales',
      officialApiDesc:
        'Habilita la API oficial de WhatsApp para esta sucursal.',
      googleReservation: 'Google Reservation',
      googleReservationDesc: 'Habilita Google Reservation para esta sucursal.',
      supplierOrders: 'Pedidos a proveedores',
      supplierOrdersDesc:
        'Habilita los pedidos a proveedores para esta sucursal.',
      deliveryTakeaway: 'Entrega / Para llevar',
      deliveryTakeawayDesc:
        'Habilita delivery y para llevar para esta sucursal. Requiere el Menú del Restaurante.',
      automation: 'Automatización',
      automationDesc: 'Habilita la automatización para esta sucursal.',
      statusEnabled: 'Activa',
      statusDisabled: 'Apagada',
      sectionHints: {
        general:
          'Nombre, contacto y cuántos comensales puede recibir esta sucursal.',
        subscription: 'Cuándo vence el acceso a esta sucursal.',
        address: 'Dónde encuentran esta sucursal los clientes y los partners.',
        coordinates: 'Posición en el mapa usada en los listados públicos.',
        marketing: 'Códigos de seguimiento del sitio de esta sucursal.',
        integrations:
          'Productos y servicios de partners conectados a esta sucursal.',
        communications:
          'Saldo de SMS, créditos de IA y buzón usado para los mensajes a los clientes.',
      },
      publicApi: {
        title: 'API REST pública',
        description:
          'Expone el historial de reservas de esta sucursal. Los clientes se autentican con la cabecera X-API-KEY.',
        configure: 'Configurar',
        statusConfigured: 'Configurada',
        statusMissing: 'Sin configurar',
        notConfigured:
          'Aún no hay X-API-KEY. Configura esta sucursal para crearla en el servidor.',
        loadFailed: 'No se pudo cargar la X-API-KEY.',
        retry: 'Reintentar',
        configureFailed: 'No se pudo crear la X-API-KEY',
        configured: 'X-API-KEY creada',
        deleteConfiguration: 'Eliminar configuración',
        deleteConfirmTitle: '¿Eliminar la configuración de la API REST?',
        deleteConfirmDescription:
          'Esto elimina la X-API-KEY de esta sede. Quienes la usen ya no podrán autenticarse.',
        deleted: 'Configuración de la API REST eliminada',
        deleteFailed: 'No se pudo eliminar la configuración de la API REST',
        keyLabel: 'X-API-KEY',
        keyHint:
          'Envía este valor en la cabecera X-API-KEY en cada petición del historial de reservas.',
        showKey: 'Mostrar clave',
        hideKey: 'Ocultar clave',
        copyKey: 'Copiar clave',
        copiedKey: 'X-API-KEY copiada',
        copyFailed: 'No se pudo copiar al portapapeles',
        bookingHistory: 'Historial de reservas',
        sampleRequest: 'Petición de ejemplo',
        sampleResponse: 'Respuesta de ejemplo',
        copySample: 'Copiar ejemplo',
        copiedSample: 'Ejemplo copiado',
        filtersHint:
          'Cada parámetro aparece por separado. El endpoint cambia según los filtros incluidos.',
        copyFilters: 'Copiar todos los filtros',
        showFilters: 'Mostrar filtros',
        hideFilters: 'Ocultar filtros',
        copiedFilters: 'Filtros copiados',
        endpointLabel: 'Endpoint',
        params: {
          page: 'page',
          size: 'size',
          status: 'status',
          bookingDate: 'bookingDate',
          bookingDateFrom: 'bookingDateFrom',
          bookingDateTo: 'bookingDateTo',
          search: 'search',
          timeRangeFilter: 'timeRangeFilter',
          assigned: 'assigned',
          walkInOnly: 'walkInOnly',
          sortBy: 'sortBy',
          sortDirection: 'sortDirection',
        },
        languages: {
          curl: 'cURL',
          javascript: 'JavaScript',
          python: 'Python',
          php: 'PHP',
        },
      },
      toast: {
        updated: 'Sucursal actualizada',
        updateFailed: 'No se pudo actualizar la sucursal',
        featureUpdated: 'Función actualizada',
        featureUpdateFailed: 'No se pudo actualizar la función',
        deliveryRequiresMenu:
          'Habilita el Menú del Restaurante antes de activar Delivery / Takeaway.',
        smsUpdated: 'Cantidad de SMS actualizada',
        smsUpdateFailed: 'No se pudo actualizar la cantidad de SMS',
        aiCreditsUpdated: 'Créditos de IA actualizados',
        aiCreditsUpdateFailed: 'No se pudieron actualizar los créditos de IA',
        emailUpdated: 'Configuración de email guardada',
        emailRestored: 'Configuración de email restaurada',
      },
      validation: {
        restaurantRequired: 'El nombre de la sucursal es obligatorio',
        emailInvalid: 'Introduce un email válido',
        urlInvalid: 'Introduce un enlace válido',
        facebookPixelNumeric: 'Facebook Pixel solo debe contener números',
        capacityInvalid: 'Introduce una capacidad válida',
        capacityInteger: 'La capacidad debe ser un número entero',
        capacityMin: 'La capacidad debe ser al menos 0',
      },
    },
    activities: {
      title: 'Sucursales',
      count: '{{count}} sucursales',
      create: 'Crear sucursal',
      loadError: 'No se pudieron cargar las sucursales. Inténtalo de nuevo.',
      expiringCaption: 'Por vencer',
      expiringAria: '{{count}} sucursales vencen en {{month}}',
    },
    tabs: {
      branch: 'Branch Pro20',
      users: 'Pro20 Users',
      reports: 'iPratico - Report',
      security: 'Seguridad',
    },
    security: {
      title: 'Cambiar contraseña',
      subtitle: 'Actualiza la contraseña de tu cuenta de backoffice.',
      password: 'Contraseña',
      confirmPassword: 'Confirmar contraseña',
      submit: 'Actualizar contraseña',
      validation: {
        required: 'La contraseña es obligatoria',
        minLength: 'La contraseña debe tener al menos 6 caracteres',
        confirmRequired: 'Confirma tu contraseña',
        mismatch: 'Las contraseñas no coinciden',
      },
      toast: {
        success: 'Contraseña actualizada correctamente',
        failed: 'No se pudo actualizar la contraseña. Inténtalo de nuevo.',
      },
    },
    users: {
      title: 'Usuarios',
      count: '{{count}} usuarios',
      create: 'Crear usuario',
      searchPlaceholder: 'Buscar usuario..',
      loadError: 'No se pudieron cargar los usuarios. Inténtalo de nuevo.',
      table: {
        userCode: 'UserCode',
        name: 'Nombre',
        email: 'Email',
        phone: 'Teléfono',
        activity: 'Actividad',
        visibleOnApp: 'Visible en la app',
      },
      tooltips: {
        linkActivity: 'Vincular actividad al usuario',
        editUser: 'Editar usuario',
        goToBackoffice: 'Ir al backoffice del usuario',
        deleteUser: 'Eliminar usuario',
      },
      fields: {
        name: 'Nombre',
        email: 'Email',
        phone: 'Teléfono',
        password: 'Contraseña',
        confirmPassword: 'Confirmar contraseña',
      },
      createModal: {
        title: 'Crear usuario',
        cancel: 'Cancelar',
        create: 'Crear',
      },
      editModal: {
        title: 'Editar usuario',
        cancel: 'Cancelar',
        update: 'Actualizar',
      },
      linkActivityModal: {
        title: 'Seleccionar actividad',
        searchLabel: 'Buscar actividad',
        searchPlaceholder: 'Buscar por nombre o código',
        availableTitle: 'Actividades disponibles',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
      },
      relatedActivitiesModal: {
        title: 'Actividades relacionadas',
        name: 'Nombre',
        code: 'Código',
        expiration: 'Vencimiento',
        unlink: 'Desvincular actividad',
        close: 'Cerrar',
      },
      unlinkModal: {
        title: '¿Desvincular actividad?',
        description:
          '¿Seguro que quieres desvincular "{{name}}" de este usuario? Esta acción no se puede deshacer.',
      },
      deleteModal: {
        title: '¿Eliminar usuario?',
        description:
          '¿Seguro que quieres eliminar a "{{name}}"? Esta acción no se puede deshacer.',
      },
      validation: {
        nameRequired: 'El nombre es obligatorio',
        emailRequired: 'El email es obligatorio',
        emailInvalid: 'Introduce un email válido',
        passwordMin: 'La contraseña debe tener al menos 6 caracteres',
        passwordMismatch: 'Las contraseñas no coinciden',
      },
      toast: {
        created: 'Usuario creado',
        createFailed: 'No se pudo crear el usuario',
        updated: 'Usuario actualizado',
        updateFailed: 'No se pudo actualizar el usuario',
        deleted: 'Usuario eliminado',
        deleteFailed: 'No se pudo eliminar el usuario',
        loadFailed: 'No se pudieron cargar los detalles del usuario',
        activitiesLinked: 'Actividades vinculadas al usuario',
        activitiesLinkFailed: 'No se pudieron vincular las actividades',
        activityUnlinked: 'Actividad desvinculada del usuario',
        activityUnlinkFailed: 'No se pudo desvincular la actividad',
        noAssociatedActivity: 'No hay actividades asociadas',
        visibilityUpdated: 'Visibilidad del usuario actualizada',
        visibilityUpdateFailed:
          'No se pudo actualizar la visibilidad del usuario',
      },
    },
    reports: {
      badge: 'Monitor de integración',
      title: 'Errores iPratico',
      subtitle:
        'Consulta solicitudes, respuestas y anomalías registradas durante las operaciones hacia iPratico.',
      errorsFound: 'errores encontrados',
      actions: {
        update: 'Actualizar',
        reset: 'Restablecer',
        filter: 'Filtrar',
      },
      filters: {
        branchCode: 'Código de sucursal',
        operation: 'Operación',
        httpStatus: 'Estado HTTP',
        fromDay: 'Desde el día',
        toDay: 'Hasta el día',
        allStatuses: 'Todos los estados',
        status400: '400 — Solicitud no válida',
        status401: '401 — No autorizado',
        status403: '403 — Acceso denegado',
        status404: '404 — Recurso no encontrado',
        status409: '409 — Conflicto',
        status429: '429 — Demasiadas solicitudes',
        status500: '500 — Error iPratico',
        status502: '502 — Gateway',
        status503: '503 — Servicio no disponible',
      },
      table: {
        date: 'Fecha',
        branch: 'Sucursal',
        operation: 'Operación',
        status: 'Estado',
        endpoint: 'Endpoint',
        mistake: 'Error',
      },
      detail: {
        title: 'Detalle del error',
        branch: 'Sucursal',
        branchName: 'Nombre de sede',
        branchCode: 'Código de sede',
        date: 'Fecha',
        method: 'Método',
        status: 'Estado',
        apiKey: 'X-API-KEY usada',
        endpoint: 'Endpoint',
        message: 'Mensaje',
        requestPayload: 'Payload de solicitud',
        responsePayload: 'Respuesta iPratico',
      },
      tooltips: {
        openDetail: 'Abrir detalle',
        closeDetail: 'Cerrar detalle',
        copyPayload: 'Copiar payload',
      },
      toast: {
        updated: 'Informes actualizados',
        copied: 'Copiado al portapapeles',
        loadFailed: 'No se pudieron cargar los errores de iPratico',
      },
      loadError:
        'No se pudieron cargar los errores de iPratico. Inténtalo de nuevo.',
    },
    searchPlaceholder: 'Buscar por nombre, código, empresa o IVA',
    filters: {
      features: {
        label: 'Funciones',
        google: 'Reserva de Google',
        menu: 'Menú del restaurante',
        ipraticoKey: 'Clave de iPratico',
        whatsappBusiness: 'WhatsApp Business',
        stripe: 'Stripe',
      },
    },
    table: {
      nameActivities: 'Nombre de sucursal',
      branchCode: 'BranchCode',
      extra: 'Extra',
      deadline: 'Expiración',
      expiredOn: 'Caducado el {{date}}',
      alertOnApp: 'Alerta en App',
      companyName: 'Nombre de la empresa',
      noCompanyName: 'Sin nombre de empresa',
      delete: 'Eliminar sucursal',
      settings: 'Actualizar sucursal',
      chat: 'Monitorear mensajes',
      send: 'Ir al backoffice',
      tooltips: {
        whatsappBusiness: 'WhatsApp Business habilitado',
        menu: 'Menú habilitado',
        google: 'Reserva de Google habilitada',
        ipraticoKey: 'Clave de iPratico',
        sms: 'SMS restantes: {{count}}',
        aiCredits: 'Créditos de IA: {{count}}',
        loadError: 'No se pudieron cargar los usuarios de esta sucursal',
      },
    },
    stripeConnect: {
      tooltip: {
        ready: 'Stripe Connect activo — ver o quitar la configuración',
        pending: 'Completa, reanuda o quita el onboarding de Stripe',
        unlinked: 'Conecta la sucursal a Stripe Connect',
      },
      link: {
        title: 'Enlace de onboarding de Stripe',
        body: 'El enlace para {{branch}} está listo.',
        warning:
          'Es un enlace de Stripe temporal y de un solo uso: compártelo o úsalo cuanto antes.',
        openLink: 'Abrir enlace',
        copyLink: 'Copiar enlace',
        linkCopied: 'Enlace copiado',
        copyFailed:
          'No se pudo copiar — selecciona y copia el enlace manualmente.',
      },
      connected: {
        title: 'Stripe Connect activo',
        body: '{{branch}} ya está conectada y puede recibir pagos y abonos.',
      },
      pending: {
        title: 'Verificaciones de Stripe en curso',
        body: 'Stripe todavía requiere algunas verificaciones para {{branch}}.',
        resume: 'Reanudar onboarding',
      },
      error: {
        title: 'No se pudo configurar Stripe',
        fallback: 'Revisa la configuración de Stripe e inténtalo de nuevo.',
      },
      remove: {
        action: 'Quitar configuración de Stripe',
        confirmTitle: '¿Quitar la configuración de Stripe?',
        confirmBody:
          '¿Desconectar Stripe de {{branch}}? La sucursal no podrá aceptar pagos de Stripe hasta que se vuelva a conectar.',
        success: 'Configuración de Stripe eliminada',
        failed: 'No se pudo quitar la configuración de Stripe',
      },
      close: 'Cerrar',
    },
    accessUserModal: {
      title: 'Selecciona un usuario para acceder al backoffice',
      name: 'Nombre',
      email: 'Email',
      close: 'Cerrar',
      loadError: 'No se pudieron cargar los usuarios de esta sucursal',
    },
    signInAsUser: {
      success: 'Sesión iniciada como el usuario seleccionado',
      failed: 'No se pudo iniciar sesión como este usuario',
      missingIdentity: 'El código de usuario y el email son obligatorios',
    },
    expiringBranchesModal: {
      title: 'Sucursales que vencen en {{month}}',
      name: 'Nombre',
      userCode: 'Usercode',
      email: 'Mail',
      expires: 'Vence',
      close: 'Cerrar',
      empty: 'Ninguna sucursal vence este mes.',
      openDetails: 'Abrir detalles de la sucursal',
    },
    deleteActivityModal: {
      title: '¿Confirmas la eliminación de la sucursal?',
      description:
        'Escribe aquí el código de sucursal ({{branchCode}}) para continuar con la eliminación',
      branchCodePlaceholder: 'CÓDIGO DE SUCURSAL',
      eliminate: 'Eliminar',
      cancel: 'Cancelar',
      deleted: 'Sucursal eliminada',
      deleteFailed: 'No se pudo eliminar la sucursal',
    },
    access: {
      title: 'Acceso al backoffice',
      description:
        'Introduce el código numérico para acceder al panel operativo Pro20.',
      codePlaceholder: '',
      enter: 'Entrar',
    },
    validation: {
      codeRequired: 'El código de acceso es obligatorio',
      codeNumeric: 'El código de acceso solo debe contener números',
      invalidCode: 'Código de acceso no válido',
    },
    messageMonitor: {
      title: 'Message Monitor',
      subtitle:
        'Comprueba los envíos normales y programados para las pruebas de API.',
      close: 'Cerrar',
      reset: 'Restablecer',
      update: 'Actualizar',
      stats: {
        posted: 'Publicados en esta página',
        errors: 'Errores en esta página',
        inProgress: 'En curso en esta página',
      },
      filters: {
        origin: 'Origen',
        state: 'Estado',
        channel: 'Canal',
        reference: 'Referencia',
        referencePlaceholder: 'Referencia',
        originEveryone: 'Todos',
        originNormal: 'Normal',
        originScheduled: 'Programado',
        originBulk: 'Masivo',
        stateEveryone: 'Todos',
        stateSent: 'Enviado',
        stateErrors: 'Errores',
        statePending: 'Pendiente',
        stateInQueue: 'En cola',
        stateInProgress: 'En curso',
        channelEveryone: 'Todos',
        channelWhatsapp: 'WhatsApp',
        channelThem: 'THEM',
        channelEmail: 'Email',
        channelSms: 'SMS',
      },
      eventsFound: '{{count}} eventos encontrados',
      empty: 'No se encontraron mensajes con los filtros seleccionados.',
      loadError:
        'No se pudieron cargar los eventos de mensaje. Inténtalo de nuevo.',
      actions: {
        resubmit: 'Reenviar mensaje',
      },
      toast: {
        resubmitted: 'Mensaje reenviado',
        resubmitFailed: 'No se pudo reenviar el mensaje',
      },
      table: {
        date: 'Data',
        type: 'Tipo',
        recipient: 'Destinatario',
        reference: 'Referencia',
        message: 'Mensaje',
        channel: 'Canal',
        state: 'Estado',
        attempts: 'Intentos',
        endpoint: 'Endpoint',
        payload: 'Payload',
        details: 'Detalles',
      },
    },
  },
  homeLanding: {
    documentTitle: 'PRO20 — Software de reservas y mesas para restaurantes',
    login: 'Iniciar sesión',
    bookDemo: 'Reservar una demo',
    startFreeTrial: 'Solicitar una prueba gratuita',
    learnMore: 'Más información',
    nav: {
      home: 'Inicio',
      product: 'Producto',
      solutions: 'Soluciones',
      industries: 'Sectores',
      integrations: 'Integraciones',
      pricing: 'Precios',
      resources: 'Recursos',
      why: 'Por qué PRO20',
      apps: 'Apps',
      howItWorks: 'Cómo funciona',
      reviews: 'Opiniones de clientes',
      faq: 'FAQ',
      about: 'Nosotros',
    },
    hero: {
      partnerBadge: 'Partner Google Reservation C2C',
      metaPartnerBadge: 'Proveedor tecnológico oficial de Meta',
      titleBefore: 'Plataforma todo en uno para',
      titleHighlight: 'restaurantes',
      titleAfter: 'y reserva de mesas.',
      subtitle:
        'Gestiona reservas, planos de sala, personal, marketing, pagos y deleita a tus clientes — todo en una potente plataforma.',
      checkGoogle: 'Google Reservations',
      checkWhatsapp: 'WhatsApp Official API',
      checkFloor: 'Mapa de sala y mesas',
      checkMarketing: 'Marketing y loyalty',
      checkPayments: 'Pagos y depósitos',
      checkApps: 'Apps iOS y Android',
      trustedBy:
        'De la confianza de más de 100 restaurantes en más de 10 ciudades de Italia.',
      previewAlt: 'Vista previa del panel de reservas PRO20',
      chatAria: 'Abrir chat',
    },
    trust: {
      heading: 'De la confianza de más de 100 restaurantes y marcas',
      subtitle:
        'Restaurantes independientes y grupos multi-sede usan PRO20 para llenar mesas cada día.',
    },
    stats: {
      title: 'De la confianza de equipos de restauración en todo el mundo',
      subtitle: 'Escala real en restaurantes, cafés, beach clubs y bares.',
      restaurantsValue: '100+',
      restaurantsLabel: 'Restaurantes',
      countriesValue: '10+',
      countriesLabel: 'Ciudades de Italia',
      bookingsValue: '2M+',
      bookingsLabel: 'Reservas gestionadas',
      uptimeValue: '99.9%',
      uptimeLabel: 'Disponibilidad',
    },
    features: {
      title: 'Todo lo que necesitas para gestionar reservas',
      subtitle:
        'Desde reservas y planos de sala hasta marketing, loyalty e integraciones — una sola plataforma.',
      reservationsTitle: 'Reservas inteligentes',
      reservationsDesc:
        'Acepta, gestiona y optimiza reservas en todos los canales en tiempo real.',
      floorTitle: 'Plano de sala y mesas',
      floorDesc:
        'Mapa visual con estado de mesas en vivo para un servicio más fluido.',
      bookingLinkTitle: 'Enlace público de reserva',
      bookingLinkDesc:
        'Crea un formulario de reserva y comparte la URL pública en tu web, QR, WhatsApp o redes para que los clientes reserven online.',
      marketingTitle: 'Marketing y campañas',
      marketingDesc:
        'Llega a tus clientes con ofertas, recordatorios y campañas segmentadas.',
      loyaltyTitle: 'Loyalty y promos',
      loyaltyDesc:
        'Recompensa a los habituales e impulsa visitas repetidas con promociones flexibles.',
      reportsTitle: 'Informes e insights',
      reportsDesc:
        'Sigue ocupación, ingresos y tendencias de clientes con paneles claros.',
    },
    why: {
      title: '¿Por qué PRO20?',
      subtitle:
        'Diseñado para restaurantes y bares — no un widget genérico de reservas.',
      allInOneTitle: 'Plataforma todo en uno',
      allInOneDesc:
        'Reservas, plano de sala, marketing, loyalty y pagos en un solo lugar.',
      channelsTitle: 'Nativo en Google y WhatsApp',
      channelsDesc: 'Encuentra a tus clientes donde ya reservan y escriben.',
      hospitalityTitle: 'Hecho para la operación de sala',
      hospitalityDesc:
        'Mapa de sala, flujos de personal y herramientas en vivo pensadas para el servicio.',
      appsTitle: 'Apps para cada dispositivo',
      appsDesc:
        'Apps nativas para teléfono e iPad en Android e iOS, sincronizadas con el backoffice web.',
      fidelityTitle: 'Fidelity y loyalty',
      fidelityDesc:
        'Recompensa a los habituales con puntos, tarjetas y promos — impulsa visitas repetidas desde un solo programa.',
      supportTitle: 'Soporte 24×7',
      supportDesc:
        'Ayuda continua cuando tu equipo la necesita — de día o de noche, todos los días.',
    },
    industries: {
      title: 'Diseñado para cada tipo de local',
      subtitle:
        'Desde fine dining hasta beach clubs — PRO20 se adapta a cómo tu local atiende a los clientes.',
      restaurants: 'Restaurantes',
      cafes: 'Cafés',
      beachClubs: 'Beach Clubs',
      bars: 'Bares y pubs',
      rooftops: 'Rooftops',
      resorts: 'Resorts',
    },
    integrations: {
      title: 'Integraciones potentes',
      subtitle:
        'Conecta las herramientas que ya usas — reservas, mensajería, POS y pagos.',
      google: 'Google Reservations',
      whatsapp: 'WhatsApp Official API',
      ipratico: 'iPratico POS',
      centralino: 'Centralino VOIP',
      stripe: 'Pagos Stripe',
      meta: 'Partner tecnológico Meta',
    },
    engage: {
      title: 'Comunica. Involucra. Deleita.',
      subtitle:
        'Conecta al instante con tus clientes mediante WhatsApp Official API y mensajes automáticos.',
      confirmations: 'Confirmaciones de reserva',
      reminders: 'Recordatorios y alertas',
      offers: 'Ofertas y promociones',
      feedback: 'Opiniones y reseñas',
      chatGuest: '¡Hola! Tu mesa para 2 está confirmada a las 20:00.',
      chatModify: 'Modificar reserva',
      chatCancel: 'Cancelar reserva',
      chatDirections: 'Cómo llegar',
    },
    apps: {
      title: 'Gestiona tu local desde el teléfono o el iPad',
      subtitle:
        'Apps nativas PRO20 para teléfonos e iPads — en Android e iOS, para que el equipo siga sincronizado en sala y en movilidad.',
      phoneTablet: 'Teléfonos e iPads',
      androidIos: 'Android e iOS',
      floorPlan: 'Plano de sala completo y control de mesas en iPad',
      onTheGo: 'Gestiona reservas desde el teléfono donde estés',
      sync: 'Los mismos datos en vivo que el backoffice web',
      appStore: 'Descargar en App Store',
      googlePlay: 'Disponible en Google Play',
    },
    about: {
      title: 'Sobre nosotros',
      subtitle:
        'Ayudamos a las marcas de restauración a convertir cada mesa en una mejor experiencia.',
      body: 'PRO20 es la plataforma todo en uno de reserva de mesas de confianza de locales en todo el mundo. Desde restaurantes independientes hasta grupos multi-sede, unimos reservas, planos de sala, mensajería y marketing para que los operadores se centren en la hospitalidad — no en malabarismos de herramientas.',
      missionTitle: 'Nuestra misión',
      missionBody:
        'Hacer simples, conectadas y agradables las operaciones de reserva de primer nivel para cada local.',
      pillarGuests: 'Clientes primero',
      pillarOperators: 'Operadores potenciados',
      pillarConnected: 'Siempre conectados',
      imageAlt: 'Equipo de restaurante en el trabajo',
    },
    howItWorks: {
      title: 'Empieza en 6 pasos sencillos',
      subtitle:
        'Todo lo que necesitas para gestionar las operaciones de tu restaurante, desde la configuración hasta clientes felices.',
      steps: {
        s1: {
          title: 'Crea tu restaurante',
          desc: 'Configura el perfil con cocina, ubicación y marca en minutos.',
        },
        s2: {
          title: 'Diseña salas y mesas',
          desc: 'Crea el plano con el planificador drag-and-drop y organiza las mesas a tu manera.',
        },
        s3: {
          title: 'Configura las reglas de reserva',
          desc: 'Define franjas, tamaño de grupos, depósitos y políticas según tu servicio.',
        },
        s4: {
          title: 'Comparte y recibe reservas',
          desc: 'Publica tu enlace de reserva en web, WhatsApp, redes o códigos QR.',
        },
        s5: {
          title: 'Gestiona y crece',
          desc: 'Gestiona sala, personal y marketing desde un solo lugar conforme llegan las reservas.',
        },
        s6: {
          title: 'Analiza y optimiza',
          desc: 'Usa informes claros de ocupación, ingresos y tendencias para mejorar cada servicio.',
        },
      },
      mocks: {
        createTitle: 'Crea tu restaurante',
        name: 'Nombre del restaurante',
        cuisine: 'Tipo de cocina',
        location: 'Ubicación',
        continue: 'Continuar',
        floorTitle: 'Plano de sala',
        rulesTitle: 'Ajustes de reserva',
        timeSlots: 'Franjas horarias',
        bookingRules: 'Reglas de reserva',
        advancePayment: 'Pago anticipado',
        shareTitle: 'Compartir enlace de reserva',
        manageTitle: 'Resumen de hoy',
        reservations: 'Reservas',
        occupied: 'Mesas ocupadas',
        walkIns: 'Walk-ins',
        revenue: 'Ingresos',
        reportsTitle: 'Resumen de informes',
        totalReservations: 'Reservas totales',
        occupancy: 'Tasa de ocupación',
      },
      ctaText:
        '¿Listo para empezar? Únete a miles de restaurantes que ya crecen con PRO20.',
      bookDemo: 'Reservar una demo',
      contactSales: 'Contactar ventas',
    },
    faq: {
      title: 'Preguntas frecuentes',
      subtitle:
        'Respuestas rápidas sobre el software de reservas PRO20 para locales multi-sede.',
      viewAll: 'Ver todas las FAQ',
      q1: '¿Puedo compartir un enlace público de reserva?',
      a1: 'Sí. Crea un formulario de reserva y comparte la URL pública en tu web, QR, WhatsApp o redes para que los clientes reserven online.',
      q2: '¿Admite varios restaurantes?',
      a2: 'Sí. Los grupos multi-sede pueden gestionar locales de forma centralizada con el Backoffice PRO20.',
      q3: '¿Puedo conectar WhatsApp?',
      a3: 'Sí. PRO20 admite WhatsApp Business y la API oficial de Meta para confirmaciones, recordatorios y campañas.',
      q4: '¿Qué integraciones hay disponibles?',
      a4: 'Google Reservations, WhatsApp Official API, sistemas POS, Stripe, Razorpay y muchas más.',
      q5: '¿Hay una app móvil?',
      a5: 'Sí. Apps nativas para teléfonos e iPads en Android e iOS, sincronizadas con la plataforma web.',
      q6: '¿Cómo empiezo?',
      a6: 'Reserva una demo o inicia la prueba gratuita — te ayudamos a configurar salas, mesas y tu enlace público de reserva.',
    },
    reviews: {
      title: 'No te fíes solo de nuestra palabra',
      subtitle:
        'Restaurantes en más de 10 ciudades de Italia ya usan PRO20 para gestionar reservas, llenar mesas y deleitar clientes. ¿Listo para ver de qué va el revuelo?',
      reassurance:
        '(De verdad. Sin letra pequeña. Sin presión comercial. Solo software sólido y un equipo que entiende los restaurantes.)',
      trustLabel: 'De cientos de opiniones',
      r1: {
        quote:
          'PRO20 sustituyó tres herramientas que usábamos antes. Reservas y WhatsApp por fin en un solo sitio.',
        author: 'Marco Rossi • Owner, Osteria Bella',
      },
      r2: {
        quote:
          'La configuración fue rápida y el planificador de mesas es exactamente lo que el personal necesitaba en noches de ajetreo.',
        author: 'Sophie Laurent • Manager, Café Lumière',
      },
      r3: {
        quote:
          'La capacidad estacional y la mensajería a clientes eran un caos. Ahora es predecible y profesional.',
        author: 'Elena Bianchi • Director, Azure Beach Club',
      },
      r4: {
        quote:
          'Nuestro enlace público llenó franjas nocturnas que dejábamos vacías. Los clientes reservan solos — nosotros confirmamos.',
        author: 'Alex Kim • Owner, Harbor Bistro',
      },
    },
    nextLevel: {
      title: '¿Listo para llevar tu restaurante al siguiente nivel?',
      body: 'PRO20 está diseñado para simplificar las operaciones diarias, aumentar las reservas y reducir el estrés de gestionar un local ajetreado.',
      closing: 'Empieza hoy con PRO20 y nota la diferencia.',
    },
    cta: {
      heading: '¿Listo para transformar tu restaurante?',
      subtitle: 'Únete a miles de restaurantes que crecen con PRO20.',
    },
    footer: {
      blurb:
        'El software todo en uno de reservas para restaurantes, engagement de clientes y operaciones de local.',
      product: 'Producto',
      productReservations: 'Reservas',
      productFloor: 'Plano de sala',
      productMarketing: 'Marketing',
      productLoyalty: 'Loyalty',
      productReports: 'Informes',
      productIntegrations: 'Integraciones',
      solutions: 'Soluciones',
      solutionsRestaurants: 'Para restaurantes',
      solutionsCafes: 'Para cafés',
      solutionsBeach: 'Para beach clubs',
      solutionsBars: 'Para bares',
      solutionsChains: 'Para cadenas',
      resources: 'Recursos',
      resourcesBlog: 'Blog',
      resourcesGuides: 'Guías',
      resourcesHelp: 'Centro de ayuda',
      resourcesCases: 'Casos de estudio',
      resourcesApi: 'Documentación API',
      resourcesApps: 'Apps móviles',
      company: 'Empresa',
      companyAbout: 'Sobre nosotros',
      companyCareers: 'Carreras',
      companyContact: 'Contacto',
      companyPartners: 'Partners',
      legal: 'Legal',
      legalPrivacy: 'Política de privacidad',
      legalTerms: 'Términos del servicio',
      legalCookies: 'Política de cookies',
      copyright: '© {{year}} PRO20. Todos los derechos reservados.',
      socialFacebook: 'Facebook',
      socialInstagram: 'Instagram',
      socialLinkedin: 'LinkedIn',
      socialYoutube: 'YouTube',
    },
  },
  faqPage: {
    documentTitle: 'FAQ — Software de reservas para restaurantes PRO20',
    title: 'Preguntas frecuentes',
    subtitle:
      'Respuestas rápidas a las preguntas más comunes sobre el software de reservas PRO20.',
    searchPlaceholder: 'Busca tu pregunta...',
    emptyResults:
      'Ninguna pregunta coincide con tu búsqueda. Prueba otra palabra o categoría.',
    categories: {
      all: 'Todas las preguntas',
      reservations: 'Reservas',
      whatsapp: 'WhatsApp',
      payments: 'Pagos',
      reports: 'Informes',
      staff: 'Personal y usuarios',
      integrations: 'Integraciones',
      general: 'General',
    },
    promo: {
      badge: 'Plataforma todo en uno',
      titleBefore: 'Todo lo que necesitas para',
      titleAccent: 'gestionar y hacer crecer',
      titleAfter: 'tu restaurante',
      body: 'De las reservas al marketing, de la automatización de WhatsApp a los informes — PRO20 te ayuda a gestionar tu negocio de forma más inteligente.',
      restaurantsValue: '10.000+',
      restaurantsLabel: 'Restaurantes',
      bookingsValue: '2M+',
      bookingsLabel: 'Reservas',
      countriesValue: '10+',
      countriesLabel: 'Ciudades de Italia',
      uptimeValue: '99,9%',
      uptimeLabel: 'Disponibilidad',
    },
    items: {
      q1: '¿Puedo compartir enlaces de reserva?',
      a1: 'Sí. Crea un formulario de reserva y comparte la URL pública en tu web, QR, WhatsApp o redes para que los clientes reserven online.',
      q1Tag1: 'URL pública',
      q1Tag2: 'Código QR',
      q1Tag3: 'WhatsApp',
      q1Tag4: 'Redes sociales',
      q1Tag5: 'Sitio web',
      q2: '¿PRO20 admite varios restaurantes o sedes?',
      a2: 'Sí. Los grupos multi-sede pueden gestionar locales de forma centralizada con PRO20 Backoffice, controles e informes compartidos.',
      q3: '¿Puedo conectar WhatsApp Official y enviar mensajes?',
      a3: 'Sí. PRO20 admite WhatsApp Business y la API oficial de Meta para confirmaciones, recordatorios, campañas y mensajería.',
      q4: '¿Qué pasarelas de pago se admiten?',
      a4: 'PRO20 se integra con Stripe y otros proveedores para cobrar depósitos y reducir no-shows.',
      q5: '¿Hay una app móvil?',
      a5: 'Sí. Apps nativas para móviles e iPads en Android e iOS, sincronizadas con la plataforma web.',
      q6: '¿Qué idiomas se admiten?',
      a6: 'PRO20 admite varios idiomas para operadores y clientes, incluidos inglés, italiano, español, francés, neerlandés y chino.',
      q7: '¿Puedo gestionar el personal y sus roles?',
      a7: 'Sí. Asigna roles y permisos para que el equipo solo vea lo necesario — desde sala hasta managers multi-sede.',
      q8: '¿Puedo gestionar campañas de marketing y códigos promo?',
      a8: 'Sí. Lanza campañas, ofertas y códigos promo desde PRO20 para llenar turnos más tranquilos y premiar a clientes habituales.',
      q9: '¿Qué tipo de informes puedo obtener?',
      a9: 'Sigue ocupación, reservas, ingresos y actividad de clientes con paneles pensados para la restauración.',
      q10: '¿Qué tan seguros están mis datos?',
      a10: 'Usamos prácticas de seguridad estándar del sector para proteger los datos del restaurante y de los clientes, con un uptime fiable.',
    },
    cta: {
      eyebrow: '¿Aún tienes preguntas?',
      title: '¡Estamos aquí para ayudarte!',
      body: 'Habla con nuestros expertos en restauración y descubre cómo PRO20 puede transformar tu negocio.',
      bookDemo: 'Reservar una demo',
      contactSales: 'Contactar ventas',
      startTrial: 'O solicita una prueba gratuita',
    },
  },
  legalPages: {
    privacy: {
      documentTitle: 'Política de privacidad — PRO20',
      loading: 'Cargando la política de privacidad…',
      loadError:
        'No se pudieron cargar los datos del responsable. La política se muestra con información limitada.',
    },
    privacyPolicy: {
      documentTitle: 'Aviso de privacidad | Proventi',
      title: 'Información sobre el tratamiento de datos personales',
      subtitle:
        'De conformidad con los artículos 13 y 14 del Reglamento (UE) 2016/679 – RGPD',
      contents: 'En esta página',
      body: privacyPolicyBodyEs,
    },
    terms: {
      documentTitle: 'Términos de servicio — PRO20',
      title: 'Términos de servicio',
      subtitle: 'Estos términos se actualizarán pronto.',
      body: 'Los Términos de servicio de PRO20 se están preparando. Vuelve más tarde o contáctanos si necesitas una copia mientras tanto.',
    },
  },
};
